import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Button,
  alpha,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import {
  SettingsOutlined,
  Dashboard,
  Person,
  Work,
  Psychology,
  Badge,
  PostAdd,
  People,
  VideoCall,
  Analytics,
  Map,
  LogoutOutlined,
  Menu as MenuIcon,
  WarningAmber as WarningAmberIcon,
  SchoolOutlined,
} from "@mui/icons-material";
import ListAltTwoToneIcon from "@mui/icons-material/ListAltTwoTone";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
 
const drawerWidth = 260;
const drawerWidthCollapsed = 64;
 
const topMenuItems = {
  talent: [
    { text: "Dashboard", icon: <Dashboard />, path: "/db-shiksha/gatep/talent" },
    {
      text: "Profile Builder",
      icon: <Person />,
      path: "/db-shiksha/gatep/talent/profile",
    },
    { text: "Global Jobs", icon: <Work />, path: "/db-shiksha/gatep/talent/jobs" },
    { text: "Interviews", icon: <Work />, path: "/db-shiksha/gatep/talent/interviews" },
    {
      text: "AI Assistant",
      icon: <Psychology />,
      path: "/db-shiksha/gatep/talent/assistant",
    },
    {
      text: "Skills Passport",
      icon: <Badge />,
      path: "/db-shiksha/gatep/talent/passport",
    },
    {
      text: "Mock Interview",
      icon: <Psychology />,
      path: "/db-shiksha/gatep/talent/exam-intro",
    },
    {
      text: "Report",
      icon: <ListAltTwoToneIcon />,
      path: "/db-shiksha/gatep/talent/report",
    },
    { text: "KaaS Portal", icon: <SchoolOutlined />, path: "kaas-sync" },
  ],
  employer: [
    { text: "Dashboard", icon: <Dashboard />, path: "/db-shiksha/gatep/employer" },
    {
      text: "Company Profile",
      icon: <PostAdd />,
      path: "/db-shiksha/gatep/employer/company-profile",
    },
    { text: "Post Jobs", icon: <PostAdd />, path: "/db-shiksha/gatep/employer/post-job" },
    {
      text: "Candidates",
      icon: <People />,
      path: "/db-shiksha/gatep/employer/candidates",
    },
    {
      text: "Interviews",
      icon: <VideoCall />,
      path: "/db-shiksha/gatep/employer/interviews",
    },
    {
      text: "Analytics",
      icon: <Analytics />,
      path: "/db-shiksha/gatep/employer/analytics",
    },
    {
      text: " Evaluations",
      icon: <Analytics />,
      path: "/db-shiksha/gatep/employer/Evaluation",
    },
    { text: "KaaS Portal", icon: <SchoolOutlined />, path: "kaas-sync" },
  ],
  admin: [
    { text: "Dashboard", icon: <Dashboard />, path: "/db-shiksha/gatep/admin" },
    { text: "Analytics", icon: <Analytics />, path: "/db-shiksha/gatep/admin/analytics" },
    { text: "User Management", icon: <People />, path: "/db-shiksha/gatep/admin/users" },
    { text: "Talent Heatmap", icon: <Map />, path: "/db-shiksha/gatep/admin/heatmap" },
    { text: "KaaS Admin", icon: <SchoolOutlined />, path: "kaas-sync" },
  ],
};
 
const bottomMenuItems = [
  // { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
  { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
];
 
const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const [isHovered, setIsHovered] = useState(false);
  const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
 
  // The AppBar height in Layout.js changes at the 'sm' breakpoint (600px).
  // We need to mirror that change here for correct positioning.
  const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
  const appBarHeight = isBelowSm ? "56px" : "64px";
 
  // Derive styles from the theme
  const sidebarBackgroundColor = theme.palette.background.paper;
  const textColor = theme.palette.text.primary;
  const activeBackgroundColor = theme.palette.primary.main;
  const activeTextColor = theme.palette.primary.contrastText;
  const hoverBackgroundColor = alpha(theme.palette.primary.main, 0.08);
  const verticalMargin = theme.spacing(2); // The desired gap (16px)
 
  const handleKaaSSync = async () => {
    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) return;

    setIsSyncing(true);
    try {
      const response = await fetch("https://tdtlworld.com/dss-gatep-backend/api/generate-kaas-token/", {
        headers: { "Authorization": `Bearer ${accessToken}` }
      });
      
      if (response.ok) {
        const { token } = await response.json();
        // Sync with KaaS
        const syncResponse = await fetch("https://tdtlworld.com/dss-kaas-backend/api/sso-sync/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token })
        });

        if (syncResponse.ok) {
          const data = await syncResponse.json();
          // Set KaaS tokens
          localStorage.setItem("jwttoken", data.access);
          localStorage.setItem("refresh", data.refresh);
          localStorage.setItem("role", data.role);
          localStorage.setItem("user_id", data.user_id);
          localStorage.setItem("email", data.email);

          // Redirect to KaaS dashboard based on role
          if (data.role === "Admin") {
            window.location.href = "/db-shiksha/kaas/admin/dashboard";
          } else {
            window.location.href = "/db-shiksha/kaas/student/dashboardStudent";
          }
        }
      }
    } catch (err) {
      console.error("KaaS sync failed:", err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleItemClick = (item) => {
    if (item.text === "Logout") {
      setOpenLogoutConfirm(true);
    } else if (item.path === "kaas-sync") {
      handleKaaSSync();
    } else {
      navigate(item.path);
      if (isSmallScreen) {
        onMobileToggle();
      }
    }
  };
 
  const handleCloseLogoutConfirm = () => {
    setOpenLogoutConfirm(false);
  };
 
  const handleConfirmLogout = () => {
    logout();
    navigate("/db-shiksha/kaas/login");
    setOpenLogoutConfirm(false);
  };
 
  const handleMouseEnter = () => {
    if (isTablet && !open) {
      setIsHovered(true);
    }
  };
 
  const handleMouseLeave = () => {
    if (isTablet) {
      setIsHovered(false);
    }
  };
 
  const isExpanded = open || isHovered;
 
  const renderMenuItems = (items) => {
    if (!Array.isArray(items)) {
      return null;
    }
    return items.map((item) => (
      <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
        <Tooltip
          title={!isExpanded && !isSmallScreen ? item.text : ""}
          placement="right"
        >
          <ListItemButton
            onClick={() => handleItemClick(item)}
            selected={location.pathname === item.path}
            sx={{
              minHeight: 48,
              justifyContent: isExpanded ? "initial" : "center",
              my: 0.5,
              borderRadius: "8px",
              color: theme.palette.text.secondary,
              px: 2.5,
              mx: 1.5,
              "&:hover": { backgroundColor: hoverBackgroundColor, color: theme.palette.primary.main },
              "&.Mui-selected": {
                backgroundColor: activeBackgroundColor,
                color: activeTextColor,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                "&:hover": { backgroundColor: activeBackgroundColor },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isExpanded ? 3 : "auto",
                justifyContent: "center",
                color: "inherit",
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              sx={{
                opacity: isExpanded ? 1 : 0,
                "& .MuiListItemText-primary": {
                  fontFamily: "sans-serif",
                  fontWeight: location.pathname === item.path ? 500 : 300,
                  whiteSpace: "nowrap",
                },
              }}
            />
          </ListItemButton>
        </Tooltip>
      </ListItem>
    ));
  };
 
  const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];
 
  const drawerContent = (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": { display: "none" },
        msOverflowStyle: "none",
        scrollbarWidth: "none",
      }}
    >
      <ListItem
        disablePadding
        sx={{
          display: { xs: "none", md: "block" },
          mt: 1,
        }}
      >
        <ListItemButton
          onClick={onToggle}
          disableRipple // --- FIX: Add this prop to disable the click effect ---
          sx={{
            minHeight: 48,
            justifyContent: isExpanded ? "initial" : "center",
            my: 0.5,
            borderRadius: "8px",
            color: theme.palette.text.secondary,
            px: 2.5,
            mx: 1.5,
            "&:hover": {
              backgroundColor: hoverBackgroundColor,
              color: theme.palette.primary.main,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: isExpanded ? 3 : "auto",
              justifyContent: "center",
              color: "inherit",
            }}
          >
            <MenuIcon />
          </ListItemIcon>
        </ListItemButton>
      </ListItem>
 
      <List sx={{ pt: 1 }}>{renderMenuItems(currentTopMenuItems)}</List>
      <Box sx={{ flexGrow: 1 }} />
      <List sx={{ pb: 1 }}>{renderMenuItems(bottomMenuItems)}</List>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 101, display: 'flex', flexDirection: 'column', gap: 2 }}
        open={isSyncing}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Syncing with KaaS Portal...
        </Typography>
      </Backdrop>
    </Box>
  );
 
  return (
    <>
      <Dialog
        open={openLogoutConfirm}
        onClose={handleCloseLogoutConfirm}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: "350px",
            bgcolor: "background.paper",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.5)",
          },
        }}
      >
        <DialogContent
          sx={{
            pt: 4,
            pb: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 50, color: "warning.main" }} />
          <Typography
            variant="h5"
            component="div"
            sx={{
              fontWeight: "bold",
              fontSize: "1.25rem",
              textAlign: "center",
            }}
          >
            Are you sure?
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", textAlign: "center" }}
          >
            Are you sure you want to log out?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{ justifyContent: "center", pb: 3, pt: 1, gap: 1.5 }}
        >
          <Button
            onClick={handleCloseLogoutConfirm}
            variant="outlined"
            color="secondary"
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLogout}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "6px",
              textTransform: "none",
              px: 4,
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
 
      {isSmallScreen ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileToggle}
          anchor="left"
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            zIndex: theme.zIndex.drawer + 2,
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: sidebarBackgroundColor,
              borderRight: "none",
              // Use the dynamic appBarHeight for correct positioning
              mt: `calc(${appBarHeight} + ${theme.spacing(1)})`,
              ml: 1,
              mb: 1,
              height: `calc(100% - ${appBarHeight} - ${theme.spacing(2)})`,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          open={open}
          anchor="left"
          sx={{
            display: { xs: "none", md: "block" },
            width: isExpanded ? drawerWidth : drawerWidthCollapsed,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: isExpanded ? drawerWidth : drawerWidthCollapsed,
              boxSizing: "border-box",
              backgroundColor: sidebarBackgroundColor,
              borderRight: "none",
              overflowX: "hidden",
              transition: theme.transitions.create("width", {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              // Use the dynamic appBarHeight here too for consistency
              mt: `calc(${appBarHeight} + ${verticalMargin})`,
              height: `calc(100% - ${appBarHeight} - (${verticalMargin} * 2))`,
              ml: 1,
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};
 
export default Sidebar;
// import React, { useState } from "react";
// import {
//   Drawer,
//   List,
//   ListItem,
//   ListItemIcon,
//   ListItemText,
//   ListItemButton,
//   Box,
//   IconButton,
//   useTheme,
//   useMediaQuery,
//   Tooltip,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   Typography,
//   Button,
// } from "@mui/material";
// import {
//   SettingsOutlined,
//   Dashboard,
//   Person,
//   Work,
//   Psychology,
//   Badge,
//   PostAdd,
//   People,
//   VideoCall,
//   Analytics,
//   Map,
//   LogoutOutlined,
//   Menu as MenuIcon,
//   WarningAmber as WarningAmberIcon,
// } from "@mui/icons-material";
// import ListAltTwoToneIcon from "@mui/icons-material/ListAltTwoTone";
// import { useNavigate, useLocation } from "react-router-dom";
// import { useAuth } from "../../context/AuthContext";
 
// const drawerWidth = 260;
// const drawerWidthCollapsed = 64;
 
// const topMenuItems = {
//   talent: [
//     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
//     {
//       text: "Profile Builder",
//       icon: <Person />,
//       path: "/gatep/talent/profile",
//     },
//     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
//     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
//     {
//       text: "AI Assistant",
//       icon: <Psychology />,
//       path: "/gatep/talent/assistant",
//     },
//     {
//       text: "Skills Passport",
//       icon: <Badge />,
//       path: "/gatep/talent/passport",
//     },
//     {
//       text: "Mock Interview",
//       icon: <Psychology />,
//       path: "/gatep/talent/exam-intro",
//     },
//     {
//       text: "Report",
//       icon: <ListAltTwoToneIcon />,
//       path: "/gatep/talent/report",
//     },
//   ],
//   employer: [
//     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
//     {
//       text: "Company Profile",
//       icon: <PostAdd />,
//       path: "/gatep/employer/company-profile",
//     },
//     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
//     {
//       text: "Candidates",
//       icon: <People />,
//       path: "/gatep/employer/candidates",
//     },
//     {
//       text: "Interviews",
//       icon: <VideoCall />,
//       path: "/gatep/employer/interviews",
//     },
//     {
//       text: "Analytics",
//       icon: <Analytics />,
//       path: "/gatep/employer/analytics",
//     },
//     {
//       text: " Evaluations",
//       icon: <Analytics />,
//       path: "/gatep/employer/Evaluation",
//     },
//   ],
//   admin: [
//     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
//     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
//     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
//     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
//   ],
// };
 
// const bottomMenuItems = [
//   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
//   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// ];
 
// const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
//   const { user, logout } = useAuth();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const theme = useTheme();
//   const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
//   const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
//   const [isHovered, setIsHovered] = useState(false);
//   const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);
 
//   // --- FIX STARTS HERE ---
//   // The AppBar height in Layout.js changes at the 'sm' breakpoint (600px).
//   // We need to mirror that change here for correct positioning.
//   const isBelowSm = useMediaQuery(theme.breakpoints.down("sm"));
//   const appBarHeight = isBelowSm ? "56px" : "64px";
//   // --- FIX ENDS HERE ---
 
//   // Derive styles from the theme
//   const sidebarBackgroundColor = theme.palette.primary.main;
//   const textColor = theme.palette.primary.contrastText;
//   const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
//   const verticalMargin = theme.spacing(2); // The desired gap (16px)
 
//   const handleItemClick = (item) => {
//     if (item.text === "Logout") {
//       setOpenLogoutConfirm(true);
//     } else {
//       navigate(item.path);
//       if (isSmallScreen) {
//         onMobileToggle();
//       }
//     }
//   };
 
//   const handleCloseLogoutConfirm = () => {
//     setOpenLogoutConfirm(false);
//   };
 
//   const handleConfirmLogout = () => {
//     logout();
//     navigate("/gatep/login");
//     setOpenLogoutConfirm(false);
//   };
 
//   const handleMouseEnter = () => {
//     if (isTablet && !open) {
//       setIsHovered(true);
//     }
//   };
 
//   const handleMouseLeave = () => {
//     if (isTablet) {
//       setIsHovered(false);
//     }
//   };
 
//   const isExpanded = open || isHovered;
 
//   const renderMenuItems = (items) => {
//     if (!Array.isArray(items)) {
//       return null;
//     }
//     return items.map((item) => (
//       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
//         <Tooltip
//           title={!isExpanded && !isSmallScreen ? item.text : ""}
//           placement="right"
//         >
//           <ListItemButton
//             onClick={() => handleItemClick(item)}
//             selected={location.pathname === item.path}
//             sx={{
//               minHeight: 48,
//               justifyContent: isExpanded ? "initial" : "center",
//               my: 0.5,
//               borderRadius: "25px",
//               color: textColor,
//               px: 2.5,
//               mx: 1.5,
//               "&:hover": { backgroundColor: hoverBackgroundColor },
//               "&.Mui-selected": {
//                 backgroundColor: theme.palette.background.paper,
//                 color: theme.palette.primary.main,
//                 "&:hover": { backgroundColor: theme.palette.background.paper },
//               },
//             }}
//           >
 
// <ListItemIcon
//               sx={{
//                 minWidth: 0,
//                 mr: isExpanded ? 3 : "auto",
//                 justifyContent: "center",
//                 color: "inherit",
//               }}
//             >
//               {item.icon}
//             </ListItemIcon>
//             <ListItemText
//               primary={item.text}
//               sx={{
//                 opacity: isExpanded ? 1 : 0,
//                 "& .MuiListItemText-primary": {
//                   fontFamily: "sans-serif",
//                   fontWeight: location.pathname === item.path ? 500 : 300,
//                   whiteSpace: "nowrap",
//                 },
//               }}
//             />
//           </ListItemButton>
//         </Tooltip>
//       </ListItem>
//     ));
//   };
 
//   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];
 
//   const drawerContent = (
//     <Box
//       onMouseEnter={handleMouseEnter}
//       onMouseLeave={handleMouseLeave}
//       sx={{
//         height: "100%",
//         display: "flex",
//         flexDirection: "column",
//         overflowY: "auto",
//         overflowX: "hidden",
//         "&::-webkit-scrollbar": { display: "none" },
//         msOverflowStyle: "none",
//         scrollbarWidth: "none",
//       }}
//     >
//       <Box
//         sx={{
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "flex-start",
//           py: 1.5,
//           pl: 1.75,
//           ...(!isExpanded &&
//             !isSmallScreen && { justifyContent: "center", pl: 0 }),
//         }}
//       >
//         <IconButton
//           onClick={onToggle}
//           sx={{
//             color: textColor,
//             display: { xs: "none", md: "inline-flex" },
//             mr: isExpanded ? 1 : 0,
//           }}
//         >
//           <MenuIcon />
//         </IconButton>
//       </Box>
//       <List sx={{ pt: 1 }}>{renderMenuItems(currentTopMenuItems)}</List>
//       <Box sx={{ flexGrow: 1 }} />
//       <List sx={{ pb: 1 }}>{renderMenuItems(bottomMenuItems)}</List>
//     </Box>
//   );
 
//   return (
//     <>
//       <Dialog
//         open={openLogoutConfirm}
//         onClose={handleCloseLogoutConfirm}
//         PaperProps={{
//           sx: {
//             borderRadius: "12px",
//             minWidth: "350px",
//             bgcolor: "background.paper",
//           },
//         }}
//         BackdropProps={{
//           sx: {
//             backgroundColor: "rgba(0,0,0,0.5)",
//           },
//         }}
//       >
//         <DialogContent
//           sx={{
//             pt: 4,
//             pb: 2,
//             display: "flex",
//             flexDirection: "column",
//             alignItems: "center",
//             gap: 2,
//           }}
//         >
//           <WarningAmberIcon sx={{ fontSize: 50, color: "warning.main" }} />
//           <Typography
//             variant="h5"
//             component="div"
//             sx={{
//               fontWeight: "bold",
//               fontSize: "1.25rem",
//               textAlign: "center",
//             }}
//           >
//             Are you sure?
//           </Typography>
//           <Typography
//             variant="body2"
//             sx={{ color: "text.secondary", textAlign: "center" }}
//           >
//             Are you sure you want to log out?
//           </Typography>
//         </DialogContent>
//         <DialogActions
//           sx={{ justifyContent: "center", pb: 3, pt: 1, gap: 1.5 }}
//         >
//           <Button
//             onClick={handleCloseLogoutConfirm}
//             variant="outlined"
//             color="secondary"
//             sx={{
//               borderRadius: "6px",
//               textTransform: "none",
//               px: 3,
//             }}
//           >
//             Cancel
//           </Button>
//           <Button
//             onClick={handleConfirmLogout}
//             variant="contained"
//             color="error"
//             sx={{
//               borderRadius: "6px",
//               textTransform: "none",
//               px: 4,
//             }}
//           >
//             Logout
//           </Button>
//         </DialogActions>
//       </Dialog>
 
//       {isSmallScreen ? (
//         <Drawer
//           variant="temporary"
//           open={mobileOpen}
//           onClose={onMobileToggle}
//           anchor="left"
//           ModalProps={{ keepMounted: true }}
//           sx={{
//             display: { xs: "block", md: "none" },
//             zIndex: theme.zIndex.drawer + 2,
//             "& .MuiDrawer-paper": {
//               boxSizing: "border-box",
//               width: drawerWidth,
//               backgroundColor: sidebarBackgroundColor,
//               borderRight: "none",
//               // Use the dynamic appBarHeight for correct positioning
//               mt: `calc(${appBarHeight} + ${theme.spacing(1)})`,
//               ml: 1,
//               mb: 1,
//               height: `calc(100% - ${appBarHeight} - ${theme.spacing(2)})`,
//               borderRadius: theme.shape.borderRadius * 2,
//             },
//           }}
//         >
//           {drawerContent}
//         </Drawer>
//       ) : (
//         <Drawer
//           variant="permanent"
//           open={open}
//           anchor="left"
//           sx={{
//             display: { xs: "none", md: "block" },
//             width: isExpanded ? drawerWidth : drawerWidthCollapsed,
//             flexShrink: 0,
//             "& .MuiDrawer-paper": {
//               width: isExpanded ? drawerWidth : drawerWidthCollapsed,
//               boxSizing: "border-box",
//               backgroundColor: sidebarBackgroundColor,
//               borderRight: "none",
//               overflowX: "hidden",
//               transition: theme.transitions.create("width", {
//                 easing: theme.transitions.easing.sharp,
//                 duration: theme.transitions.duration.enteringScreen,
//               }),
//               // Use the dynamic appBarHeight here too for consistency
//               mt: `calc(${appBarHeight} + ${verticalMargin})`,
//               height: `calc(100% - ${appBarHeight} - (${verticalMargin} * 2))`,
//               ml: 1,
//               borderRadius: theme.shape.borderRadius * 2,
//             },
//           }}
//         >
//           {drawerContent}
//         </Drawer>
//       )}
//     </>
//   );
// };
 
// export default Sidebar;
 
 

// import React, { useState } from "react";
// import {
//   Drawer,
//   List,
//   ListItem,
//   ListItemIcon,
//   ListItemText,
//   ListItemButton,
//   Box,
//   IconButton,
//   useTheme,
//   useMediaQuery,
//   Tooltip,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   Typography,
//   Button,
// } from "@mui/material";
// import {
//   SettingsOutlined,
//   Dashboard,
//   Person,
//   Work,
//   Psychology,
//   Badge,
//   PostAdd,
//   People,
//   VideoCall,
//   Analytics,
//   Map,
//   LogoutOutlined,
//   Menu as MenuIcon,
//   WarningAmber as WarningAmberIcon,
// } from "@mui/icons-material";
// import ListAltTwoToneIcon from "@mui/icons-material/ListAltTwoTone";
// import { useNavigate, useLocation } from "react-router-dom";
// import { useAuth } from "../../context/AuthContext";

// const drawerWidth = 260;
// const drawerWidthCollapsed = 64;

// // All hardcoded style variables have been removed

// const topMenuItems = {
//   talent: [
//     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
//     {
//       text: "Profile Builder",
//       icon: <Person />,
//       path: "/gatep/talent/profile",
//     },
//     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
//     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
//     {
//       text: "AI Assistant",
//       icon: <Psychology />,
//       path: "/gatep/talent/assistant",
//     },
//     {
//       text: "Skills Passport",
//       icon: <Badge />,
//       path: "/gatep/talent/passport",
//     },
//     {
//       text: "Mock Interview",
//       icon: <Psychology />,
//       path: "/gatep/talent/exam-intro",
//     },
//     {
//       text: "Report",
//       icon: <ListAltTwoToneIcon />,
//       path: "/gatep/talent/report",
//     },
//   ],
//   employer: [
//     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
//     {
//       text: "Company Profile",
//       icon: <PostAdd />,
//       path: "/gatep/employer/company-profile",
//     },
//     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
//     {
//       text: "Candidates",
//       icon: <People />,
//       path: "/gatep/employer/candidates",
//     },
//     {
//       text: "Interviews",
//       icon: <VideoCall />,
//       path: "/gatep/employer/interviews",
//     },
//     {
//       text: "Analytics",
//       icon: <Analytics />,
//       path: "/gatep/employer/analytics",
//     },
//     {
//       text: " Evaluations",
//       icon: <Analytics />,
//       path: "/gatep/employer/Evaluation",
//     },
//   ],
//   admin: [
//     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
//     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
//     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
//     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
//   ],
// };

// const bottomMenuItems = [
//   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
//   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// ];

// const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
//   const { user, logout } = useAuth();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const theme = useTheme();
//   const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
//   const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
//   const [isHovered, setIsHovered] = useState(false);
//   const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);

//   // Derive styles from the theme
//   const sidebarBackgroundColor = theme.palette.primary.main;
//   const textColor = theme.palette.primary.contrastText;
//   const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)"; // This can remain as is or be theme.palette.action.hover
//   const mobileAppBarHeight = isSmallScreen ? "56px" : "64px";

//   const handleItemClick = (item) => {
//     if (item.text === "Logout") {
//       setOpenLogoutConfirm(true);
//     } else {
//       navigate(item.path);
//       if (isSmallScreen) {
//         onMobileToggle();
//       }
//     }
//   };

//   const handleCloseLogoutConfirm = () => {
//     setOpenLogoutConfirm(false);
//   };

//   const handleConfirmLogout = () => {
//     logout();
//     navigate("/gatep/login");
//     setOpenLogoutConfirm(false);
//   };

//   const handleMouseEnter = () => {
//     if (isTablet && !open) {
//       setIsHovered(true);
//     }
//   };

//   const handleMouseLeave = () => {
//     if (isTablet) {
//       setIsHovered(false);
//     }
//   };

//   const isExpanded = open || isHovered;

//   const renderMenuItems = (items) => {
//     if (!Array.isArray(items)) {
//       return null;
//     }
//     return items.map((item) => (
//       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
//         <Tooltip
//           title={!isExpanded && !isSmallScreen ? item.text : ""}
//           placement="right"
//         >
//           <ListItemButton
//             onClick={() => handleItemClick(item)}
//             selected={location.pathname === item.path}
//             sx={{
//               minHeight: 48,
//               justifyContent: isExpanded ? "initial" : "center",
//               my: 0.5,
//               borderRadius: "25px",
//               color: textColor, // Using theme color
//               px: 2.5,
//               mx: 1.5,
//               "&:hover": { backgroundColor: hoverBackgroundColor },
//               "&.Mui-selected": {
//                 backgroundColor: theme.palette.background.paper, // Using theme color
//                 color: theme.palette.primary.main, // Using theme color
//                 "&:hover": { backgroundColor: theme.palette.background.paper }, // Using theme color
//               },
//             }}
//           >
//             <ListItemIcon
//               sx={{
//                 minWidth: 0,
//                 mr: isExpanded ? 3 : "auto",
//                 justifyContent: "center",
//                 color: "inherit",
//               }}
//             >
//               {item.icon}
//             </ListItemIcon>
//             <ListItemText
//               primary={item.text}
//               sx={{
//                 opacity: isExpanded ? 1 : 0,
//                 "& .MuiListItemText-primary": {
//                   fontFamily: "sans-serif",
//                   fontWeight: location.pathname === item.path ? 500 : 300,
//                   whiteSpace: "nowrap",
//                 },
//               }}
//             />
//           </ListItemButton>
//         </Tooltip>
//       </ListItem>
//     ));
//   };

//   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

//   const drawerContent = (
//     <Box
//       onMouseEnter={handleMouseEnter}
//       onMouseLeave={handleMouseLeave}
//       sx={{
//         height: "100%",
//         display: "flex",
//         flexDirection: "column",
//         overflowY: "auto",
//         overflowX: "hidden",
//         "&::-webkit-scrollbar": { display: "none" },
//         msOverflowStyle: "none",
//         scrollbarWidth: "none",
//       }}
//     >
//       <Box
//         sx={{
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "flex-start",
//           py: 1.5,
//           pl: 1.75,
//           ...(!isExpanded &&
//             !isSmallScreen && { justifyContent: "center", pl: 0 }),
//         }}
//       >
//         <IconButton
//           onClick={onToggle}
//           sx={{
//             color: textColor,
//             display: { xs: "none", md: "inline-flex" },
//             mr: isExpanded ? 1 : 0,
//           }}
//         >
//           <MenuIcon />
//         </IconButton>
//       </Box>
//       <List sx={{ pt: 1 }}>{renderMenuItems(currentTopMenuItems)}</List>
//       <Box sx={{ flexGrow: 1 }} />
//       <List sx={{ pb: 1 }}>{renderMenuItems(bottomMenuItems)}</List>
//     </Box>
//   );

//   return (
//     <>
//       <Dialog
//         open={openLogoutConfirm}
//         onClose={handleCloseLogoutConfirm}
//         PaperProps={{
//           sx: {
//             borderRadius: "12px",
//             minWidth: "350px",
//             // Use theme color for dialog background
//             // bgcolor: "background.paper",
//             backgroundColor: "#F1FFFA"
//           },
//         }}
//         BackdropProps={{
//           sx: {
//             backgroundColor: "rgba(0,0,0,0.5)",
//           },
//         }}
//       >
//         <DialogContent
//           sx={{
//             pt: 4,
//             pb: 2,
//             display: "flex",
//             flexDirection: "column",
//             alignItems: "center",
//             gap: 2,
//           }}
//         >
//           <WarningAmberIcon sx={{ fontSize: 50, color: "warning.main" }} />
//           <Typography
//             variant="h5"
//             component="div"
//             sx={{
//               fontWeight: "bold",
//               fontSize: "1.25rem",
//               textAlign: "center",
//             }}
//           >
//             Are you sure?
//           </Typography>
//           <Typography
//             variant="body2"
//             sx={{ color: "text.secondary", textAlign: "center" }}
//           >
//             Are you sure you want to log out?
//           </Typography>
//         </DialogContent>
//         <DialogActions
//           sx={{ justifyContent: "center", pb: 3, pt: 1, gap: 1.5 }}
//         >
//           <Button
//             onClick={handleCloseLogoutConfirm}
//             variant="outlined"
//             color="secondary"
//             sx={{
//               borderRadius: "6px",
//               textTransform: "none",
//               px: 3,
//             }}
//           >
//             Cancel
//           </Button>
//           <Button
//             onClick={handleConfirmLogout}
//             variant="contained"
//             color="error"
//             sx={{
//               borderRadius: "6px",
//               textTransform: "none",
//               px: 4,
//             }}
//           >
//             Logout
//           </Button>
//         </DialogActions>
//       </Dialog>

//       {isSmallScreen ? (
//         <Drawer
//           variant="temporary"
//           open={mobileOpen}
//           onClose={onMobileToggle}
//           anchor="left"
//           ModalProps={{ keepMounted: true }}
//           sx={{
//             display: { xs: "block", md: "none" },
//             zIndex: theme.zIndex.drawer + 2, // Ensure it's above the AppBar
//             "& .MuiDrawer-paper": {
//               boxSizing: "border-box",
//               width: drawerWidth,
//               backgroundColor: sidebarBackgroundColor,
//               borderRight: "none",
//               mt: mobileAppBarHeight,
//               height: `calc(100% - ${mobileAppBarHeight})`,
//             },
//           }}
//         >
//           {drawerContent}
//         </Drawer>
//       ) : (
//         <Drawer
//           variant="permanent"
//           open={open}
//           anchor="left"
//           sx={{
//             display: { xs: "none", md: "block" },
//             width: isExpanded ? drawerWidth : drawerWidthCollapsed,
//             flexShrink: 0,
//             "& .MuiDrawer-paper": {
//               width: isExpanded ? drawerWidth : drawerWidthCollapsed,
//               boxSizing: "border-box",
//               backgroundColor: sidebarBackgroundColor,
//               borderRight: "none",
//               overflowX: "hidden",
//               transition: theme.transitions.create("width", {
//                 easing: theme.transitions.easing.sharp,
//                 duration: theme.transitions.duration.enteringScreen,
//               }),
//               // Adjust margin to not overlap with AppBar
//               mt: `${theme.spacing(10)}`, // Approx 80px
//               height: `calc(100% - ${theme.spacing(12)})`, // 100% - top&bottom margin
//               ml: 1,
//               borderRadius: theme.shape.borderRadius * 2,
//             },
//           }}
//         >
//           {drawerContent}
//         </Drawer>
//       )}
//     </>
//   );
// };

// export default Sidebar;


// import React, { useState } from 'react';
// import {
//   Drawer,
//   List,
//   ListItem,
//   ListItemIcon,
//   ListItemText,
//   ListItemButton,
//   Box,
//   IconButton,
//   useTheme,
//   useMediaQuery,
//   Tooltip,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   Typography,
//   Button,
// } from "@mui/material";
// import {
//   SettingsOutlined,
//   Dashboard,
//   Person,
//   Work,
//   Psychology,
//   Badge,
//   PostAdd,
//   People,
//   VideoCall,
//   Analytics,
//   Map,
//   LogoutOutlined,
//   Menu as MenuIcon,
//   WarningAmber as WarningAmberIcon,
// } from "@mui/icons-material";
// import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// import { useNavigate, useLocation } from "react-router-dom";
// import { useAuth } from "../../context/AuthContext";

// const drawerWidth = 260;
// const drawerWidthCollapsed = 64;
// const sidebarBackgroundColor = "#5741D9";
// const textColor = "#FFFFFF";
// const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
// const mobileAppBarHeight = "56px";

// const topMenuItems = {
//   talent: [
//     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
//     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
//     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
//     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
//     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
//     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
//     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam-intro" },
//     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
//   ],
//   employer: [
//     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
//     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
//     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
//     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
//     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
//     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
//   ],
//   admin: [
//     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
//     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
//     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
//     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
//   ],
// };

// const bottomMenuItems = [
//   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
//   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// ];

// const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
//   const { user, logout } = useAuth();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const theme = useTheme();
//   const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
//   const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
//   const [isHovered, setIsHovered] = useState(false);
//   const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);

//   const handleItemClick = (item) => {
//     if (item.text === "Logout") {
//       setOpenLogoutConfirm(true);
//     } else {
//       navigate(item.path);
//       if (isSmallScreen) {
//         onMobileToggle();
//       }
//     }
//   };

//   const handleCloseLogoutConfirm = () => {
//     setOpenLogoutConfirm(false);
//   };

//   const handleConfirmLogout = () => {
//     logout();
//     navigate("/gatep/login");
//     setOpenLogoutConfirm(false);
//   };

//   const handleMouseEnter = () => {
//     if (isTablet && !open) {
//       setIsHovered(true);
//     }
//   };

//   const handleMouseLeave = () => {
//     if (isTablet) {
//       setIsHovered(false);
//     }
//   };

//   const isExpanded = open || isHovered;

//   const renderMenuItems = (items) => {
//     if (!Array.isArray(items)) {
//       return null;
//     }
//     return items.map((item) => (
//       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
//         <Tooltip title={!isExpanded && !isSmallScreen ? item.text : ""} placement="right">
//           <ListItemButton
//             onClick={() => handleItemClick(item)}
//             selected={location.pathname === item.path}
//             sx={{
//               minHeight: 48,
//               justifyContent: isExpanded ? "initial" : "center",
//               my: 0.5,
//               borderRadius: '25px',
//               color: textColor,
//               px: 2.5,
//               mx: 1.5,
//               "&:hover": { backgroundColor: hoverBackgroundColor },
//               "&.Mui-selected": {
//                 backgroundColor: "#FFFFFF",
//                 color: sidebarBackgroundColor,
//                 "&:hover": { backgroundColor: "#FFFFFF" },
//               },
//             }}
//           >
//             <ListItemIcon sx={{ minWidth: 0, mr: isExpanded ? 3 : "auto", justifyContent: "center", color: "inherit" }}>
//               {item.icon}
//             </ListItemIcon>
//             <ListItemText
//               primary={item.text}
//               sx={{
//                 opacity: isExpanded ? 1 : 0,
//                 "& .MuiListItemText-primary": {
//                   fontFamily: 'sans-serif',
//                   fontWeight: location.pathname === item.path ? 500 : 300,
//                   whiteSpace: 'nowrap',
//                 },
//               }}
//             />
//           </ListItemButton>
//         </Tooltip>
//       </ListItem>
//     ));
//   };

//   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

//   const drawerContent = (
//     <Box
//       onMouseEnter={handleMouseEnter}
//       onMouseLeave={handleMouseLeave}
//       sx={{
//         height: "100%",
//         display: "flex",
//         flexDirection: "column",
//         overflowY: 'auto',
//         overflowX: 'hidden',
//         '&::-webkit-scrollbar': { display: 'none' },
//         msOverflowStyle: 'none',
//         scrollbarWidth: 'none',
//       }}
//     >
//       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', py: 1.5, pl: 1.75, ...(!isExpanded && !isSmallScreen && { justifyContent: 'center', pl: 0 }) }}>
//         <IconButton onClick={onToggle} sx={{ color: textColor, display: { xs: 'none', md: 'inline-flex' }, mr: isExpanded ? 1 : 0 }}>
//           <MenuIcon />
//         </IconButton>
//       </Box>
//       <List sx={{ pt: 1 }}>{renderMenuItems(currentTopMenuItems)}</List>
//       <Box sx={{ flexGrow: 1 }} />
//       <List sx={{ pb: 1 }}>{renderMenuItems(bottomMenuItems)}</List>
//     </Box>
//   );

//   return (
//     <>
//       <Dialog
//         open={openLogoutConfirm}
//         onClose={handleCloseLogoutConfirm}
//         PaperProps={{
//           sx: {
//             borderRadius: '8px',
//             minWidth: '350px',
//           },
//         }}
//         BackdropProps={{
//           sx: {
//             backgroundColor: 'rgba(0,0,0,0.5)',
//           },
//         }}
//       >
//         <DialogContent sx={{ pt: 4, pb: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
//           <WarningAmberIcon sx={{ fontSize: 50, color: '#e6a23c' }} />
//           <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', fontSize: '1.25rem', textAlign: 'center' }}>
//             Are you sure?
//           </Typography>
//           <Typography variant="body2" sx={{ color: '#555', textAlign: 'center' }}>
//             Are you sure you want to log out?
//           </Typography>
//         </DialogContent>
//         <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 1, gap: 1.5 }}>
//           <Button
//             onClick={handleCloseLogoutConfirm}
//             sx={{
//               backgroundColor: '#f0f0f0',
//               color: '#000',
//               borderRadius: '6px',
//               textTransform: 'none',
//               px: 3,
//               '&:hover': {
//                 backgroundColor: '#e0e0e0',
//               },
//             }}
//           >
//             Cancel
//           </Button>
//           <Button
//             onClick={handleConfirmLogout}
//             sx={{
//               backgroundColor: '#d9534f',
//               color: '#fff',
//               borderRadius: '6px',
//               textTransform: 'none',
//               px: 4,
//               '&:hover': {
//                 backgroundColor: '#c9302c',
//               },
//             }}
//           >
//             Logout
//           </Button>
//         </DialogActions>
//       </Dialog>

//       {isSmallScreen ? (
//         <Drawer
//           variant="temporary"
//           open={mobileOpen}
//           onClose={onMobileToggle}
//           anchor="left"
//           ModalProps={{ keepMounted: true }}
//           sx={{
//             display: { xs: 'block', md: 'none' },
//             zIndex: theme.zIndex.drawer,
//             '& .MuiDrawer-paper': {
//               boxSizing: 'border-box',
//               width: drawerWidth,
//               backgroundColor: sidebarBackgroundColor,
//               borderRight: "none",
//               mt: mobileAppBarHeight,
//               height: `calc(100% - ${mobileAppBarHeight})`,
//             },
//           }}
//         >
//           {drawerContent}
//         </Drawer>
//       ) : (
//         <Drawer
//           variant="permanent"
//           open={open}
//           anchor="left"
//           sx={{
//             display: { xs: 'none', md: 'block' },
//             width: isExpanded ? drawerWidth : drawerWidthCollapsed,
//             flexShrink: 0,
//             "& .MuiDrawer-paper": {
//               width: isExpanded ? drawerWidth : drawerWidthCollapsed,
//               boxSizing: "border-box",
//               backgroundColor: sidebarBackgroundColor,
//               borderRight: "none",
//               overflowX: 'hidden',
//               transition: (theme) => theme.transitions.create('width', {
//                 easing: theme.transitions.easing.sharp,
//                 duration: theme.transitions.duration.enteringScreen,
//               }),
//               mt: 8.5,
//               height: 'calc(94% - 32px)',
//               borderRadius: (theme) => theme.shape.borderRadius * 1,
//             },
//           }}
//         >
//           {drawerContent}
//         </Drawer>
//       )}
//     </>
//   );
// };

// export default Sidebar;
// // import React, { useState } from 'react';
// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   IconButton,
// //   useTheme,
// //   useMediaQuery,
// //   Tooltip,
// //   Dialog,
// //   DialogActions,
// //   DialogContent,
// //   DialogContentText,
// //   DialogTitle,
// //   Button,
// // } from "@mui/material";
// // import {
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   LogoutOutlined,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 260;
// // const drawerWidthCollapsed = 64;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
// // const mobileAppBarHeight = "56px";

// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam-intro" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //   ],
// // };

// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
// //   const { user, logout } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
// //   const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
// //   const [isHovered, setIsHovered] = useState(false);
// //   const [openLogoutConfirm, setOpenLogoutConfirm] = useState(false);

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       setOpenLogoutConfirm(true);
// //     } else {
// //       navigate(item.path);
// //       if (isSmallScreen) {
// //         onMobileToggle();
// //       }
// //     }
// //   };

// //   const handleCloseLogoutConfirm = () => {
// //     setOpenLogoutConfirm(false);
// //   };

// //   const handleConfirmLogout = () => {
// //     logout();
// //     navigate("/gatep/login");
// //     setOpenLogoutConfirm(false);
// //   };

// //   const handleMouseEnter = () => {
// //     if (isTablet && !open) {
// //       setIsHovered(true);
// //     }
// //   };

// //   const handleMouseLeave = () => {
// //     if (isTablet) {
// //       setIsHovered(false);
// //     }
// //   };

// //   const isExpanded = open || isHovered;

// //   const renderMenuItems = (items) => {
// //     if (!Array.isArray(items)) {
// //       return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <Tooltip title={!isExpanded && !isSmallScreen ? item.text : ""} placement="right">
// //           <ListItemButton
// //             onClick={() => handleItemClick(item)}
// //             selected={location.pathname === item.path}
// //             sx={{
// //               minHeight: 48,
// //               justifyContent: isExpanded ? "initial" : "center",
// //               my: 0.5,
// //               borderRadius: '25px',
// //               color: textColor,
// //               px: 2.5,
// //               mx: 1.5,
// //               "&:hover": { backgroundColor: hoverBackgroundColor },
// //               "&.Mui-selected": {
// //                 backgroundColor: "#FFFFFF",
// //                 color: sidebarBackgroundColor,
// //                 "&:hover": { backgroundColor: "#FFFFFF" },
// //               },
// //             }}
// //           >
// //             <ListItemIcon sx={{ minWidth: 0, mr: isExpanded ? 3 : "auto", justifyContent: "center", color: "inherit" }}>
// //               {item.icon}
// //             </ListItemIcon>
// //             <ListItemText
// //               primary={item.text}
// //               sx={{
// //                 opacity: isExpanded ? 1 : 0,
// //                 "& .MuiListItemText-primary": {
// //                   fontFamily: 'sans-serif',
// //                   fontWeight: location.pathname === item.path ? 500 : 300,
// //                   whiteSpace: 'nowrap',
// //                 },
// //               }}
// //             />
// //           </ListItemButton>
// //         </Tooltip>
// //       </ListItem>
// //     ));
// //   };

// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

// //   const drawerContent = (
// //     <Box
// //       onMouseEnter={handleMouseEnter}
// //       onMouseLeave={handleMouseLeave}
// //       sx={{
// //         height: "100%",
// //         display: "flex",
// //         flexDirection: "column",
// //         overflowY: 'auto',
// //         overflowX: 'hidden',
// //         '&::-webkit-scrollbar': { display: 'none' },
// //         msOverflowStyle: 'none',
// //         scrollbarWidth: 'none',
// //       }}
// //     >
// //       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', py: 1.5, pl: 1.75, ...(!isExpanded && !isSmallScreen && { justifyContent: 'center', pl: 0 }) }}>
// //         <IconButton onClick={onToggle} sx={{ color: textColor, display: { xs: 'none', md: 'inline-flex' }, mr: isExpanded ? 1 : 0 }}>
// //           <MenuIcon />
// //         </IconButton>
// //       </Box>
// //       <List sx={{ pt: 1 }}>{renderMenuItems(currentTopMenuItems)}</List>
// //       <Box sx={{ flexGrow: 1 }} />
// //       <List sx={{ pb: 1 }}>{renderMenuItems(bottomMenuItems)}</List>
// //     </Box>
// //   );

// //   return (
// //     <>
// //       <Dialog
// //         open={openLogoutConfirm}
// //         onClose={handleCloseLogoutConfirm}
// //         aria-labelledby="alert-dialog-title"
// //         aria-describedby="alert-dialog-description"
// //       >
// //         <DialogTitle id="alert-dialog-title">
// //           {"Logout Confirmation"}
// //         </DialogTitle>
// //         <DialogContent>
// //           <DialogContentText id="alert-dialog-description">
// //             Are you sure you want to log out?
// //           </DialogContentText>
// //         </DialogContent>
// //         <DialogActions>
// //           <Button onClick={handleCloseLogoutConfirm}>Cancel</Button>
// //           <Button onClick={handleConfirmLogout} autoFocus>
// //             Logout
// //           </Button>
// //         </DialogActions>
// //       </Dialog>

// //       {isSmallScreen ? (
// //         <Drawer
// //           variant="temporary"
// //           open={mobileOpen}
// //           onClose={onMobileToggle}
// //           anchor="left"
// //           ModalProps={{ keepMounted: true }}
// //           sx={{
// //             display: { xs: 'block', md: 'none' },
// //             zIndex: theme.zIndex.drawer,
// //             '& .MuiDrawer-paper': {
// //               boxSizing: 'border-box',
// //               width: drawerWidth,
// //               backgroundColor: sidebarBackgroundColor,
// //               borderRight: "none",
// //               mt: mobileAppBarHeight,
// //               height: `calc(100% - ${mobileAppBarHeight})`,
// //             },
// //           }}
// //         >
// //           {drawerContent}
// //         </Drawer>
// //       ) : (
// //         <Drawer
// //           variant="permanent"
// //           open={open}
// //           anchor="left"
// //           sx={{
// //             display: { xs: 'none', md: 'block' },
// //             width: isExpanded ? drawerWidth : drawerWidthCollapsed,
// //             flexShrink: 0,
// //             "& .MuiDrawer-paper": {
// //               width: isExpanded ? drawerWidth : drawerWidthCollapsed,
// //               boxSizing: "border-box",
// //               backgroundColor: sidebarBackgroundColor,
// //               borderRight: "none",
// //               overflowX: 'hidden',
// //               transition: (theme) => theme.transitions.create('width', {
// //                 easing: theme.transitions.easing.sharp,
// //                 duration: theme.transitions.duration.enteringScreen,
// //               }),
// //               mt: 8.5,
// //               height: 'calc(94% - 32px)',
// //               borderRadius: (theme) => theme.shape.borderRadius * 1,
// //             },
// //           }}
// //         >
// //           {drawerContent}
// //         </Drawer>
// //       )}
// //     </>
// //   );
// // };

// // export default Sidebar;
// // import React, { useState } from 'react';
// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   IconButton,
// //   useTheme,
// //   useMediaQuery,
// //   Tooltip,
// // } from "@mui/material";
// // import {
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   LogoutOutlined,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 260;
// // const drawerWidthCollapsed = 64;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
// // const mobileAppBarHeight = "56px";

// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam-intro" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //   ],
// // };

// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
// //   const { user, logout } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
// //   const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
// //   const [isHovered, setIsHovered] = useState(false);

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       logout();
// //       navigate("/gatep/login");
// //     } else {
// //       navigate(item.path);
// //     }
// //     if (isSmallScreen) {
// //       onMobileToggle();
// //     }
// //   };

// //   const handleMouseEnter = () => {
// //     if (isTablet && !open) {
// //       setIsHovered(true);
// //     }
// //   };

// //   const handleMouseLeave = () => {
// //     if (isTablet) {
// //       setIsHovered(false);
// //     }
// //   };

// //   const isExpanded = open || isHovered;

// //   const renderMenuItems = (items) => {
// //     if (!Array.isArray(items)) {
// //       return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <Tooltip title={!isExpanded && !isSmallScreen ? item.text : ""} placement="right">
// //           <ListItemButton
// //             onClick={() => handleItemClick(item)}
// //             selected={location.pathname === item.path}
// //             sx={{
// //               minHeight: 48,
// //               justifyContent: isExpanded ? "initial" : "center",
// //               my: 0.5,
// //               borderRadius: '25px',
// //               color: textColor,
// //               px: 2.5,
// //               mx: 1.5,
// //               "&:hover": { backgroundColor: hoverBackgroundColor },
// //               "&.Mui-selected": {
// //                 backgroundColor: "#FFFFFF",
// //                 color: sidebarBackgroundColor,
// //                 "&:hover": { backgroundColor: "#FFFFFF" },
// //               },
// //             }}
// //           >
// //             <ListItemIcon sx={{ minWidth: 0, mr: isExpanded ? 3 : "auto", justifyContent: "center", color: "inherit" }}>
// //               {item.icon}
// //             </ListItemIcon>
// //             <ListItemText
// //               primary={item.text}
// //               sx={{
// //                 opacity: isExpanded ? 1 : 0,
// //                 "& .MuiListItemText-primary": {
// //                   fontFamily: 'sans-serif',
// //                   fontWeight: location.pathname === item.path ? 500 : 300,
// //                   whiteSpace: 'nowrap',
// //                 },
// //               }}
// //             />
// //           </ListItemButton>
// //         </Tooltip>
// //       </ListItem>
// //     ));
// //   };

// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

// //   const drawerContent = (
// //     <Box
// //       onMouseEnter={handleMouseEnter}
// //       onMouseLeave={handleMouseLeave}
// //       sx={{
// //         height: "100%",
// //         display: "flex",
// //         flexDirection: "column",
// //         overflowY: 'auto',
// //         overflowX: 'hidden',
// //         '&::-webkit-scrollbar': { display: 'none' },
// //         msOverflowStyle: 'none',
// //         scrollbarWidth: 'none',
// //       }}
// //     >
// //       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', py: 1.5, pl: 1.75, ...(!isExpanded && !isSmallScreen && { justifyContent: 'center', pl: 0 }) }}>
// //         <IconButton onClick={onToggle} sx={{ color: textColor, display: { xs: 'none', md: 'inline-flex' }, mr: isExpanded ? 1 : 0 }}>
// //           <MenuIcon />
// //         </IconButton>
// //       </Box>
// //       <List sx={{ pt: 1 }}>{renderMenuItems(currentTopMenuItems)}</List>
// //       <Box sx={{ flexGrow: 1 }} />
// //       <List sx={{ pb: 1 }}>{renderMenuItems(bottomMenuItems)}</List>
// //     </Box>
// //   );

// //   if (isSmallScreen) {
// //     return (
// //       <Drawer
// //         variant="temporary"
// //         open={mobileOpen}
// //         onClose={onMobileToggle}
// //         anchor="left"
// //         ModalProps={{ keepMounted: true }}
// //         sx={{
// //           display: { xs: 'block', md: 'none' },
// //           zIndex: theme.zIndex.drawer,
// //           '& .MuiDrawer-paper': {
// //             boxSizing: 'border-box',
// //             width: drawerWidth,
// //             backgroundColor: sidebarBackgroundColor,
// //             borderRight: "none",
// //             mt: mobileAppBarHeight,
// //             height: `calc(100% - ${mobileAppBarHeight})`,
// //           },
// //         }}
// //       >
// //         {drawerContent}
// //       </Drawer>
// //     );
// //   }

// //   return (
// //     <Drawer
// //       variant="permanent"
// //       open={open}
// //       anchor="left"
// //       sx={{
// //         display: { xs: 'none', md: 'block' },
// //         width: isExpanded ? drawerWidth : drawerWidthCollapsed,
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: isExpanded ? drawerWidth : drawerWidthCollapsed,
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none",
// //           overflowX: 'hidden',
// //           transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //           }),
// //           mt: 8.5,
// //           height: 'calc(94% - 32px)',
// //           borderRadius: (theme) => theme.shape.borderRadius * 1,
// //         },
// //       }}
// //     >
// //       {drawerContent}
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;
// // import React, { useState } from 'react';
// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   IconButton,
// //   useTheme,
// //   useMediaQuery,
// //   Tooltip,
// // } from "@mui/material";
// // import {
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   LogoutOutlined,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 260;
// // const drawerWidthCollapsed = 64;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
// // const mobileAppBarHeight = "56px";

// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //   ],
// // };

// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
// //   const { user, logout } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
// //   const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
// //   const [isHovered, setIsHovered] = useState(false);

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       logout();
// //       navigate("/gatep/login");
// //     } else {
// //       navigate(item.path);
// //     }
// //     if (isSmallScreen) {
// //       onMobileToggle();
// //     }
// //   };

// //   const handleMouseEnter = () => {
// //     if (isTablet && !open) {
// //       setIsHovered(true);
// //     }
// //   };

// //   const handleMouseLeave = () => {
// //     if (isTablet) {
// //       setIsHovered(false);
// //     }
// //   };

// //   const isExpanded = open || isHovered;

// //   const renderMenuItems = (items) => {
// //     if (!Array.isArray(items)) {
// //       return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <Tooltip title={!isExpanded && !isSmallScreen ? item.text : ""} placement="right">
// //           <ListItemButton
// //             onClick={() => handleItemClick(item)}
// //             selected={location.pathname === item.path}
// //             sx={{
// //               minHeight: 48,
// //               justifyContent: isExpanded ? "initial" : "center",
// //               my: 0.5,
// //               borderRadius: '25px',
// //               color: textColor,
// //               px: 2.5,
// //               mx: 1.5,
// //               "&:hover": { backgroundColor: hoverBackgroundColor },
// //               "&.Mui-selected": {
// //                 backgroundColor: "#FFFFFF",
// //                 color: sidebarBackgroundColor,
// //                 "&:hover": { backgroundColor: "#FFFFFF" },
// //               },
// //             }}
// //           >
// //             <ListItemIcon sx={{ minWidth: 0, mr: isExpanded ? 3 : "auto", justifyContent: "center", color: "inherit" }}>
// //               {item.icon}
// //             </ListItemIcon>
// //             <ListItemText
// //               primary={item.text}
// //               sx={{
// //                 opacity: isExpanded ? 1 : 0,
// //                 "& .MuiListItemText-primary": {
// //                   fontFamily: 'sans-serif',
// //                   fontWeight: location.pathname === item.path ? 500 : 300,
// //                   whiteSpace: 'nowrap',
// //                 },
// //               }}
// //             />
// //           </ListItemButton>
// //         </Tooltip>
// //       </ListItem>
// //     ));
// //   };

// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

// //   const drawerContent = (
// //     <Box
// //       onMouseEnter={handleMouseEnter}
// //       onMouseLeave={handleMouseLeave}
// //       sx={{
// //         height: "100%",
// //         display: "flex",
// //         flexDirection: "column",
// //         overflowY: 'auto',
// //         overflowX: 'hidden',
// //         '&::-webkit-scrollbar': { display: 'none' },
// //         msOverflowStyle: 'none',
// //         scrollbarWidth: 'none',
// //       }}
// //     >
// //       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', py: 1.5, pl: 1.75, ...(!isExpanded && !isSmallScreen && { justifyContent: 'center', pl: 0 }) }}>
// //         <IconButton onClick={onToggle} sx={{ color: textColor, display: { xs: 'none', md: 'inline-flex' }, mr: isExpanded ? 1 : 0 }}>
// //           <MenuIcon />
// //         </IconButton>
// //       </Box>
// //       <List sx={{ pt: 1 }}>{renderMenuItems(currentTopMenuItems)}</List>
// //       <Box sx={{ flexGrow: 1 }} />
// //       <List sx={{ pb: 1 }}>{renderMenuItems(bottomMenuItems)}</List>
// //     </Box>
// //   );

// //   if (isSmallScreen) {
// //     return (
// //       <Drawer
// //         variant="temporary"
// //         open={mobileOpen}
// //         onClose={onMobileToggle}
// //         anchor="left"
// //         ModalProps={{ keepMounted: true }}
// //         sx={{
// //           display: { xs: 'block', md: 'none' },
// //           zIndex: theme.zIndex.drawer,
// //           '& .MuiDrawer-paper': {
// //             boxSizing: 'border-box',
// //             width: drawerWidth,
// //             backgroundColor: sidebarBackgroundColor,
// //             borderRight: "none",
// //             mt: mobileAppBarHeight,
// //             height: `calc(100% - ${mobileAppBarHeight})`,
// //           },
// //         }}
// //       >
// //         {drawerContent}
// //       </Drawer>
// //     );
// //   }

// //   return (
// //     <Drawer
// //       variant="permanent"
// //       open={open}
// //       anchor="left"
// //       sx={{
// //         display: { xs: 'none', md: 'block' },
// //         width: isExpanded ? drawerWidth : drawerWidthCollapsed,
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: isExpanded ? drawerWidth : drawerWidthCollapsed,
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none",
// //           overflowX: 'hidden',
// //           transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //           }),
// //           mt: 8.5,
// //           height: 'calc(94% - 32px)',
// //           borderRadius: (theme) => theme.shape.borderRadius * 1,
// //         },
// //       }}
// //     >
// //       {drawerContent}
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;
// // import React, { useState } from 'react';
// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   IconButton,
// //   useTheme,
// //   useMediaQuery,
// //   Tooltip,
// // } from "@mui/material";
// // import {
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   LogoutOutlined,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 260;
// // const drawerWidthCollapsed = 64;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";

// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //   ],
// // };

// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
// //   const { user, logout } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
// //   const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
// //   const [isHovered, setIsHovered] = useState(false);

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       logout();
// //       navigate("/gatep/login");
// //     } else {
// //       navigate(item.path);
// //     }
// //     if (isSmallScreen) {
// //       onMobileToggle(); // Close mobile drawer on item click
// //     }
// //   };

// //   const handleMouseEnter = () => {
// //     if (isTablet && !open) {
// //       setIsHovered(true);
// //     }
// //   };

// //   const handleMouseLeave = () => {
// //     if (isTablet) {
// //       setIsHovered(false);
// //     }
// //   };

// //   const isExpanded = open || isHovered;

// //   const renderMenuItems = (items) => {
// //     if (!Array.isArray(items)) {
// //       return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <Tooltip title={!isExpanded && !isSmallScreen ? item.text : ""} placement="right">
// //           <ListItemButton
// //             onClick={() => handleItemClick(item)}
// //             selected={location.pathname === item.path}
// //             sx={{
// //               minHeight: 48,
// //               justifyContent: isExpanded ? "initial" : "center",
// //               my: 0.5,
// //               borderRadius: '25px',
// //               color: textColor,
// //               px: 2.5,
// //               mx: 1.5,
// //               "&:hover": { backgroundColor: hoverBackgroundColor },
// //               "&.Mui-selected": {
// //                 backgroundColor: "#FFFFFF",
// //                 color: sidebarBackgroundColor,
// //                 "&:hover": { backgroundColor: "#FFFFFF" },
// //               },
// //             }}
// //           >
// //             <ListItemIcon sx={{ minWidth: 0, mr: isExpanded ? 3 : "auto", justifyContent: "center", color: "inherit" }}>
// //               {item.icon}
// //             </ListItemIcon>
// //             <ListItemText
// //               primary={item.text}
// //               sx={{
// //                 opacity: isExpanded ? 1 : 0,
// //                 "& .MuiListItemText-primary": {
// //                   fontFamily: 'sans-serif',
// //                   fontWeight: location.pathname === item.path ? 500 : 300,
// //                   whiteSpace: 'nowrap',
// //                 },
// //               }}
// //             />
// //           </ListItemButton>
// //         </Tooltip>
// //       </ListItem>
// //     ));
// //   };

// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

// //   const drawerContent = (
// //     <Box
// //       onMouseEnter={handleMouseEnter}
// //       onMouseLeave={handleMouseLeave}
// //       sx={{
// //         height: "100%",
// //         display: "flex",
// //         flexDirection: "column",
// //         overflowY: 'auto',
// //         overflowX: 'hidden',
// //         '&::-webkit-scrollbar': { display: 'none' },
// //         msOverflowStyle: 'none',
// //         scrollbarWidth: 'none',
// //       }}
// //     >
// //       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', py: 1.5, pl: 1.75, ...(!isExpanded && !isSmallScreen && { justifyContent: 'center', pl: 0 }) }}>
// //         <IconButton onClick={onToggle} sx={{ color: textColor, display: { xs: 'none', md: 'inline-flex' }, mr: isExpanded ? 1 : 0 }}>
// //           <MenuIcon />
// //         </IconButton>
// //       </Box>
// //       <List sx={{ pt: 1 }}>{renderMenuItems(currentTopMenuItems)}</List>
// //       <Box sx={{ flexGrow: 1 }} />
// //       <List sx={{ pb: 1 }}>{renderMenuItems(bottomMenuItems)}</List>
// //     </Box>
// //   );

// //   if (isSmallScreen) {
// //     return (
// //       <Drawer
// //         variant="temporary"
// //         open={mobileOpen}
// //         onClose={onMobileToggle}
// //         anchor="left"
// //         ModalProps={{ keepMounted: true }} // Better for performance on mobile
// //         sx={{
// //           display: { xs: 'block', md: 'none' },
// //           '& .MuiDrawer-paper': {
// //             boxSizing: 'border-box',
// //             width: drawerWidth,
// //             backgroundColor: sidebarBackgroundColor,
// //             borderRight: "none",
// //           },
// //         }}
// //       >
// //         {drawerContent}
// //       </Drawer>
// //     );
// //   }

// //   return (
// //     <Drawer
// //       variant="permanent"
// //       open={open}
// //       anchor="left"
// //       sx={{
// //         display: { xs: 'none', md: 'block' },
// //         width: isExpanded ? drawerWidth : drawerWidthCollapsed,
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: isExpanded ? drawerWidth : drawerWidthCollapsed,
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none",
// //           overflowX: 'hidden',
// //           transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //           }),
// //           mt: 8.5,
// //           ml: 0.2,
// //           height: 'calc(94% - 32px)',
// //           borderRadius: (theme) => theme.shape.borderRadius * 1,
// //         },
// //       }}
// //     >
// //       {drawerContent}
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;
// // import React, { useState } from 'react';
// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   IconButton,
// //   useTheme,
// //   useMediaQuery,
// //   Tooltip,
// // } from "@mui/material";
// // import {
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   LogoutOutlined,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 260;
// // const drawerWidthCollapsed = 64;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";

// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //   ],
// // };

// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = ({ open, onToggle, mobileOpen, onMobileToggle }) => {
// //   const { user, logout } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
// //   const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));
// //   const [isHovered, setIsHovered] = useState(false);

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       logout();
// //       navigate("/gatep/login");
// //     } else {
// //       navigate(item.path);
// //     }
// //     if (isSmallScreen) {
// //       onMobileToggle();
// //     }
// //   };

// //   const handleMouseEnter = () => {
// //     if (isTablet && !open) {
// //       setIsHovered(true);
// //     }
// //   };

// //   const handleMouseLeave = () => {
// //     if (isTablet) {
// //       setIsHovered(false);
// //     }
// //   };

// //   const isExpanded = open || isHovered;

// //   const renderMenuItems = (items) => {
// //     if (!Array.isArray(items)) {
// //       return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <Tooltip title={!isExpanded && !isSmallScreen ? item.text : ""} placement="right">
// //           <ListItemButton
// //             onClick={() => handleItemClick(item)}
// //             selected={location.pathname === item.path}
// //             sx={{
// //               minHeight: 48,
// //               justifyContent: isExpanded ? "initial" : "center",
// //               my: 0.5,
// //               borderRadius: '25px',
// //               color: textColor,
// //               px: 2.5,
// //               mx: 1.5,
// //               "&:hover": {
// //                 backgroundColor: hoverBackgroundColor,
// //               },
// //               "&.Mui-selected": {
// //                 backgroundColor: "#FFFFFF",
// //                 color: sidebarBackgroundColor,
// //                 "&:hover": {
// //                   backgroundColor: "#FFFFFF",
// //                 },
// //               },
// //             }}
// //           >
// //             <ListItemIcon
// //               sx={{
// //                 minWidth: 0,
// //                 mr: isExpanded ? 3 : "auto",
// //                 justifyContent: "center",
// //                 color: "inherit",
// //               }}
// //             >
// //               {item.icon}
// //             </ListItemIcon>
// //             <ListItemText
// //               primary={item.text}
// //               sx={{
// //                 opacity: isExpanded ? 1 : 0,
// //                 "& .MuiListItemText-primary": {
// //                   fontFamily: 'sans-serif',
// //                   fontWeight: location.pathname === item.path ? 500 : 300,
// //                   whiteSpace: 'nowrap',
// //                 },
// //               }}
// //             />
// //           </ListItemButton>
// //         </Tooltip>
// //       </ListItem>
// //     ));
// //   };

// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

// //   const drawerContent = (
// //     <Box
// //       onMouseEnter={handleMouseEnter}
// //       onMouseLeave={handleMouseLeave}
// //       sx={{
// //         height: "100%",
// //         display: "flex",
// //         flexDirection: "column",
// //         overflowY: 'auto',
// //         overflowX: 'hidden',
// //         '&::-webkit-scrollbar': { display: 'none' },
// //         msOverflowStyle: 'none',
// //         scrollbarWidth: 'none',
// //       }}
// //     >
// //       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', py: 1.5, pl: 1.75, ...(!isExpanded && { justifyContent: 'center', pl: 0 }) }}>
// //         <IconButton onClick={onToggle} sx={{ color: textColor, display: { xs: 'none', md: 'inline-flex' }, mr: isExpanded ? 1 : 0 }}>
// //           <MenuIcon />
// //         </IconButton>
// //       </Box>
// //       <List sx={{ pt: 1 }}>
// //         {renderMenuItems(currentTopMenuItems)}
// //       </List>
// //       <Box sx={{ flexGrow: 1 }} />
// //       <List sx={{ pb: 1 }}>
// //         {renderMenuItems(bottomMenuItems)}
// //       </List>
// //     </Box>
// //   );

// //   return (
// //     <Drawer
// //       variant={isSmallScreen ? "temporary" : "permanent"}
// //       open={isSmallScreen ? mobileOpen : open}
// //       onClose={onMobileToggle}
// //       anchor="left"
// //       ModalProps={{
// //         keepMounted: true,
// //       }}
// //       sx={{
// //         width: isSmallScreen ? drawerWidth : (isExpanded ? drawerWidth : drawerWidthCollapsed),
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: isSmallScreen ? drawerWidth : (isExpanded ? drawerWidth : drawerWidthCollapsed),
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none",
// //           overflowX: 'hidden',
// //           transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //           }),
// //           ...(isSmallScreen ? {
// //             mt: 0,
// //             ml: 0,
// //             height: '100%',
// //             borderRadius: 0,
// //           } : {
// //             mt: 8.5,
// //             ml: 0.2,
// //             height: 'calc(94% - 32px)',
// //             borderRadius: (theme) => theme.shape.borderRadius * 1,
// //           }),
// //         },
// //       }}
// //     >
// //       {drawerContent}
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;

// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   IconButton,
// // } from "@mui/material";
// // import {
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   LogoutOutlined,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 260;
// // const drawerWidthCollapsed = 64;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";

// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //     // { text: "Integrations", icon: <Settings />, path: "/gatep/admin/integrations" },
// //   ],
// // };

// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = ({ open, onToggle }) => {
// //   const { user, logout } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       logout();
// //       navigate("/gatep/login");
// //     } else {
// //       navigate(item.path);
// //     }
// //   };

// //   const renderMenuItems = (items) => {
// //     if (!Array.isArray(items)) {
// //       return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <ListItemButton
// //           onClick={() => handleItemClick(item)}
// //           selected={location.pathname === item.path}
// //           sx={{
// //             minHeight: 48,
// //             justifyContent: "center",
// //             my: 0.5,
// //             borderRadius: '25px',
// //             color: textColor,
// //             ...(open
// //               ? {
// //                   px: 2.5,
// //                   mx: 1.5,
// //                 }
// //               : {
// //                   mx: "auto",
// //                   p: 1,
// //                   width: 48,
// //                   height: 48,
// //                 }
// //             ),
// //             "&:hover": {
// //               backgroundColor: hoverBackgroundColor,
// //             },
// //             "&.Mui-selected": {
// //               backgroundColor: "#FFFFFF",
// //               color: sidebarBackgroundColor,
// //               "&:hover": {
// //                 backgroundColor: "#FFFFFF",
// //               },
// //             },
// //           }}
// //         >
// //           <ListItemIcon
// //             sx={{
// //               minWidth: 0,
// //               justifyContent: "center",
// //               color: "inherit",
// //               mr: open ? 1 : 0,
// //             }}
// //           >
// //             {item.icon}
// //           </ListItemIcon>
// //           <ListItemText
// //             primary={item.text}
// //             sx={{
// //               opacity: open ? 1 : 0,
// //               "& .MuiListItemText-primary": {
// //                 fontFamily: 'sans-serif',
// //                 fontWeight: location.pathname === item.path ? 500 : 300,
// //               },
// //             }}
// //           />
// //         </ListItemButton>
// //       </ListItem>
// //     ));
// //   };

// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

// //   return (
// //     <Drawer
// //       variant="permanent"
// //       anchor="left"
// //       open={open}
// //       sx={{
// //         width: open ? drawerWidth : drawerWidthCollapsed,
// //         transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //         }),
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: open ? drawerWidth : drawerWidthCollapsed,
// //           overflowX: 'hidden',
// //           transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //           }),
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none",
// //           mt: 8.5,
// //           ml: 0.2,
// //           height: 'calc(94% - 32px)',
// //           borderRadius: (theme) => theme.shape.borderRadius * 1,
// //         },
// //       }}
// //     >
// //       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', py: 1.5, pl: 1.75 }}>
// //         <IconButton onClick={onToggle} sx={{ color: textColor }}>
// //           <MenuIcon />
// //         </IconButton>
// //       </Box>

// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //           overflowY: open ? 'auto' : 'hidden',
// //           '&::-webkit-scrollbar': { display: 'none' },
// //           msOverflowStyle: 'none',
// //           scrollbarWidth: 'none'
// //         }}
// //       >
// //         <List sx={{ pt: 1 }}>
// //           {renderMenuItems(currentTopMenuItems)}
// //         </List>
// //         <Box sx={{ flexGrow: 1 }} />
// //         <List sx={{ pb: 1 }}>
// //           {renderMenuItems(bottomMenuItems)}
// //         </List>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;

// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   IconButton,
// // } from "@mui/material";
// // import {
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   LogoutOutlined,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 260;
// // const drawerWidthCollapsed = 64;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
// // const selectedBackgroundColor = "rgba(255, 255, 255, 0.15)";

// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //     // { text: "Integrations", icon: <Settings />, path: "/gatep/admin/integrations" },
// //   ],
// // };

// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = ({ open, onToggle }) => {
// //   const { user, logout } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       logout();
// //       navigate("/gatep/login");
// //     } else {
// //       navigate(item.path);
// //     }
// //   };

// //   const renderMenuItems = (items) => {
// //     if (!Array.isArray(items)) {
// //       return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <ListItemButton
// //           onClick={() => handleItemClick(item)}
// //           selected={location.pathname === item.path}
// //           sx={{
// //             minHeight: 48,
// //             justifyContent: "center",
// //             my: 0.5,
// //             borderRadius: 1,
// //             color: textColor,
// //             ...(open
// //               ? { // Styles for OPEN state
// //                   px: 2.5,
// //                   mx: 1.5,
// //                 }
// //               : { // Styles for COLLAPSED state
// //                   mx: "auto",
// //                   p: 1,
// //                   width: 48,
// //                   height: 48,
// //                 }
// //             ),
// //             "&:hover": {
// //               backgroundColor: hoverBackgroundColor,
// //             },
// //             "&.Mui-selected": {
// //               backgroundColor: selectedBackgroundColor,
// //               "&:hover": {
// //                 backgroundColor: selectedBackgroundColor,
// //                 filter: 'brightness(1.1)',
// //               },
// //             },
// //           }}
// //         >
// //           <ListItemIcon
// //             sx={{
// //               minWidth: 0,
// //               justifyContent: "center",
// //               color: "inherit",
// //               mr: open ? 1 : 0, // No margin when collapsed
// //             }}
// //           >
// //             {item.icon}
// //           </ListItemIcon>
// //           <ListItemText
// //             primary={item.text}
// //             sx={{
// //               opacity: open ? 1 : 0,
// //               "& .MuiListItemText-primary": {
// //                 fontWeight: location.pathname === item.path ? 500 : 300,
// //               },
// //             }}
// //           />
// //         </ListItemButton>
// //       </ListItem>
// //     ));
// //   };

// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

// //   return (
// //     <Drawer
// //       variant="permanent"
// //       anchor="left"
// //       open={open}
// //       sx={{
// //         width: open ? drawerWidth : drawerWidthCollapsed,
// //         transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //         }),
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: open ? drawerWidth : drawerWidthCollapsed,
// //           overflowX: 'hidden',
// //           transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //           }),
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none",
// //           mt: 8,
// //           ml: 0.2,
// //           height: 'calc(95% - 32px)',
// //           borderRadius: (theme) => theme.shape.borderRadius * 1,
// //         },
// //       }}
// //     >
// //       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', py: 1.5, pl: 1.75 }}>
// //         <IconButton onClick={onToggle} sx={{ color: textColor }}>
// //           <MenuIcon />
// //         </IconButton>
// //       </Box>

// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //           overflowY: open ? 'auto' : 'hidden',
// //           '&::-webkit-scrollbar': { display: 'none' },
// //           msOverflowStyle: 'none',
// //           scrollbarWidth: 'none'
// //         }}
// //       >
// //         <List sx={{ pt: 1 }}>
// //           {renderMenuItems(currentTopMenuItems)}
// //         </List>
// //         <Box sx={{ flexGrow: 1 }} />
// //         <List sx={{ pb: 1 }}>
// //           {renderMenuItems(bottomMenuItems)}
// //         </List>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;
// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   IconButton,
// // } from "@mui/material";
// // import {
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   LogoutOutlined,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 260;
// // const drawerWidthCollapsed = 64;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
// // const selectedBackgroundColor = "rgba(255, 255, 255, 0.15)";

// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam-intro" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //     { text: "Integrations", icon: <Settings />, path: "/gatep/admin/integrations" },
// //   ],
// // };

// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = ({ open, onToggle }) => {
// //   const { user, logout } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       logout();
// //       navigate("/gatep/login");
// //     } else {
// //       navigate(item.path);
// //     }
// //   };

// //   const renderMenuItems = (items) => {
// //     if (!Array.isArray(items)) {
// //       return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <ListItemButton
// //           onClick={() => handleItemClick(item)}
// //           selected={location.pathname === item.path}
// //           sx={{
// //             minHeight: 48,
// //             justifyContent: open ? "initial" : "center",
// //             px: 2.5,
// //             mx: 1.5,
// //             my: 0.5,
// //             borderRadius: 2,
// //             color: textColor,
// //             "&:hover": {
// //               backgroundColor: hoverBackgroundColor,
// //             },
// //             "&.Mui-selected": {
// //               backgroundColor: selectedBackgroundColor,
// //               "&:hover": {
// //                 backgroundColor: selectedBackgroundColor,
// //                 filter: 'brightness(1.1)',
// //               },
// //             },
// //           }}
// //         >
// //           <ListItemIcon
// //             sx={{
// //               minWidth: 0,
// //               mr: open ? 1 : "auto",
// //               justifyContent: "center",
// //               color: "inherit",
// //             }}
// //           >
// //             {item.icon}
// //           </ListItemIcon>
// //           <ListItemText
// //             primary={item.text}
// //             sx={{
// //               opacity: open ? 1 : 0,
// //               "& .MuiListItemText-primary": {
// //                 fontWeight: location.pathname === item.path ? 500 : 300,
// //               },
// //             }}
// //           />
// //         </ListItemButton>
// //       </ListItem>
// //     ));
// //   };

// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

// //   return (
// //     <Drawer
// //       variant="permanent"
// //       anchor="left"
// //       open={open}
// //       sx={{
// //         width: open ? drawerWidth : drawerWidthCollapsed,
// //         transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //         }),
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: open ? drawerWidth : drawerWidthCollapsed,
// //           overflowX: 'hidden',
// //           transition: (theme) => theme.transitions.create('width', {
// //             easing: theme.transitions.easing.sharp,
// //             duration: theme.transitions.duration.enteringScreen,
// //           }),
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none",
// //           mt: 8,
// //           ml: 0.2,
// //           height: 'calc(95% - 32px)',
// //           borderRadius: (theme) => theme.shape.borderRadius * 2,
// //         },
// //       }}
// //     >
// //       <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', py: 1.5, pl: 1.75 }}>
// //         <IconButton onClick={onToggle} sx={{ color: textColor }}>
// //           <MenuIcon />
// //         </IconButton>
// //       </Box>

// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //           overflowY: open ? 'auto' : 'hidden',
// //           '&::-webkit-scrollbar': { display: 'none' },
// //           msOverflowStyle: 'none',
// //           scrollbarWidth: 'none'
// //         }}
// //       >
// //         <List sx={{ pt: 1 }}>
// //           {renderMenuItems(currentTopMenuItems)}
// //         </List>
// //         <Box sx={{ flexGrow: 1 }} />
// //         <List sx={{ pb: 1 }}>
// //           {renderMenuItems(bottomMenuItems)}
// //         </List>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;
// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// // } from "@mui/material";
// // import {
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   LogoutOutlined,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';

// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // // Define constants for styling
// // const drawerWidth = 260;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
// // const selectedBackgroundColor = "rgba(255, 255, 255, 0.15)";

// // // --- Menu Item Definitions ---

// // // Top section of the sidebar menu
// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam-intro" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //     { text: "Integrations", icon: <Settings />, path: "/gatep/admin/integrations" },
// //   ],
// // };

// // // Bottom section of the sidebar menu
// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = () => {
// //   const { user, logout } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       logout();
// //       navigate("/gatep/login");
// //     } else {
// //       navigate(item.path);
// //     }
// //   };

// //   const renderMenuItems = (items) => {
// //     if (!Array.isArray(items)) {
// //         return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <ListItemButton
// //           onClick={() => handleItemClick(item)}
// //           selected={location.pathname === item.path}
// //           sx={{
// //             mx: 1.5,
// //             my: 0.5,
// //             borderRadius: 2,
// //             minHeight: 48,
// //             color: textColor,
// //             "& .MuiListItemIcon-root": {
// //               minWidth: "48px",
// //               color: textColor,
// //             },
// //             "& .MuiListItemText-primary": {
// //               fontWeight: 300,
// //             },
// //             "&:hover": {
// //               backgroundColor: hoverBackgroundColor,
// //             },
// //             "&.Mui-selected": {
// //               backgroundColor: selectedBackgroundColor,
// //               "& .MuiListItemText-primary": {
// //                 fontWeight: 500,
// //               },
// //               "&:hover": {
// //                 backgroundColor: selectedBackgroundColor,
// //                 filter: 'brightness(1.1)',
// //               },
// //             },
// //             transition: (theme) =>
// //               theme.transitions.create(["background-color", "filter"], {
// //                 duration: theme.transitions.duration.short,
// //               }),
// //           }}
// //         >
// //           <ListItemIcon>{item.icon}</ListItemIcon>
// //           <ListItemText primary={item.text} />
// //         </ListItemButton>
// //       </ListItem>
// //     ));
// //   };

// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];

// //   return (
// //     <Drawer
// //       variant="permanent"
// //       anchor="left"
// //       sx={{
// //         width: drawerWidth,
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: drawerWidth,
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none",
// //           mt: 8,
// //           ml: 0.2,
// //           height: 'calc(95% - 32px)',
// //           borderRadius: (theme) => theme.shape.borderRadius * 2,
// //         },
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //           overflowY: 'auto',
// //           '&::-webkit-scrollbar': {
// //             display: 'none'
// //           },
// //           msOverflowStyle: 'none',
// //           scrollbarWidth: 'none'
// //         }}
// //       >
// //         <List sx={{ pt: 1 }}>
// //           {renderMenuItems(currentTopMenuItems)}
// //         </List>

// //         <Box sx={{ flexGrow: 1 }} />

// //         <List sx={{ pb: 1 }}>
// //           {renderMenuItems(bottomMenuItems)}
// //         </List>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;
// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// // } from "@mui/material";
// // import {
// //   HomeOutlined,
// //   PersonOutlined,
// //   SearchOutlined,
// //   ExploreOutlined,
// //   ArticleOutlined,
// //   SettingsOutlined,
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   Menu as MenuIcon,
// //   LogoutOutlined,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';

// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext"; // Assuming this is correctly set up

// // // Define constants for styling
// // const drawerWidth = 260;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
// // const selectedBackgroundColor = "rgba(255, 255, 255, 0.15)";

// // // --- Menu Item Definitions ---

// // // Top section of the sidebar menu
// // const topMenuItems = {
// //   talent: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //     { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //     { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //     { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //     { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //     { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //     { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam-intro" },
// //     { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //   ],
// //   employer: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //     { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //     { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //     { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //     { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //   ],
// //   admin: [
// //     { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //     { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //     { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //     { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //     { text: "Integrations", icon: <Settings />, path: "/gatep/admin/integrations" },
// //   ],
// // };

// // // Bottom section of the sidebar menu
// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/gatep/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/gatep/logout" },
// // ];

// // const Sidebar = () => {
// //   // --- FIX START ---
// //   // 1. Get user and logout function from the AuthContext
// //   const { user, logout } = useAuth();
// //   // --- FIX END ---

// //   const navigate = useNavigate();
// //   const location = useLocation();

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       // --- FIX: Uncommented logout call ---
// //       logout();
// //       // No need to navigate here, the logout function or a route guard should handle the redirect.
// //       // But navigating to /login is a safe fallback.
// //       navigate("/gatep/login");
// //     } else {
// //       navigate(item.path);
// //     }
// //   };

// //   // Helper function to render a list of menu items
// //   const renderMenuItems = (items) => {
// //     // Ensure 'items' is always an array before mapping
// //     if (!Array.isArray(items)) {
// //         return null;
// //     }
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <ListItemButton
// //           onClick={() => handleItemClick(item)}
// //           selected={location.pathname === item.path}
// //           sx={{
// //             mx: 2,
// //             mt: 2,
// //             borderRadius: 2,
// //             minHeight: 48,
// //             color: textColor,
// //             "& .MuiListItemIcon-root": {
// //               minWidth: "48px",
// //               color: textColor,
// //             },
// //             "& .MuiListItemText-primary": {
// //               fontWeight: 300,
// //             },
// //             "&:hover": {
// //               backgroundColor: hoverBackgroundColor,
// //             },
// //             "&.Mui-selected": {
// //               backgroundColor: selectedBackgroundColor,
// //               "& .MuiListItemText-primary": {
// //                 fontWeight: 500,
// //               },
// //               "&:hover": {
// //                 backgroundColor: selectedBackgroundColor,
// //                 filter: 'brightness(1.1)',
// //               },
// //             },
// //             transition: (theme) =>
// //               theme.transitions.create(["background-color", "filter"], {
// //                 duration: theme.transitions.duration.short,
// //               }),
// //           }}
// //         >
// //           <ListItemIcon>{item.icon}</ListItemIcon>
// //           <ListItemText primary={item.text} />
// //         </ListItemButton>
// //       </ListItem>
// //     ));
// //   };

// //   // --- FIX START ---
// //   // 2. Select the correct menu items based on the user's role.
// //   // Use optional chaining (user?.role) for safety in case user is not yet loaded.
// //   // Default to an empty array `[]` to prevent errors if the role doesn't exist.
// //   const currentTopMenuItems = user?.role ? topMenuItems[user.role] || [] : [];
// //   // --- FIX END ---

// //   return (
// //     <Drawer
// //       variant="permanent"
// //       anchor="left"
// //       sx={{
// //         width: drawerWidth,
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: drawerWidth,
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none",
// //           mt: 8,
// //           ml: 0.2,
// //           height: 'calc(95% - 32px)',
// //           borderRadius: (theme) => theme.shape.borderRadius * 2,
// //         },
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //         }}
// //       >
// //         {/* Top menu items */}
// //         <List sx={{ pt: 3 }}>
// //           {/* 3. Render the dynamically selected menu items */}
// //           {renderMenuItems(currentTopMenuItems)}
// //         </List>

// //         {/* This Box pushes the bottom items to the end */}
// //         <Box sx={{ flexGrow: 1 }} />

// //         {/* Bottom menu items (this was already correct) */}
// //         <List sx={{ pb: 3 }}>
// //           {renderMenuItems(bottomMenuItems)}
// //         </List>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;

// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// // } from "@mui/material";
// // import {
// //   HomeOutlined,
// //   PersonOutlined,
// //   SearchOutlined,
// //   ExploreOutlined,
// //   ArticleOutlined,
// //   SettingsOutlined,
// //   LogoutOutlined,
// // } from "@mui/icons-material";
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // // Define constants for styling
// // const drawerWidth = 260;
// // const sidebarBackgroundColor = "#5741D9";
// // const textColor = "#FFFFFF";
// // const hoverBackgroundColor = "rgba(255, 255, 255, 0.08)";
// // const selectedBackgroundColor = "rgba(255, 255, 255, 0.15)";

// // // --- Menu Item Definitions ---

// // // Top section of the sidebar menu
// // const topMenuItems = [
// //   { text: "Dashboard", icon: <HomeOutlined />, path: "/dashboard" },
// //   { text: "Profile Builder", icon: <PersonOutlined />, path: "/profile" },
// //   { text: "Global Jobs", icon: <SearchOutlined />, path: "/jobs" },
// //   { text: "AI Assistance", icon: <ExploreOutlined />, path: "/ai-assistant" },
// //   { text: "Skill passport", icon: <ArticleOutlined />, path: "/passport" },
// // ];

// // // Bottom section of the sidebar menu
// // const bottomMenuItems = [
// //   { text: "Setting", icon: <SettingsOutlined />, path: "/settings" },
// //   { text: "Logout", icon: <LogoutOutlined />, path: "/logout" },
// // ];

// // const Sidebar = () => {
// //   const { logout } = useAuth(); // Assuming useAuth provides a logout function
// //   const navigate = useNavigate();
// //   const location = useLocation();

// //   const handleItemClick = (item) => {
// //     if (item.text === "Logout") {
// //       // Handle logout logic here, e.g., calling a context method
// //       // logout();
// //       console.log("Logout action triggered");
// //       navigate("/login"); // Redirect to login page after logout
// //     } else {
// //       navigate(item.path);
// //     }
// //   };

// //   // Helper function to render a list of menu items
// //   const renderMenuItems = (items) => {
// //     return items.map((item) => (
// //       <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //         <ListItemButton
// //           onClick={() => handleItemClick(item)}
// //           selected={location.pathname === item.path}
// //           sx={{
// //             mx: 2,
// //             my: 0.5,
// //             borderRadius: 2,
// //             minHeight: 48,
// //             color: textColor,
// //             "& .MuiListItemIcon-root": {
// //               minWidth: "48px",
// //               color: textColor,
// //             },
// //             "& .MuiListItemText-primary": {
// //               fontWeight: 300, // Light font weight for text
// //             },
// //             "&:hover": {
// //               backgroundColor: hoverBackgroundColor,
// //             },
// //             "&.Mui-selected": {
// //               backgroundColor: selectedBackgroundColor,
// //               "& .MuiListItemText-primary": {
// //                 fontWeight: 500, // Make text slightly bolder when selected
// //               },
// //               "&:hover": {
// //                 backgroundColor: selectedBackgroundColor,
// //                 filter: 'brightness(1.1)',
// //               },
// //             },
// //             transition: (theme) =>
// //               theme.transitions.create(["background-color", "filter"], {
// //                 duration: theme.transitions.duration.short,
// //               }),
// //           }}
// //         >
// //           <ListItemIcon>{item.icon}</ListItemIcon>
// //           <ListItemText primary={item.text} />
// //         </ListItemButton>
// //       </ListItem>
// //     ));
// //   };

// //   return (
// //     <Drawer
// //       variant="permanent"
// //       anchor="left"
// //       sx={{
// //         width: drawerWidth,
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: drawerWidth,
// //           boxSizing: "border-box",
// //           backgroundColor: sidebarBackgroundColor,
// //           borderRight: "none", // Remove the default border
// //         },
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //         }}
// //       >
// //         {/* Top menu items */}
// //         <List sx={{ pt: 3 }}>
// //           {renderMenuItems(topMenuItems)}
// //         </List>

// //         {/* This Box pushes the bottom items to the end */}
// //         <Box sx={{ flexGrow: 1 }} />

// //         {/* Bottom menu items */}
// //         <List sx={{ pb: 3 }}>
// //           {renderMenuItems(bottomMenuItems)}
// //         </List>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;
// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   Typography,
// //   useTheme,
// //   useMediaQuery,
// //   alpha,
// //   Tooltip,
// // } from "@mui/material";
// // import {
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 256;
// // const drawerWidthCollapsed = 64;

// // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// //   const { user } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// //   const accentColor2 = "#d130dfff"; // The pink/purple-ish color
// //   const accentColor1 = "#0e39b8ff"; // The vibrant blue color

// //   const menuItems = {
// //     talent: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //       {
// //         text: "Profile Builder",
// //         icon: <Person />,
// //         path: "/gatep/talent/profile",
// //       },
// //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //       {
// //         text: "AI Assistant",
// //         icon: <Psychology />,
// //         path: "/gatep/talent/assistant",
// //       },
// //       {
// //         text: "Skills Passport",
// //         icon: <Badge />,
// //         path: "/gatep/talent/passport",
// //       },
// //       {
// //         text: "Mock Interview",
// //         icon: <Psychology />,
// //         path: "/gatep/talent/exam-intro",
// //       },
// //       {
// //         text: "Report",
// //         icon: <ListAltTwoToneIcon />,
// //         path: "/gatep/talent/report",
// //       },
// //     ],
// //     employer: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //       {
// //         text: "Company Profile",
// //         icon: <PostAdd />,
// //         path: "/gatep/employer/company-profile",
// //       },
// //       {
// //         text: "Post Jobs",
// //         icon: <PostAdd />,
// //         path: "/gatep/employer/post-job",
// //       },
// //       {
// //         text: "Candidates",
// //         icon: <People />,
// //         path: "/gatep/employer/candidates",
// //       },
// //       {
// //         text: "Interviews",
// //         icon: <VideoCall />,
// //         path: "/gatep/employer/interviews",
// //       },
// //       {
// //         text: "Analytics",
// //         icon: <Analytics />,
// //         path: "/gatep/employer/analytics",
// //       },
// //     ],
// //     admin: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //       {
// //         text: "Analytics",
// //         icon: <Analytics />,
// //         path: "/gatep/admin/analytics",
// //       },
// //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //       // {
// //       //   text: "Integrations",
// //       //   icon: <Settings />,
// //       //   path: "/gatep/admin/integrations",
// //       // },
// //     ],
// //   };

// //   const currentMenuItems = menuItems[user?.role] || [];

// //   const handleNavigation = (path) => {
// //     navigate(path);
// //     if (isMobile) {
// //       onClose();
// //     }
// //   };

// //   const normalizedPathname = location.pathname.length > 1 ? location.pathname.replace(/\/$/, '') : location.pathname;

// //   return (
// //     <Drawer
// //       anchor="left"
// //       open={open}
// //       variant={variant}
// //       onClose={onClose}
// //       sx={{
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: open ? drawerWidth : drawerWidthCollapsed,
// //           boxSizing: "border-box",
// //           mt: !isMobile ? 9 : 0,
// //           ml: !isMobile ? 0 : 0,
// //           mb: !isMobile ? 0.10 : 0,
// //           height: !isMobile ? 'calc(100% - 80px)' : '100%',
// //           borderRadius: !isMobile ? 4 : 0,
// //           border: "1px solid",
// //           borderColor: alpha(theme.palette.divider, 0.3),
// //           background:"rgba(76, 123, 215, 0.2)",
// //             // theme.palette.mode === "light"
// //             //   ? `linear-gradient(135deg, ${alpha(
// //             //       theme.palette.background.paper,
// //             //       0.9
// //             //     )}, ${alpha(theme.palette.background.default, 0.8)})`
// //             //   : `linear-gradient(135deg, ${alpha(
// //             //       theme.palette.background.paper,
// //             //       0.9
// //             //     )}, ${alpha(theme.palette.background.default, 0.8)})`,
// //           boxShadow: !isMobile
// //             ? `0px 8px 20px ${alpha(
// //                 theme.palette.common.black,
// //                 0.1
// //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// //             : "none",
// //           transition: theme.transitions.create(
// //             ["width", "border-radius", "box-shadow", "background", "margin", "height"],
// //             {
// //               easing: theme.transitions.easing.easeInOut,
// //               duration: theme.transitions.duration.standard,
// //             }
// //           ),
// //         },
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //         }}
// //       >
// //         <List
// //           sx={{
// //             py: 1,
// //             flexGrow: 1,
// //             overflowY: "auto",
// //             mt: isMobile ? "54px" : 0,
// //           }}
// //         >
// //           {!isMobile && (
// //             <ListItem disablePadding sx={{ display: "block" }}>
// //               <ListItemButton
// //                 onClick={onToggle}
// //                 disableRipple
// //                 sx={{
// //                   mx: 1,
// //                   my: 0.5,
// //                   borderRadius: 2,
// //                   minHeight: 48,
// //                   justifyContent: open ? "initial" : "center",
// //                   px: 2.5,
// //                   backgroundColor: "transparent",
// //                   boxShadow: "none",
// //                   color: theme.palette.text.secondary,
// //                   "&:hover": {
// //                     backgroundColor: "transparent",
// //                   },
// //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// //                   "&:focus": { outline: "none", boxShadow: "none" },
// //                 }}
// //               >
// //                 <ListItemIcon
// //                   sx={{
// //                     minWidth: 0,
// //                     mr: open ? 2 : "auto",
// //                     justifyContent: "center",
// //                     color: "inherit",
// //                   }}
// //                 >
// //                   <MenuIcon />
// //                 </ListItemIcon>
// //                 <ListItemText
// //                   sx={{
// //                     opacity: open ? 1 : 0,
// //                     width: open ? "auto" : 0,
// //                     overflow: "hidden",
// //                     whiteSpace: "nowrap",
// //                     transition: theme.transitions.create(["opacity", "width"], {
// //                       easing: theme.transitions.easing.sharp,
// //                       duration: theme.transitions.duration.enteringScreen,
// //                     }),
// //                   }}
// //                 />
// //               </ListItemButton>
// //             </ListItem>
// //           )}
// //           {currentMenuItems.map((item) => (
// //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// //                 <ListItemButton
// //                   onClick={() => handleNavigation(item.path)}
// //                   selected={normalizedPathname === item.path}
// //                   sx={{
// //                     mx: 1,
// //                     my: 0.5,
// //                     borderRadius: 2,
// //                     minHeight: 48,
// //                     justifyContent: open ? "initial" : "center",
// //                     px: 2.5,
// //                     color: theme.palette.text.primary,
// //                     "& .MuiListItemIcon-root": {
// //                       color: theme.palette.text.secondary,
// //                     },
// //                     "&:hover": {
// //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// //                         accentColor1,
// //                         0.65
// //                       )}, ${alpha(accentColor2, 0.45)})`,
// //                       color: theme.palette.primary.contrastText,
// //                       transform: "scale(1.02)",
// //                       "& .MuiListItemIcon-root": {
// //                         color: theme.palette.primary.contrastText,
// //                       },
// //                     },
// //                     "&.Mui-selected": {
// //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// //                         accentColor1,
// //                         0.65
// //                       )}, ${alpha(accentColor2, 0.45)})`,
// //                       color: theme.palette.primary.contrastText,
// //                       fontWeight: "bold",
// //                       transform: "scale(1.01)",
// //                       "& .MuiListItemIcon-root": {
// //                         color: theme.palette.primary.contrastText,
// //                       },
// //                       "&:hover": {
// //                         backgroundImage: `linear-gradient(90deg, ${alpha(
// //                           accentColor1,
// //                           0.65
// //                         )}, ${alpha(accentColor2, 0.45)})`,
// //                         filter: "brightness(1.15)",
// //                       },
// //                     },
// //                     transition: theme.transitions.create(
// //                       ["background-image", "color", "transform", "filter"],
// //                       {
// //                         duration: theme.transitions.duration.short,
// //                         easing: theme.transitions.easing.easeInOut,
// //                       }
// //                     ),
// //                   }}
// //                 >
// //                   <ListItemIcon
// //                     sx={{
// //                       minWidth: 0,
// //                       mr: open ? 2 : "auto",
// //                       justifyContent: "center",
// //                       transition: theme.transitions.create("color", {
// //                         duration: theme.transitions.duration.short,
// //                       }),
// //                     }}
// //                   >
// //                     {item.icon}
// //                   </ListItemIcon>
// //                   <ListItemText
// //                     primary={item.text}
// //                     sx={{
// //                       opacity: open ? 1 : 0,
// //                       width: open ? "auto" : 0,
// //                       overflow: "hidden",
// //                       whiteSpace: "nowrap",
// //                       transition: theme.transitions.create(
// //                         ["opacity", "width"],
// //                         {
// //                           easing: theme.transitions.easing.sharp,
// //                           duration: theme.transitions.duration.enteringScreen,
// //                         }
// //                       ),
// //                     }}
// //                   />
// //                 </ListItemButton>
// //               </Tooltip>
// //             </ListItem>
// //           ))}
// //         </List>
// //         <Box
// //           sx={{
// //             py: open ? 2 : 0,
// //             px: 2,
// //             mt: "auto",
// //             borderTop: "1px solid",
// //             borderColor: open
// //               ? alpha(theme.palette.divider, 0.3)
// //               : "transparent",
// //             opacity: open ? 1 : 0,
// //             maxHeight: open ? 100 : 0,
// //             overflow: "hidden",
// //             transition: theme.transitions.create(
// //               ["opacity", "max-height", "padding", "border-color"],
// //               {
// //                 easing: theme.transitions.easing.sharp,
// //                 duration: theme.transitions.duration.enteringScreen,
// //               }
// //             ),
// //           }}
// //         >
// //           <Typography
// //             variant="caption"
// //             color="textSecondary"
// //             align="center"
// //             display="block"
// //           >
// //             © 2025 The Data Tech Labs
// //           </Typography>
// //         </Box>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;

// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   Typography,
// //   useTheme,
// //   useMediaQuery,
// //   alpha,
// //   Tooltip,
// // } from "@mui/material";
// // import {
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 256;
// // const drawerWidthCollapsed = 64;

// // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// //   const { user } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// //   // --- Gradient Colors ---
// //   const accentColor = "#eb63ccff"; // The vibrant magenta/purple (light end of gradient)
// //   const gradientStartColor = "#195ecdff"; // A deep navy blue (dark end of gradient)

// //   const menuItems = {
// //     talent: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //       {
// //         text: "Profile Builder",
// //         icon: <Person />,
// //         path: "/gatep/talent/profile",
// //       },
// //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //       {
// //         text: "AI Assistant",
// //         icon: <Psychology />,
// //         path: "/gatep/talent/assistant",
// //       },
// //       {
// //         text: "Skills Passport",
// //         icon: <Badge />,
// //         path: "/gatep/talent/passport",
// //       },
// //       {
// //         text: "Mock Interview",
// //         icon: <Psychology />,
// //         path: "/gatep/talent/exam-intro",
// //       },
// //       {
// //         text: "Report",
// //         icon: <ListAltTwoToneIcon />,
// //         path: "/gatep/talent/report",
// //       },
// //     ],
// //     employer: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //       {
// //         text: "Company Profile",
// //         icon: <PostAdd />,
// //         path: "/gatep/employer/company-profile",
// //       },
// //       {
// //         text: "Post Jobs",
// //         icon: <PostAdd />,
// //         path: "/gatep/employer/post-job",
// //       },
// //       {
// //         text: "Candidates",
// //         icon: <People />,
// //         path: "/gatep/employer/candidates",
// //       },
// //       {
// //         text: "Interviews",
// //         icon: <VideoCall />,
// //         path: "/gatep/employer/interviews",
// //       },
// //       {
// //         text: "Analytics",
// //         icon: <Analytics />,
// //         path: "/gatep/employer/analytics",
// //       },
// //     ],
// //     admin: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //       {
// //         text: "Analytics",
// //         icon: <Analytics />,
// //         path: "/gatep/admin/analytics",
// //       },
// //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //       // {
// //       //   text: "Integrations",
// //       //   icon: <Settings />,
// //       //   path: "/gatep/admin/integrations",
// //       // },
// //     ],
// //   };

// //   const currentMenuItems = menuItems[user?.role] || [];

// //   const handleNavigation = (path) => {
// //     navigate(path);
// //     if (isMobile) {
// //       onClose();
// //     }
// //   };

// //   const normalizedPathname = location.pathname.length > 1 ? location.pathname.replace(/\/$/, '') : location.pathname;

// //   return (
// //     <Drawer
// //       anchor="left"
// //       open={open}
// //       variant={variant}
// //       onClose={onClose}
// //       sx={{
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: open ? drawerWidth : drawerWidthCollapsed,
// //           boxSizing: "border-box",
// //           mt: !isMobile ? 9 : 0,
// //           ml: !isMobile ? 0 : 0,
// //           mb: !isMobile ? 0.10 : 0,
// //           height: !isMobile ? 'calc(100% - 80px)' : '100%',
// //           borderRadius: !isMobile ? 4 : 0,
// //           border: "1px solid",

// //           // --- START: Gradient and Color Styling ---
// //           borderColor: alpha(accentColor, 0.3),
// //           color: theme.palette.common.white, // Set default text/icon color to white for contrast
// //           background: `linear-gradient(145deg, ${gradientStartColor} 10%, ${accentColor} 100%)`,
// //           boxShadow: !isMobile
// //             ? `0px 8px 20px ${alpha(
// //                 accentColor,
// //                 0.2
// //               )}`
// //             : "none",
// //           // --- END: Gradient and Color Styling ---

// //           transition: theme.transitions.create(
// //             ["width", "border-radius", "box-shadow", "background", "margin", "height"],
// //             {
// //               easing: theme.transitions.easing.easeInOut,
// //               duration: theme.transitions.duration.standard,
// //             }
// //           ),
// //         },
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //         }}
// //       >
// //         <List
// //           sx={{
// //             py: 1,
// //             flexGrow: 1,
// //             overflowY: "auto",
// //             mt: isMobile ? "54px" : 0,
// //           }}
// //         >
// //           {!isMobile && (
// //             <ListItem disablePadding sx={{ display: "block" }}>
// //               <ListItemButton
// //                 onClick={onToggle}
// //                 disableRipple
// //                 sx={{
// //                   mx: 1,
// //                   my: 0.5,
// //                   borderRadius: 2,
// //                   minHeight: 48,
// //                   justifyContent: open ? "initial" : "center",
// //                   px: 2.5,
// //                   // --- Style Update for Contrast ---
// //                   color: alpha(theme.palette.common.white, 0.7),
// //                   "&:hover": {
// //                     backgroundColor: alpha(theme.palette.common.white, 0.1),
// //                     color: theme.palette.common.white,
// //                   },
// //                 }}
// //               >
// //                 <ListItemIcon
// //                   sx={{
// //                     minWidth: 0,
// //                     mr: open ? 2 : "auto",
// //                     justifyContent: "center",
// //                     color: "inherit",
// //                   }}
// //                 >
// //                   <MenuIcon />
// //                 </ListItemIcon>
// //               </ListItemButton>
// //             </ListItem>
// //           )}
// //           {currentMenuItems.map((item) => (
// //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// //                 <ListItemButton
// //                   onClick={() => handleNavigation(item.path)}
// //                   selected={normalizedPathname === item.path}
// //                   sx={{
// //                     mx: 1,
// //                     my: 0.5,
// //                     borderRadius: 2,
// //                     minHeight: 48,
// //                     justifyContent: open ? "initial" : "center",
// //                     px: 2.5,

// //                     // --- START: List Item Style Updates for Contrast ---
// //                     color: alpha(theme.palette.common.white, 0.7), // Slightly transparent for inactive items
// //                     "& .MuiListItemIcon-root": {
// //                       color: "inherit",
// //                     },
// //                     "&:hover": {
// //                       backgroundColor: alpha(theme.palette.common.white, 0.1), // Subtle glow on hover
// //                       color: theme.palette.common.white,
// //                       transform: "scale(1.02)",
// //                     },
// //                     "&.Mui-selected": {
// //                       backgroundColor: alpha(accentColor, 0.25), // Highlight with accent color
// //                       color: theme.palette.common.white,
// //                       fontWeight: "bold",
// //                       boxShadow: `0 0 12px ${alpha(accentColor, 0.5)}`,
// //                       "&:hover": {
// //                         backgroundColor: alpha(accentColor, 0.35),
// //                       },
// //                     },
// //                     // --- END: List Item Style Updates for Contrast ---

// //                     transition: theme.transitions.create(
// //                       ["background-color", "color", "transform", "box-shadow"],
// //                       {
// //                         duration: theme.transitions.duration.short,
// //                         easing: theme.transitions.easing.easeInOut,
// //                       }
// //                     ),
// //                   }}
// //                 >
// //                   <ListItemIcon
// //                     sx={{
// //                       minWidth: 0,
// //                       mr: open ? 2 : "auto",
// //                       justifyContent: "center",
// //                       color: "inherit",
// //                       transition: theme.transitions.create("color", {
// //                         duration: theme.transitions.duration.short,
// //                       }),
// //                     }}
// //                   >
// //                     {item.icon}
// //                   </ListItemIcon>
// //                   <ListItemText
// //                     primary={item.text}
// //                     sx={{
// //                       opacity: open ? 1 : 0,
// //                       width: open ? "auto" : 0,
// //                       overflow: "hidden",
// //                       whiteSpace: "nowrap",
// //                       transition: theme.transitions.create(
// //                         ["opacity", "width"],
// //                         {
// //                           easing: theme.transitions.easing.sharp,
// //                           duration: theme.transitions.duration.enteringScreen,
// //                         }
// //                       ),
// //                     }}
// //                   />
// //                 </ListItemButton>
// //               </Tooltip>
// //             </ListItem>
// //           ))}
// //         </List>
// //         <Box
// //           sx={{
// //             py: open ? 2 : 0,
// //             px: 2,
// //             mt: "auto",
// //             borderTop: "1px solid",

// //             // --- Style Update for Contrast ---
// //             borderColor: open
// //               ? alpha(theme.palette.common.white, 0.2)
// //               : "transparent",

// //             opacity: open ? 1 : 0,
// //             maxHeight: open ? 100 : 0,
// //             overflow: "hidden",
// //             transition: theme.transitions.create(
// //               ["opacity", "max-height", "padding", "border-color"],
// //               {
// //                 easing: theme.transitions.easing.sharp,
// //                 duration: theme.transitions.duration.enteringScreen,
// //               }
// //             ),
// //           }}
// //         >
// //           <Typography
// //             variant="caption"
// //             // --- Style Update for Contrast ---
// //             sx={{ color: alpha(theme.palette.common.white, 0.5) }}
// //             align="center"
// //             display="block"
// //           >
// //             © 2025 The Data Tech Labs
// //           </Typography>
// //         </Box>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;

// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   Typography,
// //   useTheme,
// //   useMediaQuery,
// //   alpha,
// //   Tooltip,
// // } from "@mui/material";
// // import {
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 256;
// // const drawerWidthCollapsed = 64;
// // const topSpacing = "80px"; // Consistent spacing for desktop app bar area

// // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// //   const { user } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// //   // Hardcoded accent colors removed to use theme palette
// //   // const accentColor2 = "#df30b6";
// //   // const accentColor1 = "#100eb8";

// //   const menuItems = {
// //     talent: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //       {
// //         text: "Profile Builder",
// //         icon: <Person />,
// //         path: "/gatep/talent/profile",
// //       },
// //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //       {
// //         text: "AI Assistant",
// //         icon: <Psychology />,
// //         path: "/gatep/talent/assistant",
// //       },
// //       {
// //         text: "Skills Passport",
// //         icon: <Badge />,
// //         path: "/gatep/talent/passport",
// //       },
// //       {
// //         text: "Mock Interview",
// //         icon: <Psychology />,
// //         path: "/gatep/talent/exam-intro",
// //       },
// //       {
// //         text: "Report",
// //         icon: <ListAltTwoToneIcon />,
// //         path: "/gatep/talent/report",
// //       },
// //     ],
// //     employer: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //       {
// //         text: "Company Profile",
// //         icon: <PostAdd />,
// //         path: "/gatep/employer/company-profile",
// //       },
// //       {
// //         text: "Post Jobs",
// //         icon: <PostAdd />,
// //         path: "/gatep/employer/post-job",
// //       },
// //       {
// //         text: "Candidates",
// //         icon: <People />,
// //         path: "/gatep/employer/candidates",
// //       },
// //       {
// //         text: "Interviews",
// //         icon: <VideoCall />,
// //         path: "/gatep/employer/interviews",
// //       },
// //       {
// //         text: "Analytics",
// //         icon: <Analytics />,
// //         path: "/gatep/employer/analytics",
// //       },
// //     ],
// //     admin: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //       {
// //         text: "Analytics",
// //         icon: <Analytics />,
// //         path: "/gatep/admin/analytics",
// //       },
// //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //       {
// //         text: "Integrations",
// //         icon: <Settings />,
// //         path: "/gatep/admin/integrations",
// //       },
// //     ],
// //   };

// //   const currentMenuItems = menuItems[user?.role] || [];

// //   const handleNavigation = (path) => {
// //     navigate(path);
// //     if (isMobile) {
// //       onClose();
// //     }
// //   };

// //   const normalizedPathname = location.pathname.length > 1 ? location.pathname.replace(/\/$/, '') : location.pathname;

// //   return (
// //     <Drawer
// //       anchor="left"
// //       open={open}
// //       variant={variant}
// //       onClose={onClose}
// //       sx={{
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: open ? drawerWidth : drawerWidthCollapsed,
// //           boxSizing: "border-box",
// //           // THEME: Use a consistent variable for top margin and height calculation
// //           mt: !isMobile ? topSpacing : 0,
// //           ml: !isMobile ? 0 : 0,
// //           mb: !isMobile ? 0.1 : 0,
// //           height: !isMobile ? `calc(100% - ${topSpacing})` : "100%",
// //           // THEME: Use a border-radius consistent with other components in the theme (e.g., MuiCard)
// //           borderRadius: !isMobile ? "12px" : 0,
// //           border: "1px solid",
// //           // THEME: Use theme.palette.divider directly for consistency with theme overrides
// //           borderColor: theme.palette.divider,
// //           background:
// //             // This custom background is fine as it uses theme variables
// //             `linear-gradient(135deg, ${alpha(
// //               theme.palette.background.paper,
// //               0.9
// //             )}, ${alpha(theme.palette.background.default, 0.8)})`,
// //           boxShadow: !isMobile
// //             ? `0px 8px 20px ${alpha(
// //                 theme.palette.common.black,
// //                 0.1
// //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// //             : "none",
// //           transition: theme.transitions.create(
// //             ["width", "border-radius", "box-shadow", "background", "margin", "height"],
// //             {
// //               easing: theme.transitions.easing.easeInOut,
// //               duration: theme.transitions.duration.standard,
// //             }
// //           ),
// //         },
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //         }}
// //       >
// //         <List
// //           sx={{
// //             py: 1,
// //             flexGrow: 1,
// //             overflowY: "auto",
// //             mt: isMobile ? "54px" : 0, // This likely matches a mobile header height
// //           }}
// //         >
// //           {!isMobile && (
// //             <ListItem disablePadding sx={{ display: "block" }}>
// //               <ListItemButton
// //                 onClick={onToggle}
// //                 disableRipple
// //                 sx={{
// //                   mx: 1,
// //                   my: 0.5,
// //                   borderRadius: 2,
// //                   minHeight: 48,
// //                   justifyContent: open ? "initial" : "center",
// //                   px: 2.5,
// //                   backgroundColor: "transparent",
// //                   boxShadow: "none",
// //                   color: theme.palette.text.secondary,
// //                   "&:hover": {
// //                     backgroundColor: "transparent",
// //                   },
// //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// //                   "&:focus": { outline: "none", boxShadow: "none" },
// //                 }}
// //               >
// //                 <ListItemIcon
// //                   sx={{
// //                     minWidth: 0,
// //                     mr: open ? 2 : "auto",
// //                     justifyContent: "center",
// //                     color: "inherit",
// //                   }}
// //                 >
// //                   <MenuIcon />
// //                 </ListItemIcon>
// //                 <ListItemText
// //                   sx={{
// //                     opacity: open ? 1 : 0,
// //                     width: open ? "auto" : 0,
// //                     overflow: "hidden",
// //                     whiteSpace: "nowrap",
// //                     transition: theme.transitions.create(["opacity", "width"], {
// //                       easing: theme.transitions.easing.sharp,
// //                       duration: theme.transitions.duration.enteringScreen,
// //                     }),
// //                   }}
// //                 />
// //               </ListItemButton>
// //             </ListItem>
// //           )}
// //           {currentMenuItems.map((item) => (
// //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// //                 <ListItemButton
// //                   onClick={() => handleNavigation(item.path)}
// //                   selected={normalizedPathname === item.path}
// //                   sx={{
// //                     mx: 1,
// //                     my: 0.5,
// //                     borderRadius: 2,
// //                     minHeight: 48,
// //                     justifyContent: open ? "initial" : "center",
// //                     px: 2.5,
// //                     color: theme.palette.text.primary,
// //                     "& .MuiListItemIcon-root": {
// //                       color: theme.palette.text.secondary,
// //                     },
// //                     "&:hover": {
// //                       // THEME: Use theme palette for dynamic colors
// //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// //                         theme.palette.primary.main,
// //                         0.65
// //                       )}, ${alpha(theme.palette.secondary.main, 0.35)})`,
// //                       color: theme.palette.primary.contrastText,
// //                       transform: "scale(1.02)",
// //                       "& .MuiListItemIcon-root": {
// //                         color: theme.palette.primary.contrastText,
// //                       },
// //                     },
// //                     "&.Mui-selected": {
// //                       // THEME: Use theme palette for dynamic colors
// //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// //                         theme.palette.primary.main,
// //                         0.65
// //                       )}, ${alpha(theme.palette.secondary.main, 0.35)})`,
// //                       color: theme.palette.primary.contrastText,
// //                       fontWeight: "bold",
// //                       transform: "scale(1.01)",
// //                       "& .MuiListItemIcon-root": {
// //                         color: theme.palette.primary.contrastText,
// //                       },
// //                       "&:hover": {
// //                         backgroundImage: `linear-gradient(90deg, ${alpha(
// //                           theme.palette.primary.main,
// //                           0.65
// //                         )}, ${alpha(theme.palette.secondary.main, 0.35)})`,
// //                         filter: "brightness(1.15)",
// //                       },
// //                     },
// //                     transition: theme.transitions.create(
// //                       ["background-image", "color", "transform", "filter"],
// //                       {
// //                         duration: theme.transitions.duration.short,
// //                         easing: theme.transitions.easing.easeInOut,
// //                       }
// //                     ),
// //                   }}
// //                 >
// //                   <ListItemIcon
// //                     sx={{
// //                       minWidth: 0,
// //                       mr: open ? 2 : "auto",
// //                       justifyContent: "center",
// //                       transition: theme.transitions.create("color", {
// //                         duration: theme.transitions.duration.short,
// //                       }),
// //                     }}
// //                   >
// //                     {item.icon}
// //                   </ListItemIcon>
// //                   <ListItemText
// //                     primary={item.text}
// //                     sx={{
// //                       opacity: open ? 1 : 0,
// //                       width: open ? "auto" : 0,
// //                       overflow: "hidden",
// //                       whiteSpace: "nowrap",
// //                       transition: theme.transitions.create(
// //                         ["opacity", "width"],
// //                         {
// //                           easing: theme.transitions.easing.sharp,
// //                           duration: theme.transitions.duration.enteringScreen,
// //                         }
// //                       ),
// //                     }}
// //                   />
// //                 </ListItemButton>
// //               </Tooltip>
// //             </ListItem>
// //           ))}
// //         </List>
// //         <Box
// //           sx={{
// //             py: open ? 2 : 0,
// //             px: 2,
// //             mt: "auto",
// //             borderTop: "1px solid",
// //             // THEME: Use theme.palette.divider directly for consistency
// //             borderColor: open ? theme.palette.divider : "transparent",
// //             opacity: open ? 1 : 0,
// //             maxHeight: open ? 100 : 0,
// //             overflow: "hidden",
// //             transition: theme.transitions.create(
// //               ["opacity", "max-height", "padding", "border-color"],
// //               {
// //                 easing: theme.transitions.easing.sharp,
// //                 duration: theme.transitions.duration.enteringScreen,
// //               }
// //             ),
// //           }}
// //         >
// //           <Typography
// //             variant="caption"
// //             color="textSecondary"
// //             align="center"
// //             display="block"
// //           >
// //             © 2025 The Data Tech Labs
// //           </Typography>
// //         </Box>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;

// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   Typography,
// //   useTheme,
// //   useMediaQuery,
// //   alpha,
// //   Tooltip,
// // } from "@mui/material";
// // import {
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 256;
// // const drawerWidthCollapsed = 64;

// // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// //   const { user } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// //   // --- New Color Palette for the Gradient Theme ---
// //   const accentColor = "#E600FF"; // The vibrant magenta/purple from your request
// //   const gradientStartColor = "#0c0c2c"; // A deep, professional navy blue for the base

// //   const menuItems = {
// //     talent: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //       { text: "Profile Builder", icon: <Person />, path: "/gatep/talent/profile" },
// //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //       { text: "AI Assistant", icon: <Psychology />, path: "/gatep/talent/assistant" },
// //       { text: "Skills Passport", icon: <Badge />, path: "/gatep/talent/passport" },
// //       { text: "Mock Interview", icon: <Psychology />, path: "/gatep/talent/exam-intro" },
// //       { text: "Report", icon: <ListAltTwoToneIcon />, path: "/gatep/talent/report" },
// //     ],
// //     employer: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //       { text: "Company Profile", icon: <PostAdd />, path: "/gatep/employer/company-profile" },
// //       { text: "Post Jobs", icon: <PostAdd />, path: "/gatep/employer/post-job" },
// //       { text: "Candidates", icon: <People />, path: "/gatep/employer/candidates" },
// //       { text: "Interviews", icon: <VideoCall />, path: "/gatep/employer/interviews" },
// //       { text: "Analytics", icon: <Analytics />, path: "/gatep/employer/analytics" },
// //     ],
// //     admin: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //       { text: "Analytics", icon: <Analytics />, path: "/gatep/admin/analytics" },
// //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //       { text: "Integrations", icon: <Settings />, path: "/gatep/admin/integrations" },
// //     ],
// //   };

// //   const currentMenuItems = menuItems[user?.role] || [];

// //   const handleNavigation = (path) => {
// //     navigate(path);
// //     if (isMobile) {
// //       onClose();
// //     }
// //   };

// //   const normalizedPathname = location.pathname.length > 1 ? location.pathname.replace(/\/$/, '') : location.pathname;

// //   return (
// //     <Drawer
// //       anchor="left"
// //       open={open}
// //       variant={variant}
// //       onClose={onClose}
// //       sx={{
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: open ? drawerWidth : drawerWidthCollapsed,
// //           boxSizing: "border-box",
// //           mt: !isMobile ? 9 : 0,
// //           ml: !isMobile ? 0 : 0,
// //           mb: !isMobile ? 0.10 : 0,
// //           height: !isMobile ? 'calc(100% - 80px)' : '100%',
// //           borderRadius: !isMobile ? 4 : 0,
// //           border: "1px solid",
// //           // --- Style Updates ---
// //           borderColor: alpha(accentColor, 0.3),
// //           color: theme.palette.common.white, // Default text color is now white
// //           background: `linear-gradient(180deg, ${gradientStartColor} 0%, ${accentColor} 120%)`,
// //           boxShadow: !isMobile
// //             ? `0px 10px 30px -5px ${alpha(accentColor, 0.3)}`
// //             : "none",
// //           transition: theme.transitions.create(
// //             ["width", "border-radius", "box-shadow", "background", "margin", "height"],
// //             {
// //               easing: theme.transitions.easing.easeInOut,
// //               duration: theme.transitions.duration.standard,
// //             }
// //           ),
// //         },
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //         }}
// //       >
// //         <List
// //           sx={{
// //             py: 1,
// //             flexGrow: 1,
// //             overflowY: "auto",
// //             mt: isMobile ? "54px" : 0,
// //           }}
// //         >
// //           {!isMobile && (
// //             <ListItem disablePadding sx={{ display: "block" }}>
// //               <ListItemButton
// //                 onClick={onToggle}
// //                 disableRipple
// //                 sx={{
// //                   mx: 1,
// //                   my: 0.5,
// //                   borderRadius: 2,
// //                   minHeight: 48,
// //                   justifyContent: open ? "initial" : "center",
// //                   px: 2.5,
// //                   // --- Style Updates ---
// //                   color: alpha(theme.palette.common.white, 0.7),
// //                   "&:hover": {
// //                     backgroundColor: alpha(theme.palette.common.white, 0.1),
// //                     color: theme.palette.common.white,
// //                   },
// //                 }}
// //               >
// //                 <ListItemIcon
// //                   sx={{
// //                     minWidth: 0,
// //                     mr: open ? 2 : "auto",
// //                     justifyContent: "center",
// //                     color: "inherit",
// //                   }}
// //                 >
// //                   <MenuIcon />
// //                 </ListItemIcon>
// //               </ListItemButton>
// //             </ListItem>
// //           )}
// //           {currentMenuItems.map((item) => (
// //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// //                 <ListItemButton
// //                   onClick={() => handleNavigation(item.path)}
// //                   selected={normalizedPathname === item.path}
// //                   sx={{
// //                     mx: 1,
// //                     my: 0.5,
// //                     borderRadius: 2,
// //                     minHeight: 48,
// //                     justifyContent: open ? "initial" : "center",
// //                     px: 2.5,
// //                     // --- Style Updates for List Items ---
// //                     color: alpha(theme.palette.common.white, 0.7),
// //                     "& .MuiListItemIcon-root": {
// //                       color: "inherit",
// //                     },
// //                     "&:hover": {
// //                       backgroundColor: alpha(theme.palette.common.white, 0.1),
// //                       color: theme.palette.common.white,
// //                       transform: "scale(1.02)",
// //                     },
// //                     "&.Mui-selected": {
// //                       backgroundColor: alpha(accentColor, 0.25),
// //                       color: theme.palette.common.white,
// //                       fontWeight: "bold",
// //                       boxShadow: `0 0 12px ${alpha(accentColor, 0.5)}, inset 0 0 8px ${alpha(theme.palette.common.white, 0.1)}`,
// //                       "&:hover": {
// //                         backgroundColor: alpha(accentColor, 0.35),
// //                         filter: "brightness(1.1)",
// //                       },
// //                     },
// //                     transition: theme.transitions.create(
// //                       ["background-color", "color", "transform", "filter", "box-shadow"],
// //                       {
// //                         duration: theme.transitions.duration.short,
// //                         easing: theme.transitions.easing.easeInOut,
// //                       }
// //                     ),
// //                   }}
// //                 >
// //                   <ListItemIcon
// //                     sx={{
// //                       minWidth: 0,
// //                       mr: open ? 2 : "auto",
// //                       justifyContent: "center",
// //                       color: "inherit", // Icon color will follow ListItemButton color
// //                       transition: theme.transitions.create("color", {
// //                         duration: theme.transitions.duration.short,
// //                       }),
// //                     }}
// //                   >
// //                     {item.icon}
// //                   </ListItemIcon>
// //                   <ListItemText
// //                     primary={item.text}
// //                     sx={{
// //                       opacity: open ? 1 : 0,
// //                       width: open ? "auto" : 0,
// //                       overflow: "hidden",
// //                       whiteSpace: "nowrap",
// //                       transition: theme.transitions.create(
// //                         ["opacity", "width"],
// //                         {
// //                           easing: theme.transitions.easing.sharp,
// //                           duration: theme.transitions.duration.enteringScreen,
// //                         }
// //                       ),
// //                     }}
// //                   />
// //                 </ListItemButton>
// //               </Tooltip>
// //             </ListItem>
// //           ))}
// //         </List>
// //         <Box
// //           sx={{
// //             py: open ? 2 : 0,
// //             px: 2,
// //             mt: "auto",
// //             // --- Style Updates for Footer ---
// //             borderTop: "1px solid",
// //             borderColor: open ? alpha(theme.palette.common.white, 0.2) : "transparent",
// //             opacity: open ? 1 : 0,
// //             maxHeight: open ? 100 : 0,
// //             overflow: "hidden",
// //             transition: theme.transitions.create(
// //               ["opacity", "max-height", "padding", "border-color"],
// //               {
// //                 easing: theme.transitions.easing.sharp,
// //                 duration: theme.transitions.duration.enteringScreen,
// //               }
// //             ),
// //           }}
// //         >
// //           <Typography
// //             variant="caption"
// //             sx={{ color: alpha(theme.palette.common.white, 0.5) }}
// //             align="center"
// //             display="block"
// //           >
// //             © 2025 The Data Tech Labs
// //           </Typography>
// //         </Box>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;

// // import {
// //   Drawer,
// //   List,
// //   ListItem,
// //   ListItemIcon,
// //   ListItemText,
// //   ListItemButton,
// //   Box,
// //   Typography,
// //   useTheme,
// //   useMediaQuery,
// //   alpha,
// //   Tooltip,
// // } from "@mui/material";
// // import {
// //   Dashboard,
// //   Person,
// //   Work,
// //   Psychology,
// //   Badge,
// //   PostAdd,
// //   People,
// //   VideoCall,
// //   Analytics,
// //   Map,
// //   Settings,
// //   Menu as MenuIcon,
// // } from "@mui/icons-material";
// // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // import { useNavigate, useLocation } from "react-router-dom";
// // import { useAuth } from "../../context/AuthContext";

// // const drawerWidth = 256;
// // const drawerWidthCollapsed = 64;

// // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// //   const { user } = useAuth();
// //   const navigate = useNavigate();
// //   const location = useLocation();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// //   const accentColor2 = "#df30b6"; // The pink/purple-ish color
// //   const accentColor1 = "#100eb8"; // The vibrant blue color

// //   const menuItems = {
// //     talent: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// //       {
// //         text: "Profile Builder",
// //         icon: <Person />,
// //         path: "/gatep/talent/profile",
// //       },
// //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// //       {
// //         text: "AI Assistant",
// //         icon: <Psychology />,
// //         path: "/gatep/talent/assistant",
// //       },
// //       {
// //         text: "Skills Passport",
// //         icon: <Badge />,
// //         path: "/gatep/talent/passport",
// //       },
// //       {
// //         text: "Mock Interview",
// //         icon: <Psychology />,
// //         path: "/gatep/talent/exam-intro",
// //       },
// //       {
// //         text: "Report",
// //         icon: <ListAltTwoToneIcon />,
// //         path: "/gatep/talent/report",
// //       },
// //     ],
// //     employer: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// //       {
// //         text: "Company Profile",
// //         icon: <PostAdd />,
// //         path: "/gatep/employer/company-profile",
// //       },
// //       {
// //         text: "Post Jobs",
// //         icon: <PostAdd />,
// //         path: "/gatep/employer/post-job",
// //       },
// //       {
// //         text: "Candidates",
// //         icon: <People />,
// //         path: "/gatep/employer/candidates",
// //       },
// //       {
// //         text: "Interviews",
// //         icon: <VideoCall />,
// //         path: "/gatep/employer/interviews",
// //       },
// //       {
// //         text: "Analytics",
// //         icon: <Analytics />,
// //         path: "/gatep/employer/analytics",
// //       },
// //     ],
// //     admin: [
// //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// //       {
// //         text: "Analytics",
// //         icon: <Analytics />,
// //         path: "/gatep/admin/analytics",
// //       },
// //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// //       {
// //         text: "Integrations",
// //         icon: <Settings />,
// //         path: "/gatep/admin/integrations",
// //       },
// //     ],
// //   };

// //   const currentMenuItems = menuItems[user?.role] || [];

// //   const handleNavigation = (path) => {
// //     navigate(path);
// //     if (isMobile) {
// //       onClose();
// //     }
// //   };

// //   const normalizedPathname = location.pathname.length > 1 ? location.pathname.replace(/\/$/, '') : location.pathname;

// //   return (
// //     <Drawer
// //       anchor="left"
// //       open={open}
// //       variant={variant}
// //       onClose={onClose}
// //       sx={{
// //         flexShrink: 0,
// //         "& .MuiDrawer-paper": {
// //           width: open ? drawerWidth : drawerWidthCollapsed,
// //           boxSizing: "border-box",
// //           mt: 0,
// //           ml: 0,
// //           mb: 0,
// //           height: "100%",
// //           borderRadius: !isMobile ? 4 : 0,
// //           border: "1px solid",
// //           borderColor: alpha(theme.palette.divider, 0.3),
// //           background:
// //             theme.palette.mode === "light"
// //               ? `linear-gradient(135deg, ${alpha(
// //                   theme.palette.background.paper,
// //                   0.9
// //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// //               : `linear-gradient(135deg, ${alpha(
// //                   theme.palette.background.paper,
// //                   0.9
// //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,
// //           boxShadow: !isMobile
// //             ? `0px 8px 20px ${alpha(
// //                 theme.palette.common.black,
// //                 0.1
// //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// //             : "none",
// //           transition: theme.transitions.create(
// //             ["width", "border-radius", "box-shadow", "background"],
// //             {
// //               easing: theme.transitions.easing.easeInOut,
// //               duration: theme.transitions.duration.standard,
// //             }
// //           ),
// //         },
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //         }}
// //       >
// //         <List
// //           sx={{
// //             py: 1,
// //             flexGrow: 1,
// //             overflowY: "auto",
// //             mt: isMobile ? "54px" : 0,
// //           }}
// //         >
// //           {!isMobile && (
// //             <ListItem disablePadding sx={{ display: "block" }}>
// //               <ListItemButton
// //                 onClick={onToggle}
// //                 disableRipple
// //                 sx={{
// //                   mx: 1,
// //                   my: 0.5,
// //                   borderRadius: 2,
// //                   minHeight: 48,
// //                   justifyContent: open ? "initial" : "center",
// //                   px: 2.5,
// //                   backgroundColor: "transparent",
// //                   boxShadow: "none",
// //                   color: theme.palette.text.secondary,
// //                   "&:hover": {
// //                     backgroundColor: "transparent",
// //                   },
// //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// //                   "&:focus": { outline: "none", boxShadow: "none" },
// //                 }}
// //               >
// //                 <ListItemIcon
// //                   sx={{
// //                     minWidth: 0,
// //                     mr: open ? 2 : "auto",
// //                     justifyContent: "center",
// //                     color: "inherit",
// //                   }}
// //                 >
// //                   <MenuIcon />
// //                 </ListItemIcon>
// //                 <ListItemText
// //                   sx={{
// //                     opacity: open ? 1 : 0,
// //                     width: open ? "auto" : 0,
// //                     overflow: "hidden",
// //                     whiteSpace: "nowrap",
// //                     transition: theme.transitions.create(["opacity", "width"], {
// //                       easing: theme.transitions.easing.sharp,
// //                       duration: theme.transitions.duration.enteringScreen,
// //                     }),
// //                   }}
// //                 />
// //               </ListItemButton>
// //             </ListItem>
// //           )}
// //           {currentMenuItems.map((item) => (
// //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// //                 <ListItemButton
// //                   onClick={() => handleNavigation(item.path)}
// //                   selected={normalizedPathname === item.path}
// //                   sx={{
// //                     mx: 1,
// //                     my: 0.5,
// //                     borderRadius: 2,
// //                     minHeight: 48,
// //                     justifyContent: open ? "initial" : "center",
// //                     px: 2.5,
// //                     color: theme.palette.text.primary,
// //                     "& .MuiListItemIcon-root": {
// //                       color: theme.palette.text.secondary,
// //                     },
// //                     "&:hover": {
// //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// //                         accentColor1,
// //                         0.55
// //                       )}, ${alpha(accentColor2, 0.35)})`,
// //                       color: theme.palette.primary.contrastText,
// //                       transform: "scale(1.02)",
// //                       "& .MuiListItemIcon-root": {
// //                         color: theme.palette.primary.contrastText,
// //                       },
// //                     },
// //                     "&.Mui-selected": {
// //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// //                         accentColor1,
// //                         0.55
// //                       )}, ${alpha(accentColor2, 0.35)})`,
// //                       color: theme.palette.primary.contrastText,
// //                       fontWeight: "bold",
// //                       transform: "scale(1.01)",
// //                       "& .MuiListItemIcon-root": {
// //                         color: theme.palette.primary.contrastText,
// //                       },
// //                       "&:hover": {
// //                         backgroundImage: `linear-gradient(90deg, ${alpha(
// //                           accentColor1,
// //                           0.55
// //                         )}, ${alpha(accentColor2, 0.35)})`,
// //                         filter: "brightness(1.15)",
// //                       },
// //                     },
// //                     transition: theme.transitions.create(
// //                       ["background-image", "color", "transform", "filter"],
// //                       {
// //                         duration: theme.transitions.duration.short,
// //                         easing: theme.transitions.easing.easeInOut,
// //                       }
// //                     ),
// //                   }}
// //                 >
// //                   <ListItemIcon
// //                     sx={{
// //                       minWidth: 0,
// //                       mr: open ? 2 : "auto",
// //                       justifyContent: "center",
// //                       transition: theme.transitions.create("color", {
// //                         duration: theme.transitions.duration.short,
// //                       }),
// //                     }}
// //                   >
// //                     {item.icon}
// //                   </ListItemIcon>
// //                   <ListItemText
// //                     primary={item.text}
// //                     sx={{
// //                       opacity: open ? 1 : 0,
// //                       width: open ? "auto" : 0,
// //                       overflow: "hidden",
// //                       whiteSpace: "nowrap",
// //                       transition: theme.transitions.create(
// //                         ["opacity", "width"],
// //                         {
// //                           easing: theme.transitions.easing.sharp,
// //                           duration: theme.transitions.duration.enteringScreen,
// //                         }
// //                       ),
// //                     }}
// //                   />
// //                 </ListItemButton>
// //               </Tooltip>
// //             </ListItem>
// //           ))}
// //         </List>
// //         <Box
// //           sx={{
// //             py: open ? 2 : 0,
// //             px: 2,
// //             mt: "auto",
// //             borderTop: "1px solid",
// //             borderColor: open
// //               ? alpha(theme.palette.divider, 0.3)
// //               : "transparent",
// //             opacity: open ? 1 : 0,
// //             maxHeight: open ? 100 : 0,
// //             overflow: "hidden",
// //             transition: theme.transitions.create(
// //               ["opacity", "max-height", "padding", "border-color"],
// //               {
// //                 easing: theme.transitions.easing.sharp,
// //                 duration: theme.transitions.duration.enteringScreen,
// //               }
// //             ),
// //           }}
// //         >
// //           <Typography
// //             variant="caption"
// //             color="textSecondary"
// //             align="center"
// //             display="block"
// //           >
// //             © 2025 The Data Tech Labs
// //           </Typography>
// //         </Box>
// //       </Box>
// //     </Drawer>
// //   );
// // };

// // export default Sidebar;

// // // import {
// // //   Drawer,
// // //   List,
// // //   ListItem,
// // //   ListItemIcon,
// // //   ListItemText,
// // //   ListItemButton,
// // //   Box,
// // //   Typography,
// // //   useTheme,
// // //   useMediaQuery,
// // //   alpha,
// // //   Tooltip,
// // // } from "@mui/material";
// // // import {
// // //   Dashboard,
// // //   Person,
// // //   Work,
// // //   Psychology,
// // //   Badge,
// // //   PostAdd,
// // //   People,
// // //   VideoCall,
// // //   Analytics,
// // //   Map,
// // //   Settings,
// // //   Menu as MenuIcon,
// // // } from "@mui/icons-material";
// // // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // // import { useNavigate, useLocation } from "react-router-dom";
// // // import { useAuth } from "../../context/AuthContext";

// // // const drawerWidth = 256;
// // // const drawerWidthCollapsed = 64;

// // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // //   const { user } = useAuth();
// // //   const navigate = useNavigate();
// // //   const location = useLocation();
// // //   const theme = useTheme();
// // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // //   const accentColor2 = "#df30b6"; // The pink/purple-ish color
// // //   const accentColor1 = "#100eb8"; // The vibrant blue color

// // //   const menuItems = {
// // //     talent: [
// // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // //       {
// // //         text: "Profile Builder",
// // //         icon: <Person />,
// // //         path: "/gatep/talent/profile",
// // //       },
// // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // //       {
// // //         text: "AI Assistant",
// // //         icon: <Psychology />,
// // //         path: "/gatep/talent/assistant",
// // //       },
// // //       {
// // //         text: "Skills Passport",
// // //         icon: <Badge />,
// // //         path: "/gatep/talent/passport",
// // //       },
// // //       {
// // //         text: "Mock Interview",
// // //         icon: <Psychology />,
// // //         path: "/gatep/talent/exam-intro",
// // //       },
// // //       {
// // //         text: "Report",
// // //         icon: <ListAltTwoToneIcon />,
// // //         path: "/gatep/talent/report",
// // //       },
// // //     ],
// // //     employer: [
// // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // //       {
// // //         text: "Company Profile",
// // //         icon: <PostAdd />,
// // //         path: "/gatep/employer/company-profile",
// // //       },
// // //       {
// // //         text: "Post Jobs",
// // //         icon: <PostAdd />,
// // //         path: "/gatep/employer/post-job",
// // //       },
// // //       {
// // //         text: "Candidates",
// // //         icon: <People />,
// // //         path: "/gatep/employer/candidates",
// // //       },
// // //       {
// // //         text: "Interviews",
// // //         icon: <VideoCall />,
// // //         path: "/gatep/employer/interviews",
// // //       },
// // //       {
// // //         text: "Analytics",
// // //         icon: <Analytics />,
// // //         path: "/gatep/employer/analytics",
// // //       },
// // //     ],
// // //     admin: [
// // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // //       {
// // //         text: "Analytics",
// // //         icon: <Analytics />,
// // //         path: "/gatep/admin/analytics",
// // //       },
// // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // //       {
// // //         text: "Integrations",
// // //         icon: <Settings />,
// // //         path: "/gatep/admin/integrations",
// // //       },
// // //     ],
// // //   };

// // //   const currentMenuItems = menuItems[user?.role] || [];

// // //   const handleNavigation = (path) => {
// // //     navigate(path);
// // //     if (isMobile) {
// // //       onClose();
// // //     }
// // //   };

// // //   // Normalize the path to remove trailing slashes for accurate comparison
// // //   const normalizedPathname = location.pathname.length > 1 ? location.pathname.replace(/\/$/, '') : location.pathname;

// // //   return (
// // //     <Drawer
// // //       anchor="left"
// // //       open={open}
// // //       variant={variant}
// // //       onClose={onClose}
// // //       sx={{
// // //         flexShrink: 0,
// // //         "& .MuiDrawer-paper": {
// // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // //           boxSizing: "border-box",
// // //           mt: !isMobile ? "80px" : 0,
// // //           ml: !isMobile ? 0 : 0,
// // //           mb: !isMobile ? 2 : 0,
// // //           height: !isMobile ? "calc(100% - 96px)" : "100%",
// // //           borderRadius: !isMobile ? 4 : 0,
// // //           border: "1px solid",
// // //           borderColor: alpha(theme.palette.divider, 0.3),
// // //           background:
// // //             theme.palette.mode === "light"
// // //               ? `linear-gradient(135deg, ${alpha(
// // //                   theme.palette.background.paper,
// // //                   0.9
// // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// // //               : `linear-gradient(135deg, ${alpha(
// // //                   theme.palette.background.paper,
// // //                   0.9
// // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,
// // //           boxShadow: !isMobile
// // //             ? `0px 8px 20px ${alpha(
// // //                 theme.palette.common.black,
// // //                 0.1
// // //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// // //             : "none",
// // //           transition: theme.transitions.create(
// // //             ["width", "border-radius", "box-shadow", "background"],
// // //             {
// // //               easing: theme.transitions.easing.easeInOut,
// // //               duration: theme.transitions.duration.standard,
// // //             }
// // //           ),
// // //         },
// // //       }}
// // //     >
// // //       <Box
// // //         sx={{
// // //           height: "100%",
// // //           display: "flex",
// // //           flexDirection: "column",
// // //         }}
// // //       >
// // //         <List
// // //           sx={{
// // //             py: 1,
// // //             flexGrow: 1,
// // //             overflowY: "auto",
// // //             mt: isMobile ? "54px" : 0,
// // //           }}
// // //         >
// // //           {!isMobile && (
// // //             <ListItem disablePadding sx={{ display: "block" }}>
// // //               <ListItemButton
// // //                 onClick={onToggle}
// // //                 disableRipple
// // //                 sx={{
// // //                   mx: 1,
// // //                   borderRadius: 2,
// // //                   minHeight: 48,
// // //                   justifyContent: open ? "initial" : "center",
// // //                   px: 2.5,
// // //                   backgroundColor: "transparent",
// // //                   boxShadow: "none",
// // //                   color: theme.palette.text.secondary,
// // //                   "&:hover": {
// // //                     backgroundColor: "transparent",
// // //                   },
// // //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// // //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// // //                   "&:focus": { outline: "none", boxShadow: "none" },
// // //                 }}
// // //               >
// // //                 <ListItemIcon
// // //                   sx={{
// // //                     minWidth: 0,
// // //                     mr: open ? 2 : "auto",
// // //                     justifyContent: "center",
// // //                     color: "inherit",
// // //                   }}
// // //                 >
// // //                   <MenuIcon />
// // //                 </ListItemIcon>
// // //                 <ListItemText
// // //                   sx={{
// // //                     opacity: open ? 1 : 0,
// // //                     width: open ? "auto" : 0,
// // //                     overflow: "hidden",
// // //                     whiteSpace: "nowrap",
// // //                     transition: theme.transitions.create(["opacity", "width"], {
// // //                       easing: theme.transitions.easing.sharp,
// // //                       duration: theme.transitions.duration.enteringScreen,
// // //                     }),
// // //                   }}
// // //                 />
// // //               </ListItemButton>
// // //             </ListItem>
// // //           )}
// // //           {currentMenuItems.map((item) => (
// // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// // //                 <ListItemButton
// // //                   onClick={() => handleNavigation(item.path)}
// // //                   selected={normalizedPathname === item.path}
// // //                   sx={{
// // //                     mx: 1,
// // //                     borderRadius: 2,
// // //                     minHeight: 48,
// // //                     justifyContent: open ? "initial" : "center",
// // //                     px: 2.5,
// // //                     color: theme.palette.text.primary,
// // //                     "& .MuiListItemIcon-root": {
// // //                       color: theme.palette.text.secondary,
// // //                     },
// // //                     "&:hover": {
// // //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// // //                         accentColor1,
// // //                         0.45
// // //                       )}, ${alpha(accentColor2, 0.35)})`,
// // //                       color: theme.palette.primary.contrastText,
// // //                       transform: "scale(1.02)",
// // //                       "& .MuiListItemIcon-root": {
// // //                         color: theme.palette.primary.contrastText,
// // //                       },
// // //                     },
// // //                     "&.Mui-selected": {
// // //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// // //                         accentColor1,
// // //                         0.45
// // //                       )}, ${alpha(accentColor2, 0.35)})`,
// // //                       color: theme.palette.primary.contrastText,
// // //                       fontWeight: "bold",
// // //                       transform: "scale(1.01)",
// // //                       "& .MuiListItemIcon-root": {
// // //                         color: theme.palette.primary.contrastText,
// // //                       },
// // //                       "&:hover": {
// // //                         backgroundImage: `linear-gradient(90deg, ${alpha(
// // //                           accentColor1,
// // //                           0.45
// // //                         )}, ${alpha(accentColor2, 0.35)})`,
// // //                         filter: "brightness(1.15)",
// // //                       },
// // //                     },
// // //                     transition: theme.transitions.create(
// // //                       ["background-image", "color", "transform", "filter"],
// // //                       {
// // //                         duration: theme.transitions.duration.short,
// // //                         easing: theme.transitions.easing.easeInOut,
// // //                       }
// // //                     ),
// // //                   }}
// // //                 >
// // //                   <ListItemIcon
// // //                     sx={{
// // //                       minWidth: 0,
// // //                       mr: open ? 2 : "auto",
// // //                       justifyContent: "center",
// // //                       transition: theme.transitions.create("color", {
// // //                         duration: theme.transitions.duration.short,
// // //                       }),
// // //                     }}
// // //                   >
// // //                     {item.icon}
// // //                   </ListItemIcon>
// // //                   <ListItemText
// // //                     primary={item.text}
// // //                     sx={{
// // //                       opacity: open ? 1 : 0,
// // //                       width: open ? "auto" : 0,
// // //                       overflow: "hidden",
// // //                       whiteSpace: "nowrap",
// // //                       transition: theme.transitions.create(
// // //                         ["opacity", "width"],
// // //                         {
// // //                           easing: theme.transitions.easing.sharp,
// // //                           duration: theme.transitions.duration.enteringScreen,
// // //                         }
// // //                       ),
// // //                     }}
// // //                   />
// // //                 </ListItemButton>
// // //               </Tooltip>
// // //             </ListItem>
// // //           ))}
// // //         </List>
// // //         <Box
// // //           sx={{
// // //             py: open ? 2 : 0,
// // //             px: 2,
// // //             mt: "auto",
// // //             borderTop: "1px solid",
// // //             borderColor: open
// // //               ? alpha(theme.palette.divider, 0.3)
// // //               : "transparent",
// // //             opacity: open ? 1 : 0,
// // //             maxHeight: open ? 100 : 0,
// // //             overflow: "hidden",
// // //             transition: theme.transitions.create(
// // //               ["opacity", "max-height", "padding", "border-color"],
// // //               {
// // //                 easing: theme.transitions.easing.sharp,
// // //                 duration: theme.transitions.duration.enteringScreen,
// // //               }
// // //             ),
// // //           }}
// // //         >
// // //           <Typography
// // //             variant="caption"
// // //             color="textSecondary"
// // //             align="center"
// // //             display="block"
// // //           >
// // //             © 2025 The Data Tech Labs
// // //           </Typography>
// // //         </Box>
// // //       </Box>
// // //     </Drawer>
// // //   );
// // // };

// // // export default Sidebar;

// // // // import {
// // // //   Drawer,
// // // //   List,
// // // //   ListItem,
// // // //   ListItemIcon,
// // // //   ListItemText,
// // // //   ListItemButton,
// // // //   Box,
// // // //   Typography,
// // // //   useTheme,
// // // //   useMediaQuery,
// // // //   alpha,
// // // //   Tooltip,
// // // // } from "@mui/material";
// // // // import {
// // // //   Dashboard,
// // // //   Person,
// // // //   Work,
// // // //   Psychology,
// // // //   Badge,
// // // //   PostAdd,
// // // //   People,
// // // //   VideoCall,
// // // //   Analytics,
// // // //   Map,
// // // //   Settings,
// // // //   Menu as MenuIcon,
// // // // } from "@mui/icons-material";
// // // // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // // // import { useNavigate, useLocation } from "react-router-dom";
// // // // import { useAuth } from "../../context/AuthContext";

// // // // const drawerWidth = 256;
// // // // const drawerWidthCollapsed = 64;

// // // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // // //   const { user } = useAuth();
// // // //   const navigate = useNavigate();
// // // //   const location = useLocation();
// // // //   const theme = useTheme();
// // // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // // //   const accentColor2 = "#df30b6"; // The pink/purple-ish color
// // // //   const accentColor1 = "#100eb8"; // The vibrant blue color

// // // //   const menuItems = {
// // // //     talent: [
// // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // // //       {
// // // //         text: "Profile Builder",
// // // //         icon: <Person />,
// // // //         path: "/gatep/talent/profile",
// // // //       },
// // // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // // //       {
// // // //         text: "AI Assistant",
// // // //         icon: <Psychology />,
// // // //         path: "/gatep/talent/assistant",
// // // //       },
// // // //       {
// // // //         text: "Skills Passport",
// // // //         icon: <Badge />,
// // // //         path: "/gatep/talent/passport",
// // // //       },
// // // //       {
// // // //         text: "Mock Interview",
// // // //         icon: <Psychology />,
// // // //         path: "/gatep/talent/exam-intro",
// // // //       },
// // // //       {
// // // //         text: "Report",
// // // //         icon: <ListAltTwoToneIcon />,
// // // //         path: "/gatep/talent/Report",
// // // //       },
// // // //     ],
// // // //     employer: [
// // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // // //       {
// // // //         text: "Company Profile",
// // // //         icon: <PostAdd />,
// // // //         path: "/gatep/employer/company-profile",
// // // //       },
// // // //       {
// // // //         text: "Post Jobs",
// // // //         icon: <PostAdd />,
// // // //         path: "/gatep/employer/post-job",
// // // //       },
// // // //       {
// // // //         text: "Candidates",
// // // //         icon: <People />,
// // // //         path: "/gatep/employer/candidates",
// // // //       },
// // // //       {
// // // //         text: "Interviews",
// // // //         icon: <VideoCall />,
// // // //         path: "/gatep/employer/interviews",
// // // //       },
// // // //       {
// // // //         text: "Analytics",
// // // //         icon: <Analytics />,
// // // //         path: "/gatep/employer/analytics",
// // // //       },
// // // //     ],
// // // //     admin: [
// // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // // //       {
// // // //         text: "Analytics",
// // // //         icon: <Analytics />,
// // // //         path: "/gatep/admin/analytics",
// // // //       },
// // // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // // //       // {
// // // //       //   text: "Integrations",
// // // //       //   icon: <Settings />,
// // // //       //   path: "/gatep/admin/integrations",
// // // //       // },
// // // //     ],
// // // //   };

// // // //   const currentMenuItems = menuItems[user?.role] || [];

// // // //   const handleNavigation = (path) => {
// // // //     navigate(path);
// // // //     if (isMobile) {
// // // //       onClose();
// // // //     }
// // // //   };

// // // //   return (
// // // //     <Drawer
// // // //       anchor="left"
// // // //       open={open}
// // // //       variant={variant}
// // // //       onClose={onClose}
// // // //       sx={{
// // // //         flexShrink: 0,
// // // //         "& .MuiDrawer-paper": {
// // // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // // //           boxSizing: "border-box",
// // // //           mt: !isMobile ? "80px" : 0,
// // // //           ml: !isMobile ? 0 : 0,
// // // //           mb: !isMobile ? 1 : 0,

// // // //          height: !isMobile ? "calc(100% - 96px)" : "100%",
// // // //           borderRadius: !isMobile ? 4 : 0,
// // // //           border: "1px solid",
// // // //           borderColor: alpha(theme.palette.divider, 0.3),
// // // //           background:
// // // //             theme.palette.mode === "light"
// // // //               ? `linear-gradient(135deg, ${alpha(
// // // //                   theme.palette.background.paper,
// // // //                   0.9
// // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// // // //               : `linear-gradient(135deg, ${alpha(
// // // //                   theme.palette.background.paper,
// // // //                   0.9
// // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,
// // // //           boxShadow: !isMobile
// // // //             ? `0px 8px 20px ${alpha(
// // // //                 theme.palette.common.black,
// // // //                 0.1
// // // //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// // // //             : "none",
// // // //           transition: theme.transitions.create(
// // // //             ["width", "border-radius", "box-shadow", "background"],
// // // //             {
// // // //               easing: theme.transitions.easing.easeInOut,
// // // //               duration: theme.transitions.duration.standard,
// // // //             }
// // // //           ),
// // // //         },
// // // //       }}
// // // //     >
// // // //       <Box
// // // //         sx={{
// // // //           height: "100%",
// // // //           display: "flex",
// // // //           flexDirection: "column",
// // // //         }}
// // // //       >
// // // //         <List
// // // //           sx={{
// // // //             py: 1,
// // // //             flexGrow: 1,
// // // //             overflowY: "auto",
// // // //             mt: isMobile ? "54px" : 0,
// // // //           }}
// // // //         >
// // // //           {!isMobile && (
// // // //             <ListItem disablePadding sx={{ display: "block" }}>
// // // //               <ListItemButton
// // // //                 onClick={onToggle}
// // // //                 disableRipple
// // // //                 sx={{
// // // //                   mx: 1,
// // // //                   borderRadius: 2,
// // // //                   minHeight: 48,
// // // //                   justifyContent: open ? "initial" : "center",
// // // //                   px: 2.5,
// // // //                   backgroundColor: "transparent",
// // // //                   boxShadow: "none",
// // // //                   color: theme.palette.text.secondary,
// // // //                   "&:hover": {
// // // //                     backgroundColor: "transparent",
// // // //                   },
// // // //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// // // //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// // // //                   "&:focus": { outline: "none", boxShadow: "none" },
// // // //                 }}
// // // //               >
// // // //                 <ListItemIcon
// // // //                   sx={{
// // // //                     minWidth: 0,
// // // //                     mr: open ? 2 : "auto",
// // // //                     justifyContent: "center",
// // // //                     color: "inherit",
// // // //                   }}
// // // //                 >
// // // //                   <MenuIcon />
// // // //                 </ListItemIcon>
// // // //                 <ListItemText
// // // //                   sx={{
// // // //                     opacity: open ? 1 : 0,
// // // //                     width: open ? "auto" : 0,
// // // //                     overflow: "hidden",
// // // //                     whiteSpace: "nowrap",
// // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // //                       easing: theme.transitions.easing.sharp,
// // // //                       duration: theme.transitions.duration.enteringScreen,
// // // //                     }),
// // // //                   }}
// // // //                 />
// // // //               </ListItemButton>
// // // //             </ListItem>
// // // //           )}
// // // //           {currentMenuItems.map((item) => (
// // // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // // //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// // // //                 <ListItemButton
// // // //                   onClick={() => handleNavigation(item.path)}
// // // //                   selected={location.pathname === item.path}
// // // //                   sx={{
// // // //                     mx: 1,
// // // //                     borderRadius: 2,
// // // //                     minHeight: 48,
// // // //                     justifyContent: open ? "initial" : "center",
// // // //                     px: 2.5,
// // // //                     color: theme.palette.text.primary,
// // // //                     "& .MuiListItemIcon-root": {
// // // //                       color: theme.palette.text.secondary,
// // // //                     },
// // // //                     "&:hover": {
// // // //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// // // //                         accentColor1,
// // // //                         0.45
// // // //                       )}, ${alpha(accentColor2, 0.35)})`,
// // // //                       color: theme.palette.primary.contrastText,
// // // //                       transform: "scale(1.02)",
// // // //                       "& .MuiListItemIcon-root": {
// // // //                         color: theme.palette.primary.contrastText,
// // // //                       },
// // // //                     },
// // // //                     "&.Mui-selected": {
// // // //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// // // //                         accentColor1,
// // // //                         0.45
// // // //                       )}, ${alpha(accentColor2, 0.35)})`,
// // // //                       color: theme.palette.primary.contrastText,
// // // //                       fontWeight: "bold",
// // // //                       transform: "scale(1.01)",
// // // //                       "& .MuiListItemIcon-root": {
// // // //                         color: theme.palette.primary.contrastText,
// // // //                       },
// // // //                       "&:hover": {
// // // //                         backgroundImage: `linear-gradient(90deg, ${alpha(
// // // //                           accentColor1,
// // // //                           0.45
// // // //                         )}, ${alpha(accentColor2, 0.35)})`,
// // // //                         filter: "brightness(1.15)",
// // // //                       },
// // // //                     },
// // // //                     transition: theme.transitions.create(
// // // //                       ["background-image", "color", "transform", "filter"],
// // // //                       {
// // // //                         duration: theme.transitions.duration.short,
// // // //                         easing: theme.transitions.easing.easeInOut,
// // // //                       }
// // // //                     ),
// // // //                   }}
// // // //                 >
// // // //                   <ListItemIcon
// // // //                     sx={{
// // // //                       minWidth: 0,
// // // //                       mr: open ? 2 : "auto",
// // // //                       justifyContent: "center",
// // // //                       transition: theme.transitions.create("color", {
// // // //                         duration: theme.transitions.duration.short,
// // // //                       }),
// // // //                     }}
// // // //                   >
// // // //                     {item.icon}
// // // //                   </ListItemIcon>
// // // //                   <ListItemText
// // // //                     primary={item.text}
// // // //                     sx={{
// // // //                       opacity: open ? 1 : 0,
// // // //                       width: open ? "auto" : 0,
// // // //                       overflow: "hidden",
// // // //                       whiteSpace: "nowrap",
// // // //                       transition: theme.transitions.create(
// // // //                         ["opacity", "width"],
// // // //                         {
// // // //                           easing: theme.transitions.easing.sharp,
// // // //                           duration: theme.transitions.duration.enteringScreen,
// // // //                         }
// // // //                       ),
// // // //                     }}
// // // //                   />
// // // //                 </ListItemButton>
// // // //               </Tooltip>
// // // //             </ListItem>
// // // //           ))}
// // // //         </List>
// // // //         <Box
// // // //           sx={{
// // // //             py: open ? 2 : 0,
// // // //             px: 2,
// // // //             mt: "auto",
// // // //             borderTop: "1px solid",
// // // //             borderColor: open
// // // //               ? alpha(theme.palette.divider, 0.3)
// // // //               : "transparent",
// // // //             opacity: open ? 1 : 0,
// // // //             maxHeight: open ? 100 : 0,
// // // //             overflow: "hidden",
// // // //             transition: theme.transitions.create(
// // // //               ["opacity", "max-height", "padding", "border-color"],
// // // //               {
// // // //                 easing: theme.transitions.easing.sharp,
// // // //                 duration: theme.transitions.duration.enteringScreen,
// // // //               }
// // // //             ),
// // // //           }}
// // // //         >
// // // //           <Typography
// // // //             variant="caption"
// // // //             color="textSecondary"
// // // //             align="center"
// // // //             display="block"
// // // //           >
// // // //             © 2025 The Data Tech Labs
// // // //           </Typography>
// // // //         </Box>
// // // //       </Box>
// // // //     </Drawer>
// // // //   );
// // // // };

// // // // export default Sidebar;

// // // // // import {
// // // // //   Drawer,
// // // // //   List,
// // // // //   ListItem,
// // // // //   ListItemIcon,
// // // // //   ListItemText,
// // // // //   ListItemButton,
// // // // //   Box,
// // // // //   Typography,
// // // // //   useTheme,
// // // // //   useMediaQuery,
// // // // //   alpha,
// // // // //   Tooltip,
// // // // // } from "@mui/material";
// // // // // import {
// // // // //   Dashboard,
// // // // //   Person,
// // // // //   Work,
// // // // //   Psychology,
// // // // //   Badge,
// // // // //   PostAdd,
// // // // //   People,
// // // // //   VideoCall,
// // // // //   Analytics,
// // // // //   Map,
// // // // //   Settings,
// // // // //   Menu as MenuIcon,
// // // // // } from "@mui/icons-material";
// // // // // import ListAltTwoToneIcon from '@mui/icons-material/ListAltTwoTone';
// // // // // import { useNavigate, useLocation } from "react-router-dom";
// // // // // import { useAuth } from "../../context/AuthContext";

// // // // // const drawerWidth = 256;
// // // // // const drawerWidthCollapsed = 64;

// // // // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // // // //   const { user } = useAuth();
// // // // //   const navigate = useNavigate();
// // // // //   const location = useLocation();
// // // // //   const theme = useTheme();
// // // // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // // // //   const accentColor2 = "#df30b6"; // The pink/purple-ish color
// // // // //   const accentColor1 = "#100eb8"; // The vibrant blue color

// // // // //   const menuItems = {
// // // // //     talent: [
// // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // // // //       {
// // // // //         text: "Profile Builder",
// // // // //         icon: <Person />,
// // // // //         path: "/gatep/talent/profile",
// // // // //       },
// // // // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // // // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // // // //       {
// // // // //         text: "AI Assistant",
// // // // //         icon: <Psychology />,
// // // // //         path: "/gatep/talent/assistant",
// // // // //       },
// // // // //       {
// // // // //         text: "Skills Passport",
// // // // //         icon: <Badge />,
// // // // //         path: "/gatep/talent/passport",
// // // // //       },
// // // // //       {
// // // // //         text: "Mock Interview",
// // // // //         icon: <Psychology />,
// // // // //         path: "/gatep/talent/exam-intro",
// // // // //       },
// // // // //       {
// // // // //         text: "Report",
// // // // //         icon: <ListAltTwoToneIcon />,
// // // // //         path: "/gatep/talent/Report",
// // // // //       },
// // // // //     ],
// // // // //     employer: [
// // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // // // //       {
// // // // //         text: "Company Profile",
// // // // //         icon: <PostAdd />,
// // // // //         path: "/gatep/employer/company-profile",
// // // // //       },
// // // // //       {
// // // // //         text: "Post Jobs",
// // // // //         icon: <PostAdd />,
// // // // //         path: "/gatep/employer/post-job",
// // // // //       },
// // // // //       {
// // // // //         text: "Candidates",
// // // // //         icon: <People />,
// // // // //         path: "/gatep/employer/candidates",
// // // // //       },
// // // // //       {
// // // // //         text: "Interviews",
// // // // //         icon: <VideoCall />,
// // // // //         path: "/gatep/employer/interviews",
// // // // //       },
// // // // //       {
// // // // //         text: "Analytics",
// // // // //         icon: <Analytics />,
// // // // //         path: "/gatep/employer/analytics",
// // // // //       },
// // // // //     ],
// // // // //     admin: [
// // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // // // //       {
// // // // //         text: "Analytics",
// // // // //         icon: <Analytics />,
// // // // //         path: "/gatep/admin/analytics",
// // // // //       },
// // // // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // // // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // // // //       {
// // // // //         text: "Integrations",
// // // // //         icon: <Settings />,
// // // // //         path: "/gatep/admin/integrations",
// // // // //       },
// // // // //     ],
// // // // //   };

// // // // //   const currentMenuItems = menuItems[user?.role] || [];

// // // // //   const handleNavigation = (path) => {
// // // // //     navigate(path);
// // // // //     if (isMobile) {
// // // // //       onClose();
// // // // //     }
// // // // //   };

// // // // //   return (
// // // // //     <Drawer
// // // // //       anchor="left"
// // // // //       open={open}
// // // // //       variant={variant}
// // // // //       onClose={onClose}
// // // // //       sx={{
// // // // //         flexShrink: 0,
// // // // //         "& .MuiDrawer-paper": {
// // // // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // // // //           boxSizing: "border-box",
// // // // //           mt: !isMobile ? "80px" : 0,
// // // // //           ml: !isMobile ? 0 : 0,
// // // // //           mb: !isMobile ? 2 : 0,
// // // // //           height: !isMobile ? "calc(100% - 96px)" : "100%",
// // // // //           borderRadius: !isMobile ? 4 : 0,
// // // // //           border: "1px solid",
// // // // //           borderColor: alpha(theme.palette.divider, 0.3),
// // // // //           background:
// // // // //             theme.palette.mode === "light"
// // // // //               ? `linear-gradient(135deg, ${alpha(
// // // // //                   theme.palette.background.paper,
// // // // //                   0.9
// // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// // // // //               : `linear-gradient(135deg, ${alpha(
// // // // //                   theme.palette.background.paper,
// // // // //                   0.9
// // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,
// // // // //           boxShadow: !isMobile
// // // // //             ? `0px 8px 20px ${alpha(
// // // // //                 theme.palette.common.black,
// // // // //                 0.1
// // // // //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// // // // //             : "none",
// // // // //           transition: theme.transitions.create(
// // // // //             ["width", "border-radius", "box-shadow", "background"],
// // // // //             {
// // // // //               easing: theme.transitions.easing.easeInOut,
// // // // //               duration: theme.transitions.duration.standard,
// // // // //             }
// // // // //           ),
// // // // //         },
// // // // //       }}
// // // // //     >
// // // // //       <Box
// // // // //         sx={{
// // // // //           height: "100%",
// // // // //           display: "flex",
// // // // //           flexDirection: "column",
// // // // //         }}
// // // // //       >
// // // // //         <List
// // // // //           sx={{
// // // // //             py: 1,
// // // // //             flexGrow: 1,
// // // // //             overflowY: "auto",
// // // // //             mt: isMobile ? "54px" : 0,
// // // // //           }}
// // // // //         >
// // // // //           {!isMobile && (
// // // // //             <ListItem disablePadding sx={{ display: "block" }}>
// // // // //               <ListItemButton
// // // // //                 onClick={onToggle}
// // // // //                 disableRipple
// // // // //                 sx={{
// // // // //                   mx: 1,
// // // // //                   borderRadius: 2,
// // // // //                   minHeight: 48,
// // // // //                   justifyContent: open ? "initial" : "center",
// // // // //                   px: 2.5,
// // // // //                   backgroundColor: "transparent",
// // // // //                   boxShadow: "none",
// // // // //                   color: theme.palette.text.secondary,
// // // // //                   "&:hover": {
// // // // //                     backgroundColor: alpha(theme.palette.action.hover, 0.5),
// // // // //                   },
// // // // //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// // // // //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// // // // //                   "&:focus": { outline: "none", boxShadow: "none" },
// // // // //                 }}
// // // // //               >
// // // // //                 <ListItemIcon
// // // // //                   sx={{
// // // // //                     minWidth: 0,
// // // // //                     mr: open ? 2 : "auto",
// // // // //                     justifyContent: "center",
// // // // //                     // color: "inherit",
// // // // //                   }}
// // // // //                 >
// // // // //                   <MenuIcon />
// // // // //                 </ListItemIcon>
// // // // //                 <ListItemText
// // // // //                   sx={{
// // // // //                     opacity: open ? 1 : 0,
// // // // //                     width: open ? "auto" : 0,
// // // // //                     overflow: "hidden",
// // // // //                     whiteSpace: "nowrap",
// // // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // // //                       easing: theme.transitions.easing.sharp,
// // // // //                       duration: theme.transitions.duration.enteringScreen,
// // // // //                     }),
// // // // //                   }}
// // // // //                 />
// // // // //               </ListItemButton>
// // // // //             </ListItem>
// // // // //           )}
// // // // //           {currentMenuItems.map((item) => (
// // // // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // // // //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// // // // //                 <ListItemButton
// // // // //                   onClick={() => handleNavigation(item.path)}
// // // // //                   selected={location.pathname === item.path}
// // // // //                   sx={{
// // // // //                     mx: 1,
// // // // //                     borderRadius: 2,
// // // // //                     minHeight: 48,
// // // // //                     justifyContent: open ? "initial" : "center",
// // // // //                     px: 2.5,
// // // // //                     color: theme.palette.text.primary,
// // // // //                     "& .MuiListItemIcon-root": {
// // // // //                       color: theme.palette.text.secondary,
// // // // //                     },
// // // // //                     "&:hover": {
// // // // //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// // // // //                         accentColor1,
// // // // //                         0.45
// // // // //                       )}, ${alpha(accentColor2, 0.35)})`,
// // // // //                       color: theme.palette.primary.contrastText,
// // // // //                       transform: "scale(1.02)",
// // // // //                       "& .MuiListItemIcon-root": {
// // // // //                         color: theme.palette.primary.contrastText,
// // // // //                       },
// // // // //                     },
// // // // //                     "&.Mui-selected": {
// // // // //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// // // // //                         accentColor1,
// // // // //                         0.45
// // // // //                       )}, ${alpha(accentColor2, 0.35)})`,
// // // // //                       color: theme.palette.primary.contrastText,
// // // // //                       fontWeight: "bold",
// // // // //                       transform: "scale(1.01)",
// // // // //                       "& .MuiListItemIcon-root": {
// // // // //                         color: theme.palette.primary.contrastText,
// // // // //                       },
// // // // //                       "&:hover": {
// // // // //                         backgroundImage: `linear-gradient(90deg, ${alpha(
// // // // //                           accentColor1,
// // // // //                           0.45
// // // // //                         )}, ${alpha(accentColor2, 0.35)})`,
// // // // //                         filter: "brightness(1.15)",
// // // // //                       },
// // // // //                     },
// // // // //                     transition: theme.transitions.create(
// // // // //                       ["background-image", "color", "transform", "filter"],
// // // // //                       {
// // // // //                         duration: theme.transitions.duration.short,
// // // // //                         easing: theme.transitions.easing.easeInOut,
// // // // //                       }
// // // // //                     ),
// // // // //                   }}
// // // // //                 >
// // // // //                   <ListItemIcon
// // // // //                     sx={{
// // // // //                       minWidth: 0,
// // // // //                       mr: open ? 2 : "auto",
// // // // //                       justifyContent: "center",
// // // // //                       transition: theme.transitions.create("color", {
// // // // //                         duration: theme.transitions.duration.short,
// // // // //                       }),
// // // // //                     }}
// // // // //                   >
// // // // //                     {item.icon}
// // // // //                   </ListItemIcon>
// // // // //                   <ListItemText
// // // // //                     primary={item.text}
// // // // //                     sx={{
// // // // //                       opacity: open ? 1 : 0,
// // // // //                       width: open ? "auto" : 0,
// // // // //                       overflow: "hidden",
// // // // //                       whiteSpace: "nowrap",
// // // // //                       transition: theme.transitions.create(
// // // // //                         ["opacity", "width"],
// // // // //                         {
// // // // //                           easing: theme.transitions.easing.sharp,
// // // // //                           duration: theme.transitions.duration.enteringScreen,
// // // // //                         }
// // // // //                       ),
// // // // //                     }}
// // // // //                   />
// // // // //                 </ListItemButton>
// // // // //               </Tooltip>
// // // // //             </ListItem>
// // // // //           ))}
// // // // //         </List>
// // // // //         <Box
// // // // //           sx={{
// // // // //             py: open ? 2 : 0,
// // // // //             px: 2,
// // // // //             mt: "auto",
// // // // //             borderTop: "1px solid",
// // // // //             borderColor: open
// // // // //               ? alpha(theme.palette.divider, 0.3)
// // // // //               : "transparent",
// // // // //             opacity: open ? 1 : 0,
// // // // //             maxHeight: open ? 100 : 0,
// // // // //             overflow: "hidden",
// // // // //             transition: theme.transitions.create(
// // // // //               ["opacity", "max-height", "padding", "border-color"],
// // // // //               {
// // // // //                 easing: theme.transitions.easing.sharp,
// // // // //                 duration: theme.transitions.duration.enteringScreen,
// // // // //               }
// // // // //             ),
// // // // //           }}
// // // // //         >
// // // // //           <Typography
// // // // //             variant="caption"
// // // // //             color="textSecondary"
// // // // //             align="center"
// // // // //             display="block"
// // // // //           >
// // // // //             © 2025 The Data Tech Labs
// // // // //           </Typography>
// // // // //         </Box>
// // // // //       </Box>
// // // // //     </Drawer>
// // // // //   );
// // // // // };

// // // // // export default Sidebar;

// // // // // // import {
// // // // // //   Drawer,
// // // // // //   List,
// // // // // //   ListItem,
// // // // // //   ListItemIcon,
// // // // // //   ListItemText,
// // // // // //   ListItemButton,
// // // // // //   Box,
// // // // // //   Typography,
// // // // // //   useTheme,
// // // // // //   useMediaQuery,
// // // // // //   alpha,
// // // // // //   Tooltip,
// // // // // // } from "@mui/material";
// // // // // // import {
// // // // // //   Dashboard,
// // // // // //   Person,
// // // // // //   Work,
// // // // // //   Psychology,
// // // // // //   Badge,
// // // // // //   PostAdd,
// // // // // //   People,
// // // // // //   VideoCall,
// // // // // //   Analytics,
// // // // // //   Map,
// // // // // //   Settings,
// // // // // //   Menu as MenuIcon,
// // // // // // } from "@mui/icons-material";
// // // // // // import { useNavigate, useLocation } from "react-router-dom";
// // // // // // import { useAuth } from "../../context/AuthContext";

// // // // // // const drawerWidth = 256;
// // // // // // const drawerWidthCollapsed = 64;

// // // // // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // // // // //   const { user } = useAuth();
// // // // // //   const navigate = useNavigate();
// // // // // //   const location = useLocation();
// // // // // //   const theme = useTheme();
// // // // // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // // // // //   const accentColor2 = "#df30b6"; // The pink/purple-ish color
// // // // // //   const accentColor1 = "#100eb8"; // The vibrant blue color
// // // // // //   const primaryAccentColor = accentColor1;

// // // // // //   const menuItems = {
// // // // // //     talent: [
// // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // // // // //       {
// // // // // //         text: "Profile Builder",
// // // // // //         icon: <Person />,
// // // // // //         path: "/gatep/talent/profile",
// // // // // //       },
// // // // // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // // // // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // // // // //       {
// // // // // //         text: "AI Assistant",
// // // // // //         icon: <Psychology />,
// // // // // //         path: "/gatep/talent/assistant",
// // // // // //       },
// // // // // //       {
// // // // // //         text: "Skills Passport",
// // // // // //         icon: <Badge />,
// // // // // //         path: "/gatep/talent/passport",
// // // // // //       },
// // // // // //       {
// // // // // //         text: "Mock Interview",
// // // // // //         icon: <Psychology />,
// // // // // //         path: "/gatep/talent/exam-intro",
// // // // // //       },
// // // // // //       {
// // // // // //         text: "Report",
// // // // // //         icon: <Psychology />,
// // // // // //         path: "/gatep/talent/Report",
// // // // // //       },
// // // // // //     ],
// // // // // //     employer: [
// // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // // // // //       {
// // // // // //         text: "Company Profile",
// // // // // //         icon: <PostAdd />,
// // // // // //         path: "/gatep/employer/company-profile",
// // // // // //       },
// // // // // //       {
// // // // // //         text: "Post Jobs",
// // // // // //         icon: <PostAdd />,
// // // // // //         path: "/gatep/employer/post-job",
// // // // // //       },
// // // // // //       {
// // // // // //         text: "Candidates",
// // // // // //         icon: <People />,
// // // // // //         path: "/gatep/employer/candidates",
// // // // // //       },
// // // // // //       {
// // // // // //         text: "Interviews",
// // // // // //         icon: <VideoCall />,
// // // // // //         path: "/gatep/employer/interviews",
// // // // // //       },
// // // // // //       {
// // // // // //         text: "Analytics",
// // // // // //         icon: <Analytics />,
// // // // // //         path: "/gatep/employer/analytics",
// // // // // //       },
// // // // // //     ],
// // // // // //     admin: [
// // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // // // // //       {
// // // // // //         text: "Analytics",
// // // // // //         icon: <Analytics />,
// // // // // //         path: "/gatep/admin/analytics",
// // // // // //       },
// // // // // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // // // // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // // // // //       {
// // // // // //         text: "Integrations",
// // // // // //         icon: <Settings />,
// // // // // //         path: "/gatep/admin/integrations",
// // // // // //       },
// // // // // //     ],
// // // // // //   };

// // // // // //   const currentMenuItems = menuItems[user?.role] || [];

// // // // // //   const handleNavigation = (path) => {
// // // // // //     navigate(path);
// // // // // //     if (isMobile) {
// // // // // //       onClose();
// // // // // //     }
// // // // // //   };

// // // // // //   return (
// // // // // //     <Drawer
// // // // // //       anchor="left"
// // // // // //       open={open}
// // // // // //       variant={variant}
// // // // // //       onClose={onClose}
// // // // // //       sx={{
// // // // // //         flexShrink: 0,
// // // // // //         "& .MuiDrawer-paper": {
// // // // // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // // // // //           boxSizing: "border-box",
// // // // // //           mt: !isMobile ? "80px" : 0,
// // // // // //           ml: !isMobile ? 0 : 0,
// // // // // //           mb: !isMobile ? 2 : 0,
// // // // // //           height: !isMobile ? "calc(100% - 96px)" : "100%",
// // // // // //           borderRadius: !isMobile ? 4 : 0,
// // // // // //           border: "1px solid",
// // // // // //           borderColor: alpha(theme.palette.divider, 0.3),
// // // // // //           background:
// // // // // //             theme.palette.mode === "light"
// // // // // //               ? `linear-gradient(135deg, ${alpha(
// // // // // //                   theme.palette.background.paper,
// // // // // //                   0.9
// // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// // // // // //               : `linear-gradient(135deg, ${alpha(
// // // // // //                   theme.palette.background.paper,
// // // // // //                   0.9
// // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,

// // // // // //           boxShadow: !isMobile
// // // // // //             ? `0px 8px 20px ${alpha(
// // // // // //                 theme.palette.common.black,
// // // // // //                 0.1
// // // // // //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// // // // // //             : "none",
// // // // // //           transition: theme.transitions.create(
// // // // // //             ["width", "border-radius", "box-shadow", "background"],
// // // // // //             {
// // // // // //               easing: theme.transitions.easing.easeInOut,
// // // // // //               duration: theme.transitions.duration.standard,
// // // // // //             }
// // // // // //           ),
// // // // // //         },
// // // // // //       }}
// // // // // //     >
// // // // // //       <Box
// // // // // //         sx={{
// // // // // //           height: "100%",
// // // // // //           display: "flex",
// // // // // //           flexDirection: "column",
// // // // // //         }}
// // // // // //       >
// // // // // //         <List
// // // // // //           sx={{
// // // // // //             py: 1,
// // // // // //             flexGrow: 1,
// // // // // //             overflowY: "auto",
// // // // // //             mt: isMobile ? "54px" : 0,
// // // // // //           }}
// // // // // //         >
// // // // // //           {/* Toggle button as first list item */}
// // // // // //           {!isMobile && (
// // // // // //             <ListItem disablePadding sx={{ display: "block" }}>
// // // // // //               <ListItemButton
// // // // // //                 onClick={onToggle}
// // // // // //                 disableRipple
// // // // // //                 sx={{
// // // // // //                   mx: 1,
// // // // // //                   borderRadius: 2,
// // // // // //                   minHeight: 48,
// // // // // //                   justifyContent: open ? "initial" : "center",
// // // // // //                   px: 2.5,
// // // // // //                   backgroundColor: "transparent",
// // // // // //                   boxShadow: "none",
// // // // // //                   color: theme.palette.text.secondary,
// // // // // //                   "&:hover": {
// // // // // //                     backgroundColor: "transparent",
// // // // // //                   },
// // // // // //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// // // // // //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// // // // // //                   "&:focus": { outline: "none", boxShadow: "none" },
// // // // // //                 }}
// // // // // //               >
// // // // // //                 <ListItemIcon
// // // // // //                   sx={{
// // // // // //                     minWidth: 0,
// // // // // //                     mr: open ? 2 : "auto",
// // // // // //                     justifyContent: "center",
// // // // // //                     color: "inherit",
// // // // // //                   }}
// // // // // //                 >
// // // // // //                   <MenuIcon />
// // // // // //                 </ListItemIcon>
// // // // // //                 <ListItemText
// // // // // //                   sx={{
// // // // // //                     opacity: open ? 1 : 0,
// // // // // //                     width: open ? "auto" : 0,
// // // // // //                     overflow: "hidden",
// // // // // //                     whiteSpace: "nowrap",
// // // // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // // // //                       easing: theme.transitions.easing.sharp,
// // // // // //                       duration: theme.transitions.duration.enteringScreen,
// // // // // //                     }),
// // // // // //                   }}
// // // // // //                 />
// // // // // //               </ListItemButton>
// // // // // //             </ListItem>
// // // // // //           )}
// // // // // //           {/* Menu items */}
// // // // // //           {currentMenuItems.map((item) => (
// // // // // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // // // // //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// // // // // //                 <ListItemButton
// // // // // //                   onClick={() => handleNavigation(item.path)}
// // // // // //                   selected={location.pathname === item.path}
// // // // // //                   sx={{
// // // // // //                     mx: 1,
// // // // // //                     borderRadius: 2,
// // // // // //                     minHeight: 48,
// // // // // //                     justifyContent: open ? "initial" : "center",
// // // // // //                     px: 2.5,

// // // // // //                     color: theme.palette.text.primary,
// // // // // //                     "& .MuiListItemIcon-root": {
// // // // // //                       color: theme.palette.text.secondary,
// // // // // //                     },
// // // // // //                     "&:hover": {
// // // // // //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// // // // // //                         accentColor1,
// // // // // //                         0.45
// // // // // //                       )}, ${alpha(accentColor2, 0.35)})`,
// // // // // //                       backgroundColor: "transparent",
// // // // // //                       color: "black",
// // // // // //                       transform: "scale(1.02)",
// // // // // //                       "& .MuiListItemIcon-root": {
// // // // // //                         color: "white",
// // // // // //                       },
// // // // // //                     },
// // // // // //                     "&.Mui-selected": {
// // // // // //                       backgroundColor: alpha(primaryAccentColor, 0.2),
// // // // // //                       background:`linear-gradient(90deg, ${alpha(
// // // // // //                         accentColor1,
// // // // // //                         0.45
// // // // // //                       )}, ${alpha(accentColor2, 0.35)})`,
// // // // // //                       backgroundImage: "none",
// // // // // //                       // --- FIX 1: Change text color to something more readable and theme-consistent. ---
// // // // // //                       // "black" is fine, but using the theme is better practice.
// // // // // //                       color: theme.palette.text.primary,
// // // // // //                       fontWeight: "bold",
// // // // // //                       transform: "scale(1.01)",
// // // // // //                       "& .MuiListItemIcon-root": {
// // // // // //                         color: "black",
// // // // // //                       },
// // // // // //                       "&:hover": {
// // // // // //                         // --- FIX 2: Use a hex code or theme color for alpha(). ---
// // // // // //                         backgroundColor: alpha(theme.palette.common.black, 0.1),
// // // // // //                         backgroundImage: "none",
// // // // // //                       },
// // // // // //                     },
// // // // // //                     transition: theme.transitions.create(
// // // // // //                       [
// // // // // //                         "background-color",
// // // // // //                         "color",
// // // // // //                         "transform",
// // // // // //                         "background-image",
// // // // // //                       ],
// // // // // //                       {
// // // // // //                         duration: theme.transitions.duration.short,
// // // // // //                         easing: theme.transitions.easing.easeInOut,
// // // // // //                       }
// // // // // //                     ),
// // // // // //                   }}
// // // // // //                 >
// // // // // //                   <ListItemIcon
// // // // // //                     sx={{
// // // // // //                       minWidth: 0,
// // // // // //                       mr: open ? 2 : "auto",
// // // // // //                       justifyContent: "center",
// // // // // //                       transition: theme.transitions.create("color", {
// // // // // //                         duration: theme.transitions.duration.short,
// // // // // //                       }),
// // // // // //                     }}
// // // // // //                   >
// // // // // //                     {item.icon}
// // // // // //                   </ListItemIcon>
// // // // // //                   <ListItemText
// // // // // //                     primary={item.text}
// // // // // //                     sx={{
// // // // // //                       opacity: open ? 1 : 0,
// // // // // //                       width: open ? "auto" : 0,
// // // // // //                       overflow: "hidden",
// // // // // //                       whiteSpace: "nowrap",
// // // // // //                       transition: theme.transitions.create(
// // // // // //                         ["opacity", "width"],
// // // // // //                         {
// // // // // //                           easing: theme.transitions.easing.sharp,
// // // // // //                           duration: theme.transitions.duration.enteringScreen,
// // // // // //                         }
// // // // // //                       ),
// // // // // //                     }}
// // // // // //                   />
// // // // // //                 </ListItemButton>
// // // // // //               </Tooltip>
// // // // // //             </ListItem>
// // // // // //           ))}
// // // // // //         </List>
// // // // // //         {/* Footer */}
// // // // // //         <Box
// // // // // //           sx={{
// // // // // //             py: open ? 2 : 0,
// // // // // //             px: 2,
// // // // // //             mt: "auto",
// // // // // //             borderTop: "1px solid",
// // // // // //             borderColor: open
// // // // // //               ? alpha(theme.palette.divider, 0.3)
// // // // // //               : "transparent",
// // // // // //             opacity: open ? 1 : 0,
// // // // // //             maxHeight: open ? 100 : 0,
// // // // // //             overflow: "hidden",
// // // // // //             transition: theme.transitions.create(
// // // // // //               ["opacity", "max-height", "padding", "border-color"],
// // // // // //               {
// // // // // //                 easing: theme.transitions.easing.sharp,
// // // // // //                 duration: theme.transitions.duration.enteringScreen,
// // // // // //               }
// // // // // //             ),
// // // // // //           }}
// // // // // //         >
// // // // // //           <Typography
// // // // // //             variant="caption"
// // // // // //             color="textSecondary"
// // // // // //             align="center"
// // // // // //             display="block"
// // // // // //           >
// // // // // //             © 2025 The Data Tech Labs
// // // // // //           </Typography>
// // // // // //         </Box>
// // // // // //       </Box>
// // // // // //     </Drawer>
// // // // // //   );
// // // // // // };

// // // // // // export default Sidebar;

// // // // // // // import {
// // // // // // //   Drawer,
// // // // // // //   List,
// // // // // // //   ListItem,
// // // // // // //   ListItemIcon,
// // // // // // //   ListItemText,
// // // // // // //   ListItemButton,
// // // // // // //   Box,
// // // // // // //   Typography,
// // // // // // //   useTheme,
// // // // // // //   useMediaQuery,
// // // // // // //   alpha,
// // // // // // //   Tooltip,
// // // // // // // } from "@mui/material";
// // // // // // // import {
// // // // // // //   Dashboard,
// // // // // // //   Person,
// // // // // // //   Work,
// // // // // // //   Psychology,
// // // // // // //   Badge,
// // // // // // //   PostAdd,
// // // // // // //   People,
// // // // // // //   VideoCall,
// // // // // // //   Analytics,
// // // // // // //   Map,
// // // // // // //   Settings,
// // // // // // //   Menu as MenuIcon,
// // // // // // // } from "@mui/icons-material";
// // // // // // // import { useNavigate, useLocation } from "react-router-dom";
// // // // // // // import { useAuth } from "../../context/AuthContext";

// // // // // // // const drawerWidth = 256;
// // // // // // // const drawerWidthCollapsed = 64;

// // // // // // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // // // // // //   const { user } = useAuth();
// // // // // // //   const navigate = useNavigate();
// // // // // // //   const location = useLocation();
// // // // // // //   const theme = useTheme();
// // // // // // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // // // // // //   // --- CHANGE: Separated the gradient colors for use with alpha() ---
// // // // // // //   // Define the two colors from your desired gradient
// // // // // // //   const accentColor2 = "#df30b6ff"; // The pink/purple-ish color
// // // // // // //   const accentColor1 = "#100eb8ff"; // The vibrant blue color

// // // // // // //   // Use the more dominant blue as the primary accent for text and solid backgrounds
// // // // // // //   const primaryAccentColor = accentColor1;

// // // // // // //   // --- REMOVED ---
// // // // // // //   // const accentColor = "linear-gradient(135deg, #c37ab2ff 0%, #5856f1ff 100%)";

// // // // // // //   const menuItems = {
// // // // // // //     talent: [
// // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // // // // // //       {
// // // // // // //         text: "Profile Builder",
// // // // // // //         icon: <Person />,
// // // // // // //         path: "/gatep/talent/profile",
// // // // // // //       },
// // // // // // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // // // // // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // // // // // //       {
// // // // // // //         text: "AI Assistant",
// // // // // // //         icon: <Psychology />,
// // // // // // //         path: "/gatep/talent/assistant",
// // // // // // //       },
// // // // // // //       {
// // // // // // //         text: "Skills Passport",
// // // // // // //         icon: <Badge />,
// // // // // // //         path: "/gatep/talent/passport",
// // // // // // //       },
// // // // // // //       {
// // // // // // //         text: "Mock Interview",
// // // // // // //         icon: <Psychology />,
// // // // // // //         path: "/gatep/talent/exam-intro",
// // // // // // //       },
// // // // // // //       {
// // // // // // //         text: "Report",
// // // // // // //         icon: <Psychology />,
// // // // // // //         path: "/gatep/talent/Report",
// // // // // // //       },
// // // // // // //     ],
// // // // // // //     employer: [
// // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // // // // // //       {
// // // // // // //         text: "Company Profile",
// // // // // // //         icon: <PostAdd />,
// // // // // // //         path: "/gatep/employer/company-profile",
// // // // // // //       },
// // // // // // //       {
// // // // // // //         text: "Post Jobs",
// // // // // // //         icon: <PostAdd />,
// // // // // // //         path: "/gatep/employer/post-job",
// // // // // // //       },
// // // // // // //       {
// // // // // // //         text: "Candidates",
// // // // // // //         icon: <People />,
// // // // // // //         path: "/gatep/employer/candidates",
// // // // // // //       },
// // // // // // //       {
// // // // // // //         text: "Interviews",
// // // // // // //         icon: <VideoCall />,
// // // // // // //         path: "/gatep/employer/interviews",
// // // // // // //       },
// // // // // // //       {
// // // // // // //         text: "Analytics",
// // // // // // //         icon: <Analytics />,
// // // // // // //         path: "/gatep/employer/analytics",
// // // // // // //       },
// // // // // // //     ],
// // // // // // //     admin: [
// // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // // // // // //       {
// // // // // // //         text: "Analytics",
// // // // // // //         icon: <Analytics />,
// // // // // // //         path: "/gatep/admin/analytics",
// // // // // // //       },
// // // // // // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // // // // // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // // // // // //       {
// // // // // // //         text: "Integrations",
// // // // // // //         icon: <Settings />,
// // // // // // //         path: "/gatep/admin/integrations",
// // // // // // //       },
// // // // // // //     ],
// // // // // // //   };

// // // // // // //   const currentMenuItems = menuItems[user?.role] || [];

// // // // // // //   const handleNavigation = (path) => {
// // // // // // //     navigate(path);
// // // // // // //     if (isMobile) {
// // // // // // //       onClose();
// // // // // // //     }
// // // // // // //   };

// // // // // // //   return (
// // // // // // //     <Drawer
// // // // // // //       anchor="left"
// // // // // // //       open={open}
// // // // // // //       variant={variant}
// // // // // // //       onClose={onClose}
// // // // // // //       sx={{
// // // // // // //         flexShrink: 0,
// // // // // // //         "& .MuiDrawer-paper": {
// // // // // // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // // // // // //           boxSizing: "border-box",
// // // // // // //           mt: !isMobile ? "80px" : 0,
// // // // // // //           ml: !isMobile ? 0 : 0,
// // // // // // //           mb: !isMobile ? 2 : 0,
// // // // // // //           height: !isMobile ? "calc(100% - 96px)" : "100%",
// // // // // // //           borderRadius: !isMobile ? 4 : 0,
// // // // // // //           border: "1px solid",
// // // // // // //           borderColor: alpha(theme.palette.divider, 0.3),
// // // // // // //           background:
// // // // // // //             theme.palette.mode === "light"
// // // // // // //               ? `linear-gradient(135deg, ${alpha(
// // // // // // //                   theme.palette.background.paper,
// // // // // // //                   0.9
// // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// // // // // // //               : `linear-gradient(135deg, ${alpha(
// // // // // // //                   theme.palette.background.paper,
// // // // // // //                   0.9
// // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,
// // // // // // //           boxShadow: !isMobile
// // // // // // //             ? `0px 8px 20px ${alpha(
// // // // // // //                 theme.palette.common.black,
// // // // // // //                 0.1
// // // // // // //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// // // // // // //             : "none",
// // // // // // //           transition: theme.transitions.create(
// // // // // // //             ["width", "border-radius", "box-shadow", "background"],
// // // // // // //             {
// // // // // // //               easing: theme.transitions.easing.easeInOut,
// // // // // // //               duration: theme.transitions.duration.standard,
// // // // // // //             }
// // // // // // //           ),
// // // // // // //         },
// // // // // // //       }}
// // // // // // //     >
// // // // // // //       <Box
// // // // // // //         sx={{
// // // // // // //           height: "100%",
// // // // // // //           display: "flex",
// // // // // // //           flexDirection: "column",
// // // // // // //         }}
// // // // // // //       >
// // // // // // //         <List
// // // // // // //           sx={{
// // // // // // //             py: 1,
// // // // // // //             flexGrow: 1,
// // // // // // //             overflowY: "auto",
// // // // // // //             mt: isMobile ? "54px" : 0,
// // // // // // //           }}
// // // // // // //         >
// // // // // // //           {/* Toggle button as first list item */}
// // // // // // //           {!isMobile && (
// // // // // // //             <ListItem disablePadding sx={{ display: "block" }}>
// // // // // // //               <ListItemButton
// // // // // // //                 onClick={onToggle}
// // // // // // //                 disableRipple
// // // // // // //                 sx={{
// // // // // // //                   mx: 1,
// // // // // // //                   borderRadius: 2,
// // // // // // //                   minHeight: 48,
// // // // // // //                   justifyContent: open ? "initial" : "center",
// // // // // // //                   px: 2.5,
// // // // // // //                   backgroundColor: "transparent",
// // // // // // //                   boxShadow: "none",
// // // // // // //                   color: theme.palette.text.secondary,
// // // // // // //                   "&:hover": {
// // // // // // //                     backgroundColor: "transparent",
// // // // // // //                   },
// // // // // // //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// // // // // // //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// // // // // // //                   "&:focus": { outline: "none", boxShadow: "none" },
// // // // // // //                 }}
// // // // // // //               >
// // // // // // //                 <ListItemIcon
// // // // // // //                   sx={{
// // // // // // //                     minWidth: 0,
// // // // // // //                     mr: open ? 2 : "auto",
// // // // // // //                     justifyContent: "center",
// // // // // // //                     color: "inherit",
// // // // // // //                   }}
// // // // // // //                 >
// // // // // // //                   <MenuIcon />
// // // // // // //                 </ListItemIcon>
// // // // // // //                 <ListItemText
// // // // // // //                   sx={{
// // // // // // //                     opacity: open ? 1 : 0,
// // // // // // //                     width: open ? "auto" : 0,
// // // // // // //                     overflow: "hidden",
// // // // // // //                     whiteSpace: "nowrap",
// // // // // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // // // // //                       easing: theme.transitions.easing.sharp,
// // // // // // //                       duration: theme.transitions.duration.enteringScreen,
// // // // // // //                     }),
// // // // // // //                   }}
// // // // // // //                 />
// // // // // // //               </ListItemButton>
// // // // // // //             </ListItem>
// // // // // // //           )}
// // // // // // //           {/* Menu items */}
// // // // // // //           {currentMenuItems.map((item) => (
// // // // // // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // // // // // //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// // // // // // //                 <ListItemButton
// // // // // // //                   onClick={() => handleNavigation(item.path)}
// // // // // // //                   selected={location.pathname === item.path}
// // // // // // //                   sx={{
// // // // // // //                     mx: 1,
// // // // // // //                     borderRadius: 2,
// // // // // // //                     minHeight: 48,
// // // // // // //                     justifyContent: open ? "initial" : "center",
// // // // // // //                     px: 2.5,
// // // // // // //                     color: theme.palette.text.primary,
// // // // // // //                     "& .MuiListItemIcon-root": {
// // // // // // //                       color: theme.palette.text.secondary,
// // // // // // //                     },
// // // // // // //                     // --- CHANGE: Use the new color variables correctly ---
// // // // // // //                     "&:hover": {
// // // // // // //                       // Construct the gradient from the individual transparent colors
// // // // // // //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// // // // // // //                         accentColor1,
// // // // // // //                         0.45
// // // // // // //                       )}, ${alpha(accentColor2, 0.35)})`,
// // // // // // //                       backgroundColor: "transparent",
// // // // // // //                       // Use the single primary color for text and icons
// // // // // // //                       color: primaryAccentColor,
// // // // // // //                       transform: "scale(1.02)",
// // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // //                         color: primaryAccentColor,
// // // // // // //                       },
// // // // // // //                     },
// // // // // // //                     "&.Mui-selected": {
// // // // // // //                       // Use alpha() on the single primary color for a solid background
// // // // // // //                       backgroundColor: alpha(primaryAccentColor, 0.2),
// // // // // // //                       backgroundImage: "none",
// // // // // // //                       color: "black",
// // // // // // //                       fontWeight: "bold",
// // // // // // //                       transform: "scale(1.01)",
// // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // //                         color: primaryAccentColor,
// // // // // // //                       },
// // // // // // //                       "&:hover": {
// // // // // // //                         backgroundColor: alpha("black", 0.25),
// // // // // // //                         backgroundImage: "none",
// // // // // // //                       },
// // // // // // //                     },
// // // // // // //                     transition: theme.transitions.create(
// // // // // // //                       [
// // // // // // //                         "background-color",
// // // // // // //                         "color",
// // // // // // //                         "transform",
// // // // // // //                         "background-image",
// // // // // // //                       ],
// // // // // // //                       {
// // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // //                         easing: theme.transitions.easing.easeInOut,
// // // // // // //                       }
// // // // // // //                     ),
// // // // // // //                   }}
// // // // // // //                 >
// // // // // // //                   <ListItemIcon
// // // // // // //                     sx={{
// // // // // // //                       minWidth: 0,
// // // // // // //                       mr: open ? 2 : "auto",
// // // // // // //                       justifyContent: "center",
// // // // // // //                       transition: theme.transitions.create("color", {
// // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // //                       }),
// // // // // // //                     }}
// // // // // // //                   >
// // // // // // //                     {item.icon}
// // // // // // //                   </ListItemIcon>
// // // // // // //                   <ListItemText
// // // // // // //                     primary={item.text}
// // // // // // //                     sx={{
// // // // // // //                       opacity: open ? 1 : 0,
// // // // // // //                       width: open ? "auto" : 0,
// // // // // // //                       overflow: "hidden",
// // // // // // //                       whiteSpace: "nowrap",
// // // // // // //                       transition: theme.transitions.create(
// // // // // // //                         ["opacity", "width"],
// // // // // // //                         {
// // // // // // //                           easing: theme.transitions.easing.sharp,
// // // // // // //                           duration: theme.transitions.duration.enteringScreen,
// // // // // // //                         }
// // // // // // //                       ),
// // // // // // //                     }}
// // // // // // //                   />
// // // // // // //                 </ListItemButton>
// // // // // // //               </Tooltip>
// // // // // // //             </ListItem>
// // // // // // //           ))}
// // // // // // //         </List>
// // // // // // //         {/* Footer */}
// // // // // // //         <Box
// // // // // // //           sx={{
// // // // // // //             py: open ? 2 : 0,
// // // // // // //             px: 2,
// // // // // // //             mt: "auto",
// // // // // // //             borderTop: "1px solid",
// // // // // // //             borderColor: open
// // // // // // //               ? alpha(theme.palette.divider, 0.3)
// // // // // // //               : "transparent",
// // // // // // //             opacity: open ? 1 : 0,
// // // // // // //             maxHeight: open ? 100 : 0,
// // // // // // //             overflow: "hidden",
// // // // // // //             transition: theme.transitions.create(
// // // // // // //               ["opacity", "max-height", "padding", "border-color"],
// // // // // // //               {
// // // // // // //                 easing: theme.transitions.easing.sharp,
// // // // // // //                 duration: theme.transitions.duration.enteringScreen,
// // // // // // //               }
// // // // // // //             ),
// // // // // // //           }}
// // // // // // //         >
// // // // // // //           <Typography
// // // // // // //             variant="caption"
// // // // // // //             color="textSecondary"
// // // // // // //             align="center"
// // // // // // //             display="block"
// // // // // // //           >
// // // // // // //             © 2025 The Data Tech Labs
// // // // // // //           </Typography>
// // // // // // //         </Box>
// // // // // // //       </Box>
// // // // // // //     </Drawer>
// // // // // // //   );
// // // // // // // };

// // // // // // // export default Sidebar;

// // // // // // // // import {
// // // // // // // //   Drawer,
// // // // // // // //   List,
// // // // // // // //   ListItem,
// // // // // // // //   ListItemIcon,
// // // // // // // //   ListItemText,
// // // // // // // //   ListItemButton,
// // // // // // // //   Box,
// // // // // // // //   Typography,
// // // // // // // //   useTheme,
// // // // // // // //   useMediaQuery,
// // // // // // // //   alpha,
// // // // // // // //   Tooltip,
// // // // // // // // } from "@mui/material";
// // // // // // // // import {
// // // // // // // //   Dashboard,
// // // // // // // //   Person,
// // // // // // // //   Work,
// // // // // // // //   Psychology,
// // // // // // // //   Badge,
// // // // // // // //   PostAdd,
// // // // // // // //   People,
// // // // // // // //   VideoCall,
// // // // // // // //   Analytics,
// // // // // // // //   Map,
// // // // // // // //   Settings,
// // // // // // // //   Menu as MenuIcon,
// // // // // // // // } from "@mui/icons-material";
// // // // // // // // import { useNavigate, useLocation } from "react-router-dom";
// // // // // // // // import { useAuth } from "../../context/AuthContext";

// // // // // // // // const drawerWidth = 256;
// // // // // // // // const drawerWidthCollapsed = 64;

// // // // // // // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // // // // // // //   const { user } = useAuth();
// // // // // // // //   const navigate = useNavigate();
// // // // // // // //   const location = useLocation();
// // // // // // // //   const theme = useTheme(); // Kept for responsiveness, transitions, and other styles
// // // // // // // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // // // // // // //   // --- CHANGE: Removed the getRoleColor function. ---

// // // // // // // //   // --- CHANGE: Hardcoded a specific blue accent color as requested. ---
// // // // // // // //   const accentColor = "linear-gradient(135deg, #c37ab2ff 0%, #5856f1ff 100%)"; // A standard, vibrant blue

// // // // // // // //   const menuItems = {
// // // // // // // //     talent: [
// // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // // // // // // //       {
// // // // // // // //         text: "Profile Builder",
// // // // // // // //         icon: <Person />,
// // // // // // // //         path: "/gatep/talent/profile",
// // // // // // // //       },
// // // // // // // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // // // // // // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // // // // // // //       {
// // // // // // // //         text: "AI Assistant",
// // // // // // // //         icon: <Psychology />,
// // // // // // // //         path: "/gatep/talent/assistant",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Skills Passport",
// // // // // // // //         icon: <Badge />,
// // // // // // // //         path: "/gatep/talent/passport",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Mock Interview",
// // // // // // // //         icon: <Psychology />,
// // // // // // // //         path: "/gatep/talent/exam-intro",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Report",
// // // // // // // //         icon: <Psychology />,
// // // // // // // //         path: "/gatep/talent/Report",
// // // // // // // //       },
// // // // // // // //     ],
// // // // // // // //     employer: [
// // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // // // // // // //       {
// // // // // // // //         text: "Company Profile",
// // // // // // // //         icon: <PostAdd />,
// // // // // // // //         path: "/gatep/employer/company-profile",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Post Jobs",
// // // // // // // //         icon: <PostAdd />,
// // // // // // // //         path: "/gatep/employer/post-job",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Candidates",
// // // // // // // //         icon: <People />,
// // // // // // // //         path: "/gatep/employer/candidates",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Interviews",
// // // // // // // //         icon: <VideoCall />,
// // // // // // // //         path: "/gatep/employer/interviews",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Analytics",
// // // // // // // //         icon: <Analytics />,
// // // // // // // //         path: "/gatep/employer/analytics",
// // // // // // // //       },
// // // // // // // //     ],
// // // // // // // //     admin: [
// // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // // // // // // //       {
// // // // // // // //         text: "Analytics",
// // // // // // // //         icon: <Analytics />,
// // // // // // // //         path: "/gatep/admin/analytics",
// // // // // // // //       },
// // // // // // // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // // // // // // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // // // // // // //       {
// // // // // // // //         text: "Integrations",
// // // // // // // //         icon: <Settings />,
// // // // // // // //         path: "/gatep/admin/integrations",
// // // // // // // //       },
// // // // // // // //     ],
// // // // // // // //   };

// // // // // // // //   const currentMenuItems = menuItems[user?.role] || [];

// // // // // // // //   const handleNavigation = (path) => {
// // // // // // // //     navigate(path);
// // // // // // // //     if (isMobile) {
// // // // // // // //       onClose();
// // // // // // // //     }
// // // // // // // //   };

// // // // // // // //   return (
// // // // // // // //     <Drawer
// // // // // // // //       anchor="left"
// // // // // // // //       open={open}
// // // // // // // //       variant={variant}
// // // // // // // //       onClose={onClose}
// // // // // // // //       sx={{
// // // // // // // //         flexShrink: 0,
// // // // // // // //         "& .MuiDrawer-paper": {
// // // // // // // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // // // // // // //           boxSizing: "border-box",
// // // // // // // //           mt: !isMobile ? "80px" : 0,
// // // // // // // //           ml: !isMobile ? 0 : 0,
// // // // // // // //           mb: !isMobile ? 2 : 0,
// // // // // // // //           height: !isMobile ? "calc(100% - 96px)" : "100%",
// // // // // // // //           borderRadius: !isMobile ? 4 : 0,
// // // // // // // //           border: "1px solid",
// // // // // // // //           borderColor: alpha(theme.palette.divider, 0.3),
// // // // // // // //           background:
// // // // // // // //             theme.palette.mode === "light"
// // // // // // // //               ? `linear-gradient(135deg, ${alpha(
// // // // // // // //                   theme.palette.background.paper,
// // // // // // // //                   0.9
// // // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// // // // // // // //               : `linear-gradient(135deg, ${alpha(
// // // // // // // //                   theme.palette.background.paper,
// // // // // // // //                   0.9
// // // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,
// // // // // // // //           boxShadow: !isMobile
// // // // // // // //             ? `0px 8px 20px ${alpha(
// // // // // // // //                 theme.palette.common.black,
// // // // // // // //                 0.1
// // // // // // // //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// // // // // // // //             : "none",
// // // // // // // //           transition: theme.transitions.create(
// // // // // // // //             ["width", "border-radius", "box-shadow", "background"],
// // // // // // // //             {
// // // // // // // //               easing: theme.transitions.easing.easeInOut,
// // // // // // // //               duration: theme.transitions.duration.standard,
// // // // // // // //             }
// // // // // // // //           ),
// // // // // // // //         },
// // // // // // // //       }}
// // // // // // // //     >
// // // // // // // //       <Box
// // // // // // // //         sx={{
// // // // // // // //           height: "100%",
// // // // // // // //           display: "flex",
// // // // // // // //           flexDirection: "column",
// // // // // // // //         }}
// // // // // // // //       >
// // // // // // // //         <List
// // // // // // // //           sx={{
// // // // // // // //             py: 1,
// // // // // // // //             flexGrow: 1,
// // // // // // // //             overflowY: "auto",
// // // // // // // //             mt: isMobile ? "54px" : 0,
// // // // // // // //           }}
// // // // // // // //         >
// // // // // // // //           {/* Toggle button as first list item */}
// // // // // // // //           {!isMobile && (
// // // // // // // //             <ListItem disablePadding sx={{ display: "block" }}>
// // // // // // // //               <ListItemButton
// // // // // // // //                 onClick={onToggle}
// // // // // // // //                 disableRipple
// // // // // // // //                 sx={{
// // // // // // // //                   mx: 1,
// // // // // // // //                   borderRadius: 2,
// // // // // // // //                   minHeight: 48,
// // // // // // // //                   justifyContent: open ? "initial" : "center",
// // // // // // // //                   px: 2.5,
// // // // // // // //                   backgroundColor: "transparent",
// // // // // // // //                   boxShadow: "none",
// // // // // // // //                   color: theme.palette.text.secondary,
// // // // // // // //                   "&:hover": {
// // // // // // // //                     backgroundColor: "transparent",
// // // // // // // //                   },
// // // // // // // //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// // // // // // // //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// // // // // // // //                   "&:focus": { outline: "none", boxShadow: "none" },
// // // // // // // //                 }}
// // // // // // // //               >
// // // // // // // //                 <ListItemIcon
// // // // // // // //                   sx={{
// // // // // // // //                     minWidth: 0,
// // // // // // // //                     mr: open ? 2 : "auto",
// // // // // // // //                     justifyContent: "center",
// // // // // // // //                     color: "inherit",
// // // // // // // //                   }}
// // // // // // // //                 >
// // // // // // // //                   <MenuIcon />
// // // // // // // //                 </ListItemIcon>
// // // // // // // //                 <ListItemText
// // // // // // // //                   sx={{
// // // // // // // //                     opacity: open ? 1 : 0,
// // // // // // // //                     width: open ? "auto" : 0,
// // // // // // // //                     overflow: "hidden",
// // // // // // // //                     whiteSpace: "nowrap",
// // // // // // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // // // // // //                       easing: theme.transitions.easing.sharp,
// // // // // // // //                       duration: theme.transitions.duration.enteringScreen,
// // // // // // // //                     }),
// // // // // // // //                   }}
// // // // // // // //                 />
// // // // // // // //               </ListItemButton>
// // // // // // // //             </ListItem>
// // // // // // // //           )}
// // // // // // // //           {/* Menu items */}
// // // // // // // //           {currentMenuItems.map((item) => (
// // // // // // // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // // // // // // //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// // // // // // // //                 <ListItemButton
// // // // // // // //                   onClick={() => handleNavigation(item.path)}
// // // // // // // //                   selected={location.pathname === item.path}
// // // // // // // //                   sx={{
// // // // // // // //                     mx: 1,
// // // // // // // //                     borderRadius: 2,
// // // // // // // //                     minHeight: 48,
// // // // // // // //                     justifyContent: open ? "initial" : "center",
// // // // // // // //                     px: 2.5,
// // // // // // // //                     color: theme.palette.text.primary,
// // // // // // // //                     "& .MuiListItemIcon-root": {
// // // // // // // //                       color: theme.palette.text.secondary,
// // // // // // // //                     },
// // // // // // // //                     // --- CHANGE: Implemented a two-color gradient on hover. ---
// // // // // // // //                     "&:hover": {
// // // // // // // //                       backgroundImage: `linear-gradient(90deg, ${alpha(
// // // // // // // //                         accentColor,
// // // // // // // //                         0.1
// // // // // // // //                       )}, ${alpha(accentColor, 0.25)})`,
// // // // // // // //                       backgroundColor: "transparent", // Use gradient instead of solid color
// // // // // // // //                       color: accentColor,
// // // // // // // //                       transform: "scale(1.02)",
// // // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // // //                         color: accentColor,
// // // // // // // //                       },
// // // // // // // //                     },
// // // // // // // //                     "&.Mui-selected": {
// // // // // // // //                       backgroundColor: alpha(accentColor, 0.2), // Solid color for selected state
// // // // // // // //                       backgroundImage: "none", // Remove gradient when selected
// // // // // // // //                       color: accentColor,
// // // // // // // //                       fontWeight: "bold",
// // // // // // // //                       transform: "scale(1.01)",
// // // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // // //                         color: accentColor,
// // // // // // // //                       },
// // // // // // // //                       "&:hover": {
// // // // // // // //                         backgroundColor: alpha(accentColor, 0.25), // Darker solid on hover when selected
// // // // // // // //                         backgroundImage: "none", // Ensure no gradient on hover when selected
// // // // // // // //                       },
// // // // // // // //                     },
// // // // // // // //                     transition: theme.transitions.create(
// // // // // // // //                       ["background-color", "color", "transform", "background-image"],
// // // // // // // //                       {
// // // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // // //                         easing: theme.transitions.easing.easeInOut,
// // // // // // // //                       }
// // // // // // // //                     ),
// // // // // // // //                   }}
// // // // // // // //                 >
// // // // // // // //                   <ListItemIcon
// // // // // // // //                     sx={{
// // // // // // // //                       minWidth: 0,
// // // // // // // //                       mr: open ? 2 : "auto",
// // // // // // // //                       justifyContent: "center",
// // // // // // // //                       transition: theme.transitions.create("color", {
// // // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // // //                       }),
// // // // // // // //                     }}
// // // // // // // //                   >
// // // // // // // //                     {item.icon}
// // // // // // // //                   </ListItemIcon>
// // // // // // // //                   <ListItemText
// // // // // // // //                     primary={item.text}
// // // // // // // //                     sx={{
// // // // // // // //                       opacity: open ? 1 : 0,
// // // // // // // //                       width: open ? "auto" : 0,
// // // // // // // //                       overflow: "hidden",
// // // // // // // //                       whiteSpace: "nowrap",
// // // // // // // //                       transition: theme.transitions.create(
// // // // // // // //                         ["opacity", "width"],
// // // // // // // //                         {
// // // // // // // //                           easing: theme.transitions.easing.sharp,
// // // // // // // //                           duration: theme.transitions.duration.enteringScreen,
// // // // // // // //                         }
// // // // // // // //                       ),
// // // // // // // //                     }}
// // // // // // // //                   />
// // // // // // // //                 </ListItemButton>
// // // // // // // //               </Tooltip>
// // // // // // // //             </ListItem>
// // // // // // // //           ))}
// // // // // // // //         </List>
// // // // // // // //         {/* Footer */}
// // // // // // // //         <Box
// // // // // // // //           sx={{
// // // // // // // //             py: open ? 2 : 0,
// // // // // // // //             px: 2,
// // // // // // // //             mt: "auto",
// // // // // // // //             borderTop: "1px solid",
// // // // // // // //             borderColor: open
// // // // // // // //               ? alpha(theme.palette.divider, 0.3)
// // // // // // // //               : "transparent",
// // // // // // // //             opacity: open ? 1 : 0,
// // // // // // // //             maxHeight: open ? 100 : 0,
// // // // // // // //             overflow: "hidden",
// // // // // // // //             transition: theme.transitions.create(
// // // // // // // //               ["opacity", "max-height", "padding", "border-color"],
// // // // // // // //               {
// // // // // // // //                 easing: theme.transitions.easing.sharp,
// // // // // // // //                 duration: theme.transitions.duration.enteringScreen,
// // // // // // // //               }
// // // // // // // //             ),
// // // // // // // //           }}
// // // // // // // //         >
// // // // // // // //           <Typography
// // // // // // // //             variant="caption"
// // // // // // // //             color="textSecondary"
// // // // // // // //             align="center"
// // // // // // // //             display="block"
// // // // // // // //           >
// // // // // // // //             © 2025 The Data Tech Labs
// // // // // // // //           </Typography>
// // // // // // // //         </Box>
// // // // // // // //       </Box>
// // // // // // // //     </Drawer>
// // // // // // // //   );
// // // // // // // // };

// // // // // // // // export default Sidebar;

// // // // // // // // import {
// // // // // // // //   Drawer,
// // // // // // // //   List,
// // // // // // // //   ListItem,
// // // // // // // //   ListItemIcon,
// // // // // // // //   ListItemText,
// // // // // // // //   ListItemButton,
// // // // // // // //   Box,
// // // // // // // //   Typography,
// // // // // // // //   useTheme,
// // // // // // // //   useMediaQuery,
// // // // // // // //   alpha,
// // // // // // // //   Tooltip,
// // // // // // // // } from "@mui/material";
// // // // // // // // import {
// // // // // // // //   Dashboard,
// // // // // // // //   Person,
// // // // // // // //   Work,
// // // // // // // //   Psychology,
// // // // // // // //   Badge,
// // // // // // // //   PostAdd,
// // // // // // // //   People,
// // // // // // // //   VideoCall,
// // // // // // // //   Analytics,
// // // // // // // //   Map,
// // // // // // // //   Settings,
// // // // // // // //   Menu as MenuIcon,
// // // // // // // // } from "@mui/icons-material";
// // // // // // // // import { useNavigate, useLocation } from "react-router-dom";
// // // // // // // // import { useAuth } from "../../context/AuthContext";

// // // // // // // // const drawerWidth = 256;
// // // // // // // // const drawerWidthCollapsed = 64;

// // // // // // // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // // // // // // //   const { user } = useAuth();
// // // // // // // //   const navigate = useNavigate();
// // // // // // // //   const location = useLocation();
// // // // // // // //   const theme = useTheme();
// // // // // // // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // // // // // // //   const getRoleColor = (role) => {
// // // // // // // //     switch (role) {
// // // // // // // //       case "talent":
// // // // // // // //         return "#0047ab";
// // // // // // // //       case "employer":
// // // // // // // //         return "#d32f2f";
// // // // // // // //       case "admin":
// // // // // // // //         return "#fbc02d";
// // // // // // // //       default:
// // // // // // // //         return "#607d8b";
// // // // // // // //     }
// // // // // // // //   };

// // // // // // // //   const menuItems = {
// // // // // // // //     talent: [
// // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // // // // // // //       {
// // // // // // // //         text: "Profile Builder",
// // // // // // // //         icon: <Person />,
// // // // // // // //         path: "/gatep/talent/profile",
// // // // // // // //       },
// // // // // // // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // // // // // // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // // // // // // //       {
// // // // // // // //         text: "AI Assistant",
// // // // // // // //         icon: <Psychology />,
// // // // // // // //         path: "/gatep/talent/assistant",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Skills Passport",
// // // // // // // //         icon: <Badge />,
// // // // // // // //         path: "/gatep/talent/passport",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Mock Interview",
// // // // // // // //         icon: <Psychology />,
// // // // // // // //         path: "/gatep/talent/exam-intro",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Report",
// // // // // // // //         icon: <Psychology />,
// // // // // // // //         path: "/gatep/talent/Report",
// // // // // // // //       },
// // // // // // // //     ],
// // // // // // // //     employer: [
// // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // // // // // // //       {
// // // // // // // //         text: "Company Profile",
// // // // // // // //         icon: <PostAdd />,
// // // // // // // //         path: "/gatep/employer/company-profile",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Post Jobs",
// // // // // // // //         icon: <PostAdd />,
// // // // // // // //         path: "/gatep/employer/post-job",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Candidates",
// // // // // // // //         icon: <People />,
// // // // // // // //         path: "/gatep/employer/candidates",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Interviews",
// // // // // // // //         icon: <VideoCall />,
// // // // // // // //         path: "/gatep/employer/interviews",
// // // // // // // //       },
// // // // // // // //       {
// // // // // // // //         text: "Analytics",
// // // // // // // //         icon: <Analytics />,
// // // // // // // //         path: "/gatep/employer/analytics",
// // // // // // // //       },
// // // // // // // //     ],
// // // // // // // //     admin: [
// // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // // // // // // //       {
// // // // // // // //         text: "Analytics",
// // // // // // // //         icon: <Analytics />,
// // // // // // // //         path: "/gatep/admin/analytics",
// // // // // // // //       },
// // // // // // // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // // // // // // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // // // // // // //       {
// // // // // // // //         text: "Integrations",
// // // // // // // //         icon: <Settings />,
// // // // // // // //         path: "/gatep/admin/integrations",
// // // // // // // //       },
// // // // // // // //     ],
// // // // // // // //   };

// // // // // // // //   const currentMenuItems = menuItems[user?.role] || [];

// // // // // // // //   const handleNavigation = (path) => {
// // // // // // // //     navigate(path);
// // // // // // // //     if (isMobile) {
// // // // // // // //       onClose();
// // // // // // // //     }
// // // // // // // //   };

// // // // // // // //   const accentColor = getRoleColor(user?.role);

// // // // // // // //   return (
// // // // // // // //     <Drawer
// // // // // // // //       anchor="left"
// // // // // // // //       open={open}
// // // // // // // //       variant={variant}
// // // // // // // //       onClose={onClose}
// // // // // // // //       sx={{
// // // // // // // //         flexShrink: 0,
// // // // // // // //         "& .MuiDrawer-paper": {
// // // // // // // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // // // // // // //           boxSizing: "border-box",
// // // // // // // //           mt: !isMobile ? "80px" : 0,
// // // // // // // //           ml: !isMobile ? 0 : 0,
// // // // // // // //           mb: !isMobile ? 2 : 0,
// // // // // // // //           height: !isMobile ? "calc(100% - 96px)" : "100%",
// // // // // // // //           borderRadius: !isMobile ? 4 : 0,
// // // // // // // //           border: "1px solid",
// // // // // // // //           borderColor: alpha(theme.palette.divider, 0.3),
// // // // // // // //           background:
// // // // // // // //             theme.palette.mode === "light"
// // // // // // // //               ? `linear-gradient(135deg, ${alpha(
// // // // // // // //                   theme.palette.background.paper,
// // // // // // // //                   0.9
// // // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// // // // // // // //               : `linear-gradient(135deg, ${alpha(
// // // // // // // //                   theme.palette.background.paper,
// // // // // // // //                   0.9
// // // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,
// // // // // // // //           boxShadow: !isMobile
// // // // // // // //             ? `0px 8px 20px ${alpha(
// // // // // // // //                 theme.palette.common.black,
// // // // // // // //                 0.1
// // // // // // // //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// // // // // // // //             : "none",
// // // // // // // //           transition: theme.transitions.create(
// // // // // // // //             ["width", "border-radius", "box-shadow", "background"],
// // // // // // // //             {
// // // // // // // //               easing: theme.transitions.easing.easeInOut,
// // // // // // // //               duration: theme.transitions.duration.standard,
// // // // // // // //             }
// // // // // // // //           ),
// // // // // // // //         },
// // // // // // // //       }}
// // // // // // // //     >
// // // // // // // //       <Box
// // // // // // // //         sx={{
// // // // // // // //           height: "100%",
// // // // // // // //           display: "flex",
// // // // // // // //           flexDirection: "column",
// // // // // // // //         }}
// // // // // // // //       >
// // // // // // // //         <List
// // // // // // // //           sx={{
// // // // // // // //             py: 1,
// // // // // // // //             flexGrow: 1,
// // // // // // // //             overflowY: "auto",
// // // // // // // //             mt: isMobile ? "54px" : 0,
// // // // // // // //           }}
// // // // // // // //         >
// // // // // // // //           {/* Toggle button as first list item */}
// // // // // // // //           {!isMobile && (
// // // // // // // //             <ListItem disablePadding sx={{ display: "block" }}>
// // // // // // // //               <ListItemButton
// // // // // // // //                 onClick={onToggle}
// // // // // // // //                 disableRipple // Disables the click visual effect
// // // // // // // //                 sx={{
// // // // // // // //                   mx: 1,
// // // // // // // //                   borderRadius: 2,
// // // // // // // //                   minHeight: 48,
// // // // // // // //                   justifyContent: open ? "initial" : "center",
// // // // // // // //                   px: 2.5,
// // // // // // // //                   backgroundColor: "transparent", // No background appearance
// // // // // // // //                   boxShadow: "none", // No shadow
// // // // // // // //                   color: theme.palette.text.secondary,
// // // // // // // //                   // Disable hover and active state visual changes
// // // // // // // //                   "&:hover": {
// // // // // // // //                     backgroundColor: "transparent",
// // // // // // // //                   },
// // // // // // // //                   // Keep focus styles clean for accessibility
// // // // // // // //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// // // // // // // //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// // // // // // // //                   "&:focus": { outline: "none", boxShadow: "none" },
// // // // // // // //                 }}
// // // // // // // //               >
// // // // // // // //                 <ListItemIcon
// // // // // // // //                   sx={{
// // // // // // // //                     minWidth: 0,
// // // // // // // //                     mr: open ? 2 : "auto",
// // // // // // // //                     justifyContent: "center",
// // // // // // // //                     color: "inherit", // Inherits the ListItemButton's color
// // // // // // // //                   }}
// // // // // // // //                 >
// // // // // // // //                   <MenuIcon />
// // // // // // // //                 </ListItemIcon>
// // // // // // // //                 <ListItemText
// // // // // // // //                   sx={{
// // // // // // // //                     opacity: open ? 1 : 0,
// // // // // // // //                     width: open ? "auto" : 0,
// // // // // // // //                     overflow: "hidden",
// // // // // // // //                     whiteSpace: "nowrap",
// // // // // // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // // // // // //                       easing: theme.transitions.easing.sharp,
// // // // // // // //                       duration: theme.transitions.duration.enteringScreen,
// // // // // // // //                     }),
// // // // // // // //                   }}
// // // // // // // //                 />
// // // // // // // //               </ListItemButton>
// // // // // // // //             </ListItem>
// // // // // // // //           )}
// // // // // // // //           {/* Menu items */}
// // // // // // // //           {currentMenuItems.map((item) => (
// // // // // // // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // // // // // // //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// // // // // // // //                 <ListItemButton
// // // // // // // //                   onClick={() => handleNavigation(item.path)}
// // // // // // // //                   selected={location.pathname === item.path}
// // // // // // // //                   sx={{
// // // // // // // //                     mx: 1,
// // // // // // // //                     borderRadius: 2,
// // // // // // // //                     minHeight: 48,
// // // // // // // //                     justifyContent: open ? "initial" : "center",
// // // // // // // //                     px: 2.5,
// // // // // // // //                     color: theme.palette.text.primary,
// // // // // // // //                     "& .MuiListItemIcon-root": {
// // // // // // // //                       color: theme.palette.text.secondary,
// // // // // // // //                     },
// // // // // // // //                     "&:hover": {
// // // // // // // //                       backgroundColor: alpha(accentColor, 0.1),
// // // // // // // //                       color: accentColor,
// // // // // // // //                       transform: "scale(1.02)",
// // // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // // //                         color: accentColor,
// // // // // // // //                       },
// // // // // // // //                     },
// // // // // // // //                     "&.Mui-selected": {
// // // // // // // //                       backgroundColor: alpha(accentColor, 0.2),
// // // // // // // //                       color: accentColor,
// // // // // // // //                       fontWeight: "bold",
// // // // // // // //                       transform: "scale(1.01)",
// // // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // // //                         color: accentColor,
// // // // // // // //                       },
// // // // // // // //                       "&:hover": {
// // // // // // // //                         backgroundColor: alpha(accentColor, 0.25),
// // // // // // // //                         color: accentColor,
// // // // // // // //                         "& .MuiListItemIcon-root": {
// // // // // // // //                           color: accentColor,
// // // // // // // //                         },
// // // // // // // //                       },
// // // // // // // //                     },
// // // // // // // //                     transition: theme.transitions.create(
// // // // // // // //                       ["background-color", "color", "transform", "box-shadow"],
// // // // // // // //                       {
// // // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // // //                         easing: theme.transitions.easing.easeInOut,
// // // // // // // //                       }
// // // // // // // //                     ),
// // // // // // // //                   }}
// // // // // // // //                 >
// // // // // // // //                   <ListItemIcon
// // // // // // // //                     sx={{
// // // // // // // //                       minWidth: 0,
// // // // // // // //                       mr: open ? 2 : "auto",
// // // // // // // //                       justifyContent: "center",
// // // // // // // //                       transition: theme.transitions.create("color", {
// // // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // // //                       }),
// // // // // // // //                     }}
// // // // // // // //                   >
// // // // // // // //                     {item.icon}
// // // // // // // //                   </ListItemIcon>
// // // // // // // //                   <ListItemText
// // // // // // // //                     primary={item.text}
// // // // // // // //                     sx={{
// // // // // // // //                       opacity: open ? 1 : 0,
// // // // // // // //                       width: open ? "auto" : 0,
// // // // // // // //                       overflow: "hidden",
// // // // // // // //                       whiteSpace: "nowrap",
// // // // // // // //                       transition: theme.transitions.create(
// // // // // // // //                         ["opacity", "width"],
// // // // // // // //                         {
// // // // // // // //                           easing: theme.transitions.easing.sharp,
// // // // // // // //                           duration: theme.transitions.duration.enteringScreen,
// // // // // // // //                         }
// // // // // // // //                       ),
// // // // // // // //                     }}
// // // // // // // //                   />
// // // // // // // //                 </ListItemButton>
// // // // // // // //               </Tooltip>
// // // // // // // //             </ListItem>
// // // // // // // //           ))}
// // // // // // // //         </List>
// // // // // // // //         {/* Footer */}
// // // // // // // //         <Box
// // // // // // // //           sx={{
// // // // // // // //             py: open ? 2 : 0,
// // // // // // // //             px: 2,
// // // // // // // //             mt: "auto",
// // // // // // // //             borderTop: "1px solid",
// // // // // // // //             borderColor: open
// // // // // // // //               ? alpha(theme.palette.divider, 0.3)
// // // // // // // //               : "transparent",
// // // // // // // //             opacity: open ? 1 : 0,
// // // // // // // //             maxHeight: open ? 100 : 0,
// // // // // // // //             overflow: "hidden",
// // // // // // // //             transition: theme.transitions.create(
// // // // // // // //               ["opacity", "max-height", "padding", "border-color"],
// // // // // // // //               {
// // // // // // // //                 easing: theme.transitions.easing.sharp,
// // // // // // // //                 duration: theme.transitions.duration.enteringScreen,
// // // // // // // //               }
// // // // // // // //             ),
// // // // // // // //           }}
// // // // // // // //         >
// // // // // // // //           <Typography
// // // // // // // //             variant="caption"
// // // // // // // //             color="textSecondary"
// // // // // // // //             align="center"
// // // // // // // //             display="block"
// // // // // // // //           >
// // // // // // // //             © 2025 The Data Tech Labs
// // // // // // // //           </Typography>
// // // // // // // //         </Box>
// // // // // // // //       </Box>
// // // // // // // //     </Drawer>
// // // // // // // //   );
// // // // // // // // };

// // // // // // // // export default Sidebar;

// // // // // // // // // import {
// // // // // // // // //   Drawer,
// // // // // // // // //   List,
// // // // // // // // //   ListItem,
// // // // // // // // //   ListItemIcon,
// // // // // // // // //   ListItemText,
// // // // // // // // //   ListItemButton,
// // // // // // // // //   Box,
// // // // // // // // //   Typography,
// // // // // // // // //   useTheme,
// // // // // // // // //   useMediaQuery,
// // // // // // // // //   alpha,
// // // // // // // // //   Tooltip,
// // // // // // // // // } from "@mui/material";
// // // // // // // // // import {
// // // // // // // // //   Dashboard,
// // // // // // // // //   Person,
// // // // // // // // //   Work,
// // // // // // // // //   Psychology,
// // // // // // // // //   Badge,
// // // // // // // // //   PostAdd,
// // // // // // // // //   People,
// // // // // // // // //   VideoCall,
// // // // // // // // //   Analytics,
// // // // // // // // //   Map,
// // // // // // // // //   Settings,
// // // // // // // // //   Menu as MenuIcon,
// // // // // // // // // } from "@mui/icons-material";
// // // // // // // // // import { useNavigate, useLocation } from "react-router-dom";
// // // // // // // // // import { useAuth } from "../../context/AuthContext";

// // // // // // // // // const drawerWidth = 256;
// // // // // // // // // const drawerWidthCollapsed = 64;

// // // // // // // // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // // // // // // // //   const { user } = useAuth();
// // // // // // // // //   const navigate = useNavigate();
// // // // // // // // //   const location = useLocation();
// // // // // // // // //   const theme = useTheme();
// // // // // // // // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // // // // // // // //   const getRoleColor = (role) => {
// // // // // // // // //     switch (role) {
// // // // // // // // //       case "talent":
// // // // // // // // //         return "#0047ab";
// // // // // // // // //       case "employer":
// // // // // // // // //         return "#d32f2f";
// // // // // // // // //       case "admin":
// // // // // // // // //         return "#fbc02d";
// // // // // // // // //       default:
// // // // // // // // //         return "#607d8b";
// // // // // // // // //     }
// // // // // // // // //   };

// // // // // // // // //   const menuItems = {
// // // // // // // // //     talent: [
// // // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // // // // // // // //       {
// // // // // // // // //         text: "Profile Builder",
// // // // // // // // //         icon: <Person />,
// // // // // // // // //         path: "/gatep/talent/profile",
// // // // // // // // //       },
// // // // // // // // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // // // // // // // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // // // // // // // //       {
// // // // // // // // //         text: "AI Assistant",
// // // // // // // // //         icon: <Psychology />,
// // // // // // // // //         path: "/gatep/talent/assistant",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Skills Passport",
// // // // // // // // //         icon: <Badge />,
// // // // // // // // //         path: "/gatep/talent/passport",
// // // // // // // // //       },
// // // // // // // // //     ],
// // // // // // // // //     employer: [
// // // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // // // // // // // //       {
// // // // // // // // //         text: "Company Profile",
// // // // // // // // //         icon: <PostAdd />,
// // // // // // // // //         path: "/gatep/employer/company-profile",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Post Jobs",
// // // // // // // // //         icon: <PostAdd />,
// // // // // // // // //         path: "/gatep/employer/post-job",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Candidates",
// // // // // // // // //         icon: <People />,
// // // // // // // // //         path: "/gatep/employer/candidates",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Interviews",
// // // // // // // // //         icon: <VideoCall />,
// // // // // // // // //         path: "/gatep/employer/interviews",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Analytics",
// // // // // // // // //         icon: <Analytics />,
// // // // // // // // //         path: "/gatep/employer/analytics",
// // // // // // // // //       },
// // // // // // // // //     ],
// // // // // // // // //     admin: [
// // // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // // // // // // // //       {
// // // // // // // // //         text: "Analytics",
// // // // // // // // //         icon: <Analytics />,
// // // // // // // // //         path: "/gatep/admin/analytics",
// // // // // // // // //       },
// // // // // // // // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // // // // // // // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // // // // // // // //       {
// // // // // // // // //         text: "Integrations",
// // // // // // // // //         icon: <Settings />,
// // // // // // // // //         path: "/gatep/admin/integrations",
// // // // // // // // //       },
// // // // // // // // //     ],
// // // // // // // // //   };

// // // // // // // // //   const currentMenuItems = menuItems[user?.role] || [];

// // // // // // // // //   const handleNavigation = (path) => {
// // // // // // // // //     navigate(path);
// // // // // // // // //     if (isMobile) {
// // // // // // // // //       onClose();
// // // // // // // // //     }
// // // // // // // // //   };

// // // // // // // // //   const accentColor = getRoleColor(user?.role);

// // // // // // // // //   return (
// // // // // // // // //     <Drawer
// // // // // // // // //       anchor="left"
// // // // // // // // //       open={open}
// // // // // // // // //       variant={variant}
// // // // // // // // //       onClose={onClose}
// // // // // // // // //       sx={{
// // // // // // // // //         flexShrink: 0,
// // // // // // // // //         "& .MuiDrawer-paper": {
// // // // // // // // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // // // // // // // //           boxSizing: "border-box",
// // // // // // // // //           mt: !isMobile ? "80px" : 0,
// // // // // // // // //           ml: !isMobile ? 0 : 0,
// // // // // // // // //           mb: !isMobile ? 2 : 0,
// // // // // // // // //           height: !isMobile ? "calc(100% - 96px)" : "100%",
// // // // // // // // //           borderRadius: !isMobile ? 4 : 0,
// // // // // // // // //           border: "1px solid",
// // // // // // // // //           borderColor: alpha(theme.palette.divider, 0.3),
// // // // // // // // //           background:
// // // // // // // // //             theme.palette.mode === "light"
// // // // // // // // //               ? `linear-gradient(135deg, ${alpha(
// // // // // // // // //                   theme.palette.background.paper,
// // // // // // // // //                   0.9
// // // // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// // // // // // // // //               : `linear-gradient(135deg, ${alpha(
// // // // // // // // //                   theme.palette.background.paper,
// // // // // // // // //                   0.9
// // // // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,
// // // // // // // // //           boxShadow: !isMobile
// // // // // // // // //             ? `0px 8px 20px ${alpha(
// // // // // // // // //                 theme.palette.common.black,
// // // // // // // // //                 0.1
// // // // // // // // //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// // // // // // // // //             : "none",
// // // // // // // // //           transition: theme.transitions.create(
// // // // // // // // //             ["width", "border-radius", "box-shadow", "background"],
// // // // // // // // //             {
// // // // // // // // //               easing: theme.transitions.easing.easeInOut,
// // // // // // // // //               duration: theme.transitions.duration.standard,
// // // // // // // // //             }
// // // // // // // // //           ),
// // // // // // // // //         },
// // // // // // // // //       }}
// // // // // // // // //     >
// // // // // // // // //       <Box
// // // // // // // // //         sx={{
// // // // // // // // //           height: "100%",
// // // // // // // // //           display: "flex",
// // // // // // // // //           flexDirection: "column",
// // // // // // // // //         }}
// // // // // // // // //       >
// // // // // // // // //         <List
// // // // // // // // //           sx={{
// // // // // // // // //             py: 1,
// // // // // // // // //             flexGrow: 1,
// // // // // // // // //             overflowY: "auto",
// // // // // // // // //             mt: isMobile ? "54px" : 0,
// // // // // // // // //           }}
// // // // // // // // //         >
// // // // // // // // //           {/* Toggle button as first list item */}
// // // // // // // // //           {!isMobile && (
// // // // // // // // //             <ListItem disablePadding sx={{ display: "block" }}>
// // // // // // // // //               <ListItemButton
// // // // // // // // //                 onClick={onToggle}
// // // // // // // // //                 disableRipple // Disables the click visual effect
// // // // // // // // //                 sx={{
// // // // // // // // //                   mx: 1,
// // // // // // // // //                   borderRadius: 2,
// // // // // // // // //                   minHeight: 48,
// // // // // // // // //                   justifyContent: open ? "initial" : "center",
// // // // // // // // //                   px: 2.5,
// // // // // // // // //                   backgroundColor: "transparent", // No background appearance
// // // // // // // // //                   boxShadow: "none", // No shadow
// // // // // // // // //                   color: theme.palette.text.secondary,
// // // // // // // // //                   // Disable hover and active state visual changes
// // // // // // // // //                   "&:hover": {
// // // // // // // // //                     backgroundColor: "transparent",
// // // // // // // // //                   },
// // // // // // // // //                   // Keep focus styles clean for accessibility
// // // // // // // // //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// // // // // // // // //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// // // // // // // // //                   "&:focus": { outline: "none", boxShadow: "none" },
// // // // // // // // //                 }}
// // // // // // // // //               >
// // // // // // // // //                 <ListItemIcon
// // // // // // // // //                   sx={{
// // // // // // // // //                     minWidth: 0,
// // // // // // // // //                     mr: open ? 2 : "auto",
// // // // // // // // //                     justifyContent: "center",
// // // // // // // // //                     color: "inherit", // Inherits the ListItemButton's color
// // // // // // // // //                   }}
// // // // // // // // //                 >
// // // // // // // // //                   <MenuIcon />
// // // // // // // // //                 </ListItemIcon>
// // // // // // // // //                 <ListItemText
// // // // // // // // //                   sx={{
// // // // // // // // //                     opacity: open ? 1 : 0,
// // // // // // // // //                     width: open ? "auto" : 0,
// // // // // // // // //                     overflow: "hidden",
// // // // // // // // //                     whiteSpace: "nowrap",
// // // // // // // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // // // // // // //                       easing: theme.transitions.easing.sharp,
// // // // // // // // //                       duration: theme.transitions.duration.enteringScreen,
// // // // // // // // //                     }),
// // // // // // // // //                   }}
// // // // // // // // //                 />
// // // // // // // // //               </ListItemButton>
// // // // // // // // //             </ListItem>
// // // // // // // // //           )}
// // // // // // // // //           {/* Menu items */}
// // // // // // // // //           {currentMenuItems.map((item) => (
// // // // // // // // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // // // // // // // //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// // // // // // // // //                 <ListItemButton
// // // // // // // // //                   onClick={() => handleNavigation(item.path)}
// // // // // // // // //                   selected={location.pathname === item.path}
// // // // // // // // //                   sx={{
// // // // // // // // //                     mx: 1,
// // // // // // // // //                     borderRadius: 2,
// // // // // // // // //                     minHeight: 48,
// // // // // // // // //                     justifyContent: open ? "initial" : "center",
// // // // // // // // //                     px: 2.5,
// // // // // // // // //                     color: theme.palette.text.primary,
// // // // // // // // //                     "& .MuiListItemIcon-root": {
// // // // // // // // //                       color: theme.palette.text.secondary,
// // // // // // // // //                     },
// // // // // // // // //                     "&:hover": {
// // // // // // // // //                       backgroundColor: alpha(accentColor, 0.1),
// // // // // // // // //                       color: accentColor,
// // // // // // // // //                       transform: "scale(1.02)",
// // // // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // // // //                         color: accentColor,
// // // // // // // // //                       },
// // // // // // // // //                     },
// // // // // // // // //                     "&.Mui-selected": {
// // // // // // // // //                       backgroundColor: alpha(accentColor, 0.2),
// // // // // // // // //                       color: accentColor,
// // // // // // // // //                       fontWeight: "bold",
// // // // // // // // //                       transform: "scale(1.01)",
// // // // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // // // //                         color: accentColor,
// // // // // // // // //                       },
// // // // // // // // //                       "&:hover": {
// // // // // // // // //                         backgroundColor: alpha(accentColor, 0.25),
// // // // // // // // //                         color: accentColor,
// // // // // // // // //                         "& .MuiListItemIcon-root": {
// // // // // // // // //                           color: accentColor,
// // // // // // // // //                         },
// // // // // // // // //                       },
// // // // // // // // //                     },
// // // // // // // // //                     transition: theme.transitions.create(
// // // // // // // // //                       ["background-color", "color", "transform", "box-shadow"],
// // // // // // // // //                       {
// // // // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // // // //                         easing: theme.transitions.easing.easeInOut,
// // // // // // // // //                       }
// // // // // // // // //                     ),
// // // // // // // // //                   }}
// // // // // // // // //                 >
// // // // // // // // //                   <ListItemIcon
// // // // // // // // //                     sx={{
// // // // // // // // //                       minWidth: 0,
// // // // // // // // //                       mr: open ? 2 : "auto",
// // // // // // // // //                       justifyContent: "center",
// // // // // // // // //                       transition: theme.transitions.create("color", {
// // // // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // // // //                       }),
// // // // // // // // //                     }}
// // // // // // // // //                   >
// // // // // // // // //                     {item.icon}
// // // // // // // // //                   </ListItemIcon>
// // // // // // // // //                   <ListItemText
// // // // // // // // //                     primary={item.text}
// // // // // // // // //                     sx={{
// // // // // // // // //                       opacity: open ? 1 : 0,
// // // // // // // // //                       width: open ? "auto" : 0,
// // // // // // // // //                       overflow: "hidden",
// // // // // // // // //                       whiteSpace: "nowrap",
// // // // // // // // //                       transition: theme.transitions.create(
// // // // // // // // //                         ["opacity", "width"],
// // // // // // // // //                         {
// // // // // // // // //                           easing: theme.transitions.easing.sharp,
// // // // // // // // //                           duration: theme.transitions.duration.enteringScreen,
// // // // // // // // //                         }
// // // // // // // // //                       ),
// // // // // // // // //                     }}
// // // // // // // // //                   />
// // // // // // // // //                 </ListItemButton>
// // // // // // // // //               </Tooltip>
// // // // // // // // //             </ListItem>
// // // // // // // // //           ))}
// // // // // // // // //         </List>
// // // // // // // // //         {/* Footer */}
// // // // // // // // //         <Box
// // // // // // // // //           sx={{
// // // // // // // // //             py: open ? 2 : 0,
// // // // // // // // //             px: 2,
// // // // // // // // //             mt: "auto",
// // // // // // // // //             borderTop: "1px solid",
// // // // // // // // //             borderColor: open
// // // // // // // // //               ? alpha(theme.palette.divider, 0.3)
// // // // // // // // //               : "transparent",
// // // // // // // // //             opacity: open ? 1 : 0,
// // // // // // // // //             maxHeight: open ? 100 : 0,
// // // // // // // // //             overflow: "hidden",
// // // // // // // // //             transition: theme.transitions.create(
// // // // // // // // //               ["opacity", "max-height", "padding", "border-color"],
// // // // // // // // //               {
// // // // // // // // //                 easing: theme.transitions.easing.sharp,
// // // // // // // // //                 duration: theme.transitions.duration.enteringScreen,
// // // // // // // // //               }
// // // // // // // // //             ),
// // // // // // // // //           }}
// // // // // // // // //         >
// // // // // // // // //           <Typography
// // // // // // // // //             variant="caption"
// // // // // // // // //             color="textSecondary"
// // // // // // // // //             align="center"
// // // // // // // // //             display="block"
// // // // // // // // //           >
// // // // // // // // //             © 2025 The Data Tech Labs
// // // // // // // // //           </Typography>
// // // // // // // // //         </Box>
// // // // // // // // //       </Box>
// // // // // // // // //     </Drawer>
// // // // // // // // //   );
// // // // // // // // // };

// // // // // // // // // export default Sidebar;
// // // // // // // // // // import {
// // // // // // // // // //   Drawer,
// // // // // // // // // //   List,
// // // // // // // // // //   ListItem,
// // // // // // // // // //   ListItemIcon,
// // // // // // // // // //   ListItemText,
// // // // // // // // // //   ListItemButton,
// // // // // // // // // //   Box,
// // // // // // // // // //   Typography,
// // // // // // // // // //   IconButton,
// // // // // // // // // //   useTheme,
// // // // // // // // // //   useMediaQuery,
// // // // // // // // // // } from "@mui/material";
// // // // // // // // // // import {
// // // // // // // // // //   Dashboard,
// // // // // // // // // //   Person,
// // // // // // // // // //   Work,
// // // // // // // // // //   Psychology,
// // // // // // // // // //   Badge,
// // // // // // // // // //   PostAdd,
// // // // // // // // // //   People,
// // // // // // // // // //   VideoCall,
// // // // // // // // // //   Analytics,
// // // // // // // // // //   Map,
// // // // // // // // // //   Settings,
// // // // // // // // // //   Menu as MenuIcon,
// // // // // // // // // // } from "@mui/icons-material";
// // // // // // // // // // import { useNavigate, useLocation } from "react-router-dom";
// // // // // // // // // // import { useAuth } from "../../context/AuthContext";

// // // // // // // // // // const drawerWidth = 256;
// // // // // // // // // // const drawerWidthCollapsed = 64;

// // // // // // // // // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // // // // // // // // //   const { user } = useAuth();
// // // // // // // // // //   const navigate = useNavigate();
// // // // // // // // // //   const location = useLocation();
// // // // // // // // // //   const theme = useTheme();
// // // // // // // // // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // // // // // // // // //   const menuItems = {
// // // // // // // // // //     talent: [
// // // // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // // // // // // // // //       {
// // // // // // // // // //         text: "Profile Builder",
// // // // // // // // // //         icon: <Person />,
// // // // // // // // // //         path: "/gatep/talent/profile",
// // // // // // // // // //       },
// // // // // // // // // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // // // // // // // // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // // // // // // // // //       {
// // // // // // // // // //         text: "AI Assistant",
// // // // // // // // // //         icon: <Psychology />,
// // // // // // // // // //         path: "/gatep/talent/assistant",
// // // // // // // // // //       },
// // // // // // // // // //       {
// // // // // // // // // //         text: "Skills Passport",
// // // // // // // // // //         icon: <Badge />,
// // // // // // // // // //         path: "/gatep/talent/passport",
// // // // // // // // // //       },
// // // // // // // // // //     ],
// // // // // // // // // //     employer: [
// // // // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // // // // // // // // //       {
// // // // // // // // // //         text: "Company Profile",
// // // // // // // // // //         icon: <PostAdd />,
// // // // // // // // // //         path: "/gatep/employer/company-profile",
// // // // // // // // // //       },
// // // // // // // // // //       {
// // // // // // // // // //         text: "Post Jobs",
// // // // // // // // // //         icon: <PostAdd />,
// // // // // // // // // //         path: "/gatep/employer/post-job",
// // // // // // // // // //       },
// // // // // // // // // //       {
// // // // // // // // // //         text: "Candidates",
// // // // // // // // // //         icon: <People />,
// // // // // // // // // //         path: "/gatep/employer/candidates",
// // // // // // // // // //       },
// // // // // // // // // //       {
// // // // // // // // // //         text: "Interviews",
// // // // // // // // // //         icon: <VideoCall />,
// // // // // // // // // //         path: "/gatep/employer/interviews",
// // // // // // // // // //       },
// // // // // // // // // //       {
// // // // // // // // // //         text: "Analytics",
// // // // // // // // // //         icon: <Analytics />,
// // // // // // // // // //         path: "/gatep/employer/analytics",
// // // // // // // // // //       },
// // // // // // // // // //     ],
// // // // // // // // // //     admin: [
// // // // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // // // // // // // // //       {
// // // // // // // // // //         text: "Analytics",
// // // // // // // // // //         icon: <Analytics />,
// // // // // // // // // //         path: "/gatep/admin/analytics",
// // // // // // // // // //       },
// // // // // // // // // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // // // // // // // // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // // // // // // // // //       {
// // // // // // // // // //         text: "Integrations",
// // // // // // // // // //         icon: <Settings />,
// // // // // // // // // //         path: "/gatep/admin/integrations",
// // // // // // // // // //       },
// // // // // // // // // //     ],
// // // // // // // // // //   };

// // // // // // // // // //   const currentMenuItems = menuItems[user?.role] || [];

// // // // // // // // // //   const getRoleColor = (role) => {
// // // // // // // // // //     return theme.palette.roles[role] || theme.palette.roles.default;
// // // // // // // // // //   };

// // // // // // // // // //   const handleNavigation = (path) => {
// // // // // // // // // //     navigate(path);
// // // // // // // // // //     if (isMobile) {
// // // // // // // // // //       onClose();
// // // // // // // // // //     }
// // // // // // // // // //   };

// // // // // // // // // //   return (
// // // // // // // // // //     <Drawer
// // // // // // // // // //       anchor="left"
// // // // // // // // // //       open={open}
// // // // // // // // // //       variant={variant}
// // // // // // // // // //       onClose={onClose}
// // // // // // // // // //       sx={{
// // // // // // // // // //         flexShrink: 0,
// // // // // // // // // //         "& .MuiDrawer-paper": {
// // // // // // // // // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // // // // // // // // //           boxSizing: "border-box",
// // // // // // // // // //           mt: !isMobile ? "80px" : 0,
// // // // // // // // // //           ml: !isMobile ? 0 : 0,
// // // // // // // // // //           mb: !isMobile ? 2 : 0,
// // // // // // // // // //           height: !isMobile ? "calc(100% - 96px)" : "100%",
// // // // // // // // // //           borderRadius: !isMobile ? 4 : 0,
// // // // // // // // // //           border: "none",
// // // // // // // // // //           backgroundColor: theme.palette.background.paper,
// // // // // // // // // //           boxShadow: !isMobile ? theme.shadows[4] : "none",
// // // // // // // // // //           transition: theme.transitions.create(["width", "border-radius"], {
// // // // // // // // // //             easing: theme.transitions.easing.easeInOut,
// // // // // // // // // //             duration: theme.transitions.duration.standard,
// // // // // // // // // //           }),
// // // // // // // // // //         },
// // // // // // // // // //       }}
// // // // // // // // // //     >
// // // // // // // // // //       <Box
// // // // // // // // // //         sx={{
// // // // // // // // // //           height: "100%",
// // // // // // // // // //           display: "flex",
// // // // // // // // // //           flexDirection: "column",
// // // // // // // // // //         }}
// // // // // // // // // //       >
// // // // // // // // // //         <List
// // // // // // // // // //           sx={{
// // // // // // // // // //             py: 1,
// // // // // // // // // //             flexGrow: 1,
// // // // // // // // // //             overflowY: "auto",
// // // // // // // // // //             mt: isMobile ? "54px" : 0,
// // // // // // // // // //           }}
// // // // // // // // // //         >
// // // // // // // // // //           {/* Toggle button as first list item */}
// // // // // // // // // //           {!isMobile && (
// // // // // // // // // //             <ListItem disablePadding sx={{ display: "block" }}>
// // // // // // // // // //               <ListItemButton
// // // // // // // // // //                 onClick={onToggle}
// // // // // // // // // //                 sx={{
// // // // // // // // // //                   mx: 1,
// // // // // // // // // //                   borderRadius: 2,
// // // // // // // // // //                   minHeight: 48,
// // // // // // // // // //                   justifyContent: open ? "initial" : "center",
// // // // // // // // // //                   px: 2.5,
// // // // // // // // // //                   "&:hover": {
// // // // // // // // // //                     backgroundColor: "transparent",
// // // // // // // // // //                   },
// // // // // // // // // //                   "&:focus-visible": {
// // // // // // // // // //                     outline: "none",
// // // // // // // // // //                     boxShadow: "none",
// // // // // // // // // //                   },
// // // // // // // // // //                   "&.Mui-focusVisible": {
// // // // // // // // // //                     outline: "none",
// // // // // // // // // //                     boxShadow: "none",
// // // // // // // // // //                   },
// // // // // // // // // //                   "&:focus": {
// // // // // // // // // //                     outline: "none",
// // // // // // // // // //                     boxShadow: "none",
// // // // // // // // // //                   },
// // // // // // // // // //                 }}
// // // // // // // // // //               >
// // // // // // // // // //                 <ListItemIcon
// // // // // // // // // //                   sx={{
// // // // // // // // // //                     minWidth: 0,
// // // // // // // // // //                     mr: open ? 2 : "auto",
// // // // // // // // // //                     justifyContent: "center",
// // // // // // // // // //                   }}
// // // // // // // // // //                 >
// // // // // // // // // //                   <MenuIcon />
// // // // // // // // // //                 </ListItemIcon>
// // // // // // // // // //                 <ListItemText
// // // // // // // // // //                   //primary="Menu"
// // // // // // // // // //                   sx={{
// // // // // // // // // //                     opacity: open ? 1 : 0,
// // // // // // // // // //                     width: open ? "auto" : 0,
// // // // // // // // // //                     overflow: "hidden",
// // // // // // // // // //                     whiteSpace: "nowrap",
// // // // // // // // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // // // // // // // //                       easing: theme.transitions.easing.sharp,
// // // // // // // // // //                       duration: theme.transitions.duration.enteringScreen,
// // // // // // // // // //                     }),
// // // // // // // // // //                   }}
// // // // // // // // // //                 />
// // // // // // // // // //               </ListItemButton>
// // // // // // // // // //             </ListItem>
// // // // // // // // // //           )}

// // // // // // // // // //           {/* Menu items */}
// // // // // // // // // //           {currentMenuItems.map((item) => (
// // // // // // // // // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // // // // // // // // //               <ListItemButton
// // // // // // // // // //                 onClick={() => handleNavigation(item.path)}
// // // // // // // // // //                 selected={location.pathname === item.path}
// // // // // // // // // //                 sx={{
// // // // // // // // // //                   mx: 1,
// // // // // // // // // //                   borderRadius: 2,
// // // // // // // // // //                   minHeight: 48,
// // // // // // // // // //                   justifyContent: open ? "initial" : "center",
// // // // // // // // // //                   px: 2.5,
// // // // // // // // // //                   "&.Mui-selected": {
// // // // // // // // // //                     backgroundColor: `${getRoleColor(user?.role)}20`,
// // // // // // // // // //                     color:
// // // // // // // // // //                       theme.palette.mode === "light"
// // // // // // // // // //                         ? getRoleColor(user?.role)
// // // // // // // // // //                         : theme.palette.text.primary,
// // // // // // // // // //                     "& .MuiListItemIcon-root": {
// // // // // // // // // //                       color:
// // // // // // // // // //                         theme.palette.mode === "light"
// // // // // // // // // //                           ? getRoleColor(user?.role)
// // // // // // // // // //                           : theme.palette.text.primary,
// // // // // // // // // //                     },
// // // // // // // // // //                   },
// // // // // // // // // //                   "&:hover": {
// // // // // // // // // //                     backgroundColor:
// // // // // // // // // //                       location.pathname !== item.path &&
// // // // // // // // // //                       `${getRoleColor(user?.role)}10`,
// // // // // // // // // //                   },
// // // // // // // // // //                 }}
// // // // // // // // // //               >
// // // // // // // // // //                 <ListItemIcon
// // // // // // // // // //                   sx={{
// // // // // // // // // //                     minWidth: 0,
// // // // // // // // // //                     mr: open ? 2 : "auto",
// // // // // // // // // //                     justifyContent: "center",
// // // // // // // // // //                   }}
// // // // // // // // // //                 >
// // // // // // // // // //                   {item.icon}
// // // // // // // // // //                 </ListItemIcon>
// // // // // // // // // //                 <ListItemText
// // // // // // // // // //                   primary={item.text}
// // // // // // // // // //                   sx={{
// // // // // // // // // //                     opacity: open ? 1 : 0,
// // // // // // // // // //                     width: open ? "auto" : 0,
// // // // // // // // // //                     overflow: "hidden",
// // // // // // // // // //                     whiteSpace: "nowrap",
// // // // // // // // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // // // // // // // //                       easing: theme.transitions.easing.sharp,
// // // // // // // // // //                       duration: theme.transitions.duration.enteringScreen,
// // // // // // // // // //                     }),
// // // // // // // // // //                   }}
// // // // // // // // // //                 />
// // // // // // // // // //               </ListItemButton>
// // // // // // // // // //             </ListItem>
// // // // // // // // // //           ))}
// // // // // // // // // //         </List>

// // // // // // // // // //         {/* Footer */}
// // // // // // // // // //         <Box
// // // // // // // // // //           sx={{
// // // // // // // // // //             py: open ? 2 : 0,
// // // // // // // // // //             px: 2,
// // // // // // // // // //             mt: "auto",
// // // // // // // // // //             borderTop: "1px solid",
// // // // // // // // // //             borderColor: open ? theme.palette.divider : "transparent",
// // // // // // // // // //             opacity: open ? 1 : 0,
// // // // // // // // // //             maxHeight: open ? 100 : 0,
// // // // // // // // // //             overflow: "hidden",
// // // // // // // // // //             transition: theme.transitions.create(
// // // // // // // // // //               ["opacity", "max-height", "padding", "border-color"],
// // // // // // // // // //               {
// // // // // // // // // //                 easing: theme.transitions.easing.sharp,
// // // // // // // // // //                 duration: theme.transitions.duration.enteringScreen,
// // // // // // // // // //               }
// // // // // // // // // //             ),
// // // // // // // // // //           }}
// // // // // // // // // //         >
// // // // // // // // // //           <Typography
// // // // // // // // // //             variant="caption"
// // // // // // // // // //             color="textSecondary"
// // // // // // // // // //             align="center"
// // // // // // // // // //             display="block"
// // // // // // // // // //           >
// // // // // // // // // //             © 2025 Ministry of Education
// // // // // // // // // //           </Typography>
// // // // // // // // // //         </Box>
// // // // // // // // // //       </Box>
// // // // // // // // // //     </Drawer>
// // // // // // // // // //   );
// // // // // // // // // // };

// // // // // // // // // // export default Sidebar;
// // // // // // // // // import {
// // // // // // // // //   Drawer,
// // // // // // // // //   List,
// // // // // // // // //   ListItem,
// // // // // // // // //   ListItemIcon,
// // // // // // // // //   ListItemText,
// // // // // // // // //   ListItemButton,
// // // // // // // // //   Box,
// // // // // // // // //   Typography,
// // // // // // // // //   useTheme,
// // // // // // // // //   useMediaQuery,
// // // // // // // // //   alpha,
// // // // // // // // //   Tooltip, // Added Tooltip to the import list
// // // // // // // // // } from "@mui/material";
// // // // // // // // // import {
// // // // // // // // //   Dashboard,
// // // // // // // // //   Person,
// // // // // // // // //   Work,
// // // // // // // // //   Psychology,
// // // // // // // // //   Badge,
// // // // // // // // //   PostAdd,
// // // // // // // // //   People,
// // // // // // // // //   VideoCall,
// // // // // // // // //   Analytics,
// // // // // // // // //   Map,
// // // // // // // // //   Settings,
// // // // // // // // //   Menu as MenuIcon,
// // // // // // // // // } from "@mui/icons-material";
// // // // // // // // // import { useNavigate, useLocation } from "react-router-dom"; // Reverted to react-router-dom
// // // // // // // // // import { useAuth } from "../../context/AuthContext"; // Import your AuthContext

// // // // // // // // // // Mock useAuth for demonstration purposes in v0 environment
// // // // // // // // // // In a real application, you would import your actual AuthContext

// // // // // // // // // const drawerWidth = 256;
// // // // // // // // // const drawerWidthCollapsed = 64;

// // // // // // // // // const Sidebar = ({ open, onClose, variant = "permanent", onToggle }) => {
// // // // // // // // //   const { user } = useAuth();
// // // // // // // // //   const navigate = useNavigate(); // Reverted
// // // // // // // // //   const location = useLocation(); // Reverted
// // // // // // // // //   const theme = useTheme();
// // // // // // // // //   const isMobile = useMediaQuery(theme.breakpoints.down("md"));

// // // // // // // // //   // Define a custom color palette for roles if not already in theme
// // // // // // // // //   // This is a placeholder; in a real app, you'd extend your MUI theme.
// // // // // // // // //   const getRoleColor = (role) => {
// // // // // // // // //     switch (role) {
// // // // // // // // //       case "talent":
// // // // // // // // //         return "#0047ab"; // Main accent color as requested
// // // // // // // // //       case "employer":
// // // // // // // // //         return "#d32f2f"; // Example color for employer
// // // // // // // // //       case "admin":
// // // // // // // // //         return "#fbc02d"; // Example color for admin
// // // // // // // // //       default:
// // // // // // // // //         return "#607d8b"; // Default grey
// // // // // // // // //     }
// // // // // // // // //   };

// // // // // // // // //   const menuItems = {
// // // // // // // // //     talent: [
// // // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/talent" },
// // // // // // // // //       {
// // // // // // // // //         text: "Profile Builder",
// // // // // // // // //         icon: <Person />,
// // // // // // // // //         path: "/gatep/talent/profile",
// // // // // // // // //       },
// // // // // // // // //       { text: "Global Jobs", icon: <Work />, path: "/gatep/talent/jobs" },
// // // // // // // // //       { text: "Interviews", icon: <Work />, path: "/gatep/talent/interviews" },
// // // // // // // // //       {
// // // // // // // // //         text: "AI Assistant",
// // // // // // // // //         icon: <Psychology />,
// // // // // // // // //         path: "/gatep/talent/assistant",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Skills Passport",
// // // // // // // // //         icon: <Badge />,
// // // // // // // // //         path: "/gatep/talent/passport",
// // // // // // // // //       },
// // // // // // // // //     ],
// // // // // // // // //     employer: [
// // // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/employer" },
// // // // // // // // //       {
// // // // // // // // //         text: "Company Profile",
// // // // // // // // //         icon: <PostAdd />,
// // // // // // // // //         path: "/gatep/employer/company-profile",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Post Jobs",
// // // // // // // // //         icon: <PostAdd />,
// // // // // // // // //         path: "/gatep/employer/post-job",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Candidates",
// // // // // // // // //         icon: <People />,
// // // // // // // // //         path: "/gatep/employer/candidates",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Interviews",
// // // // // // // // //         icon: <VideoCall />,
// // // // // // // // //         path: "/gatep/employer/interviews",
// // // // // // // // //       },
// // // // // // // // //       {
// // // // // // // // //         text: "Analytics",
// // // // // // // // //         icon: <Analytics />,
// // // // // // // // //         path: "/gatep/employer/analytics",
// // // // // // // // //       },
// // // // // // // // //     ],
// // // // // // // // //     admin: [
// // // // // // // // //       { text: "Dashboard", icon: <Dashboard />, path: "/gatep/admin" },
// // // // // // // // //       {
// // // // // // // // //         text: "Analytics",
// // // // // // // // //         icon: <Analytics />,
// // // // // // // // //         path: "/gatep/admin/analytics",
// // // // // // // // //       },
// // // // // // // // //       { text: "User Management", icon: <People />, path: "/gatep/admin/users" },
// // // // // // // // //       { text: "Talent Heatmap", icon: <Map />, path: "/gatep/admin/heatmap" },
// // // // // // // // //       {
// // // // // // // // //         text: "Integrations",
// // // // // // // // //         icon: <Settings />,
// // // // // // // // //         path: "/gatep/admin/integrations",
// // // // // // // // //       },
// // // // // // // // //     ],
// // // // // // // // //   };

// // // // // // // // //   const currentMenuItems = menuItems[user?.role] || [];

// // // // // // // // //   const handleNavigation = (path) => {
// // // // // // // // //     navigate(path); // Reverted
// // // // // // // // //     if (isMobile) {
// // // // // // // // //       onClose();
// // // // // // // // //     }
// // // // // // // // //   };

// // // // // // // // //   const accentColor = getRoleColor(user?.role);

// // // // // // // // //   return (
// // // // // // // // //     <Drawer
// // // // // // // // //       anchor="left"
// // // // // // // // //       open={open}
// // // // // // // // //       variant={variant}
// // // // // // // // //       onClose={onClose}
// // // // // // // // //       sx={{
// // // // // // // // //         flexShrink: 0,
// // // // // // // // //         "& .MuiDrawer-paper": {
// // // // // // // // //           width: open ? drawerWidth : drawerWidthCollapsed,
// // // // // // // // //           boxSizing: "border-box",
// // // // // // // // //           mt: !isMobile ? "80px" : 0,
// // // // // // // // //           ml: !isMobile ? 0 : 0,
// // // // // // // // //           mb: !isMobile ? 2 : 0,
// // // // // // // // //           height: !isMobile ? "calc(100% - 96px)" : "100%",
// // // // // // // // //           borderRadius: !isMobile ? 4 : 0,
// // // // // // // // //           border: "1px solid",
// // // // // // // // //           borderColor: alpha(theme.palette.divider, 0.3),
// // // // // // // // //           background: " rgba(151, 176, 239, 0.6)",
// // // // // // // // //           background:
// // // // // // // // //             theme.palette.mode === "light"
// // // // // // // // //               ? `linear-gradient(135deg, ${alpha(
// // // // // // // // //                   theme.palette.background.paper,
// // // // // // // // //                   0.9
// // // // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`
// // // // // // // // //               : `linear-gradient(135deg, ${alpha(
// // // // // // // // //                   theme.palette.background.paper,
// // // // // // // // //                   0.9
// // // // // // // // //                 )}, ${alpha(theme.palette.background.default, 0.8)})`,
// // // // // // // // //           boxShadow: !isMobile
// // // // // // // // //             ? `0px 8px 20px ${alpha(
// // // // // // // // //                 theme.palette.common.black,
// // // // // // // // //                 0.1
// // // // // // // // //               )}, 0px 4px 8px ${alpha(theme.palette.common.black, 0.05)}`
// // // // // // // // //             : "none",
// // // // // // // // //           transition: theme.transitions.create(
// // // // // // // // //             ["width", "border-radius", "box-shadow", "background"],
// // // // // // // // //             {
// // // // // // // // //               easing: theme.transitions.easing.easeInOut,
// // // // // // // // //               duration: theme.transitions.duration.standard,
// // // // // // // // //             }
// // // // // // // // //           ),
// // // // // // // // //         },
// // // // // // // // //       }}
// // // // // // // // //     >
// // // // // // // // //       <Box
// // // // // // // // //         sx={{
// // // // // // // // //           height: "100%",
// // // // // // // // //           display: "flex",
// // // // // // // // //           flexDirection: "column",
// // // // // // // // //         }}
// // // // // // // // //       >
// // // // // // // // //         <List
// // // // // // // // //           sx={{
// // // // // // // // //             py: 1,
// // // // // // // // //             flexGrow: 1,
// // // // // // // // //             overflowY: "auto",
// // // // // // // // //             mt: isMobile ? "54px" : 0,
// // // // // // // // //           }}
// // // // // // // // //         >
// // // // // // // // //           {/* Toggle button as first list item */}
// // // // // // // // //           {!isMobile && (
// // // // // // // // //             <ListItem disablePadding sx={{ display: "block" }}>
// // // // // // // // //               {/* WRAPPED WITH TOOLTIP */}
// // // // // // // // //               <ListItemButton
// // // // // // // // //                 onClick={onToggle}
// // // // // // // // //                 sx={{
// // // // // // // // //                   mx: 1,
// // // // // // // // //                   borderRadius: 2,
// // // // // // // // //                   minHeight: 48,
// // // // // // // // //                   justifyContent: open ? "initial" : "center",
// // // // // // // // //                   px: 2.5,
// // // // // // // // //                   backgroundColor: alpha(theme.palette.action.hover, 0.05),
// // // // // // // // //                   boxShadow: theme.shadows[1],
// // // // // // // // //                   color: theme.palette.text.secondary,
// // // // // // // // //                   "&:hover": {
// // // // // // // // //                     backgroundColor: alpha(accentColor, 0.1),
// // // // // // // // //                     boxShadow: theme.shadows[3],
// // // // // // // // //                     transform: "scale(1.02)",
// // // // // // // // //                     color: accentColor,
// // // // // // // // //                     "& .MuiListItemIcon-root": {
// // // // // // // // //                       color: accentColor,
// // // // // // // // //                     },
// // // // // // // // //                   },
// // // // // // // // //                   "&:active": {
// // // // // // // // //                     boxShadow: theme.shadows[0],
// // // // // // // // //                     transform: "scale(0.98)",
// // // // // // // // //                   },
// // // // // // // // //                   transition: theme.transitions.create(
// // // // // // // // //                     ["background-color", "box-shadow", "transform", "color"],
// // // // // // // // //                     {
// // // // // // // // //                       duration: theme.transitions.duration.short,
// // // // // // // // //                     }
// // // // // // // // //                   ),
// // // // // // // // //                   "&:focus-visible": { outline: "none", boxShadow: "none" },
// // // // // // // // //                   "&.Mui-focusVisible": { outline: "none", boxShadow: "none" },
// // // // // // // // //                   "&:focus": { outline: "none", boxShadow: "none" },
// // // // // // // // //                 }}
// // // // // // // // //               >
// // // // // // // // //                 <ListItemIcon
// // // // // // // // //                   sx={{
// // // // // // // // //                     minWidth: 0,
// // // // // // // // //                     mr: open ? 2 : "auto",
// // // // // // // // //                     justifyContent: "center",
// // // // // // // // //                     color: "inherit",
// // // // // // // // //                     transition: theme.transitions.create("color", {
// // // // // // // // //                       duration: theme.transitions.duration.short,
// // // // // // // // //                     }),
// // // // // // // // //                   }}
// // // // // // // // //                 >
// // // // // // // // //                   <MenuIcon />
// // // // // // // // //                 </ListItemIcon>
// // // // // // // // //                 <ListItemText
// // // // // // // // //                   sx={{
// // // // // // // // //                     opacity: open ? 1 : 0,
// // // // // // // // //                     width: open ? "auto" : 0,
// // // // // // // // //                     overflow: "hidden",
// // // // // // // // //                     whiteSpace: "nowrap",
// // // // // // // // //                     transition: theme.transitions.create(["opacity", "width"], {
// // // // // // // // //                       easing: theme.transitions.easing.sharp,
// // // // // // // // //                       duration: theme.transitions.duration.enteringScreen,
// // // // // // // // //                     }),
// // // // // // // // //                   }}
// // // // // // // // //                 />
// // // // // // // // //               </ListItemButton>
// // // // // // // // //             </ListItem>
// // // // // // // // //           )}
// // // // // // // // //           {/* Menu items */}
// // // // // // // // //           {currentMenuItems.map((item) => (
// // // // // // // // //             <ListItem key={item.text} disablePadding sx={{ display: "block" }}>
// // // // // // // // //               {/* WRAPPED WITH TOOLTIP. Title is empty when sidebar is open to disable it. */}
// // // // // // // // //               <Tooltip title={!open ? item.text : ""} placement="right" arrow>
// // // // // // // // //                 <ListItemButton
// // // // // // // // //                   onClick={() => handleNavigation(item.path)}
// // // // // // // // //                   selected={location.pathname === item.path} // Reverted
// // // // // // // // //                   sx={{
// // // // // // // // //                     mx: 1,
// // // // // // // // //                     borderRadius: 2,
// // // // // // // // //                     minHeight: 48,
// // // // // // // // //                     justifyContent: open ? "initial" : "center",
// // // // // // // // //                     px: 2.5,
// // // // // // // // //                     color: theme.palette.text.primary,
// // // // // // // // //                     "& .MuiListItemIcon-root": {
// // // // // // // // //                       color: theme.palette.text.secondary,
// // // // // // // // //                     },
// // // // // // // // //                     "&:hover": {
// // // // // // // // //                       backgroundColor: alpha(accentColor, 0.1),
// // // // // // // // //                       color: accentColor,
// // // // // // // // //                       transform: "scale(1.02)",
// // // // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // // // //                         color: accentColor,
// // // // // // // // //                       },
// // // // // // // // //                     },
// // // // // // // // //                     "&.Mui-selected": {
// // // // // // // // //                       backgroundColor: alpha(accentColor, 0.2),
// // // // // // // // //                       color: accentColor,
// // // // // // // // //                       fontWeight: "bold",
// // // // // // // // //                       transform: "scale(1.01)",
// // // // // // // // //                       "& .MuiListItemIcon-root": {
// // // // // // // // //                         color: accentColor,
// // // // // // // // //                       },
// // // // // // // // //                       "&:hover": {
// // // // // // // // //                         backgroundColor: alpha(accentColor, 0.25),
// // // // // // // // //                         color: accentColor,
// // // // // // // // //                         "& .MuiListItemIcon-root": {
// // // // // // // // //                           color: accentColor,
// // // // // // // // //                         },
// // // // // // // // //                       },
// // // // // // // // //                     },
// // // // // // // // //                     transition: theme.transitions.create(
// // // // // // // // //                       ["background-color", "color", "transform", "box-shadow"],
// // // // // // // // //                       {
// // // // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // // // //                         easing: theme.transitions.easing.easeInOut,
// // // // // // // // //                       }
// // // // // // // // //                     ),
// // // // // // // // //                   }}
// // // // // // // // //                 >
// // // // // // // // //                   <ListItemIcon
// // // // // // // // //                     sx={{
// // // // // // // // //                       minWidth: 0,
// // // // // // // // //                       mr: open ? 2 : "auto",
// // // // // // // // //                       justifyContent: "center",
// // // // // // // // //                       transition: theme.transitions.create("color", {
// // // // // // // // //                         duration: theme.transitions.duration.short,
// // // // // // // // //                       }),
// // // // // // // // //                     }}
// // // // // // // // //                   >
// // // // // // // // //                     {item.icon}
// // // // // // // // //                   </ListItemIcon>
// // // // // // // // //                   <ListItemText
// // // // // // // // //                     primary={item.text}
// // // // // // // // //                     sx={{
// // // // // // // // //                       opacity: open ? 1 : 0,
// // // // // // // // //                       width: open ? "auto" : 0,
// // // // // // // // //                       overflow: "hidden",
// // // // // // // // //                       whiteSpace: "nowrap",
// // // // // // // // //                       transition: theme.transitions.create(
// // // // // // // // //                         ["opacity", "width"],
// // // // // // // // //                         {
// // // // // // // // //                           easing: theme.transitions.easing.sharp,
// // // // // // // // //                           duration: theme.transitions.duration.enteringScreen,
// // // // // // // // //                         }
// // // // // // // // //                       ),
// // // // // // // // //                     }}
// // // // // // // // //                   />
// // // // // // // // //                 </ListItemButton>
// // // // // // // // //               </Tooltip>
// // // // // // // // //             </ListItem>
// // // // // // // // //           ))}
// // // // // // // // //         </List>
// // // // // // // // //         {/* Footer */}
// // // // // // // // //         <Box
// // // // // // // // //           sx={{
// // // // // // // // //             py: open ? 2 : 0,
// // // // // // // // //             px: 2,
// // // // // // // // //             mt: "auto",
// // // // // // // // //             borderTop: "1px solid",
// // // // // // // // //             borderColor: open
// // // // // // // // //               ? alpha(theme.palette.divider, 0.3)
// // // // // // // // //               : "transparent",
// // // // // // // // //             opacity: open ? 1 : 0,
// // // // // // // // //             maxHeight: open ? 100 : 0,
// // // // // // // // //             overflow: "hidden",
// // // // // // // // //             transition: theme.transitions.create(
// // // // // // // // //               ["opacity", "max-height", "padding", "border-color"],
// // // // // // // // //               {
// // // // // // // // //                 easing: theme.transitions.easing.sharp,
// // // // // // // // //                 duration: theme.transitions.duration.enteringScreen,
// // // // // // // // //               }
// // // // // // // // //             ),
// // // // // // // // //           }}
// // // // // // // // //         >
// // // // // // // // //           <Typography
// // // // // // // // //             variant="caption"
// // // // // // // // //             color="textSecondary"
// // // // // // // // //             align="center"
// // // // // // // // //             display="block"
// // // // // // // // //           >
// // // // // // // // //             © 2025 The Data Tech Labs
// // // // // // // // //           </Typography>
// // // // // // // // //         </Box>
// // // // // // // // //       </Box>
// // // // // // // // //     </Drawer>
// // // // // // // // //   );
// // // // // // // // // };

// // // // // // // // // export default Sidebar;
